import { useState, useRef, useEffect } from 'react'
import * as Sentry from "@sentry/react"
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import TextAlign from '@tiptap/extension-text-align'
import Underline from '@tiptap/extension-underline'
import { TextStyle } from '@tiptap/extension-text-style'
import { FontFamily } from '@tiptap/extension-font-family'
import Highlight from '@tiptap/extension-highlight'
import { TableKit } from '@tiptap/extension-table/kit'
import { generateJSON } from '@tiptap/html'
import ReactMarkdown from 'react-markdown'
import Settings from './Settings'
import Help from './Help'
import { supabase } from '../lib/supabase'
import { isValidMarkdownTable, parseMarkdownTable } from '../utils/parseMarkdownTable'
import { markdownToHtml } from '../utils/markdownToHtml'
import { AiSpreadsheetResult, TableData } from '../types/table'
import SpreadsheetTable, { type SpreadsheetTableRef } from './SpreadsheetTable'
import TypewriterText from './TypewriterText'
import { useVoiceRecorder } from '../hooks/useVoiceRecorder'
import './ChatInterface.css'

// Constants
const MAX_SELECTION_CHARS = 10000
const MAX_SPREADSHEET_ROWS = 10
const MAX_SPREADSHEET_COLUMNS = 10

// Chat message interface
interface SearchSource {
  title: string
  link: string
  snippet: string
}

interface ChatMessage {
  id: string
  text: string
  sender: 'user' | 'assistant'
  timestamp: Date
  isProposal?: boolean
  isLoading?: boolean
  isWebSearch?: boolean
  sources?: SearchSource[]
  proposalData?: {
    proposal: string
    selectionRange: { from: number, to: number }
  }
  aiResult?: AiSpreadsheetResult
  confidence?: number
  legalSources?: string[]
  metadata?: {
    avg_similarity?: number
    documents_used?: number
    country?: string
    analysis?: {
      laws: string
      explanations: string
      conclusion: string
      spreadsheet: string
      followupQuestions: string
    }
    type?: 'deep-analysis' | string
  }
}

function LandingChatScreen() {
  const [inputValue, setInputValue] = useState('')
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([])
  const [isLoadingSession, setIsLoadingSession] = useState(true) // Loading state for session restoration
  const [showPromptSuggestions, setShowPromptSuggestions] = useState(true) // Show prompt suggestions until first message
  const hasLoadedFromStorage = useRef(false) // Track if we've loaded from sessionStorage
  const hasLoadedEditorFromStorage = useRef(false) // Track if we've updated editor content from sessionStorage
  const loadedFromVault = useRef<{type: 'document' | 'spreadsheet' | null, timestamp: number}>({type: null, timestamp: 0}) // Track if content was loaded from Vault
  
  // SessionStorage keys
  const SESSION_STORAGE_KEY = 'avocat_ai_chat_messages'
  const SESSION_STORAGE_EDITOR_KEY = 'avocat_ai_editor_documents'
  const SESSION_STORAGE_SPREADSHEET_KEY = 'avocat_ai_spreadsheet_data'
  const [isChatLoading, setIsChatLoading] = useState(false)
  const [loadingActionMode, setLoadingActionMode] = useState<string | null>(null) // Track which action mode is loading
  const [loadingMessageStage, setLoadingMessageStage] = useState<'analyse' | null>(null) // Track which loading message stage to display
  const [streamingMessageIds, setStreamingMessageIds] = useState<Set<string>>(new Set()) // Track which messages are currently streaming
  const [isImproving, setIsImproving] = useState(false)
  const [isSavingConversation, setIsSavingConversation] = useState(false)
  const [isDeletingConversation, setIsDeletingConversation] = useState(false)
  const [selectedLaw, setSelectedLaw] = useState<'tunisien'>('tunisien')
  const [isLawDropdownOpen, setIsLawDropdownOpen] = useState(false)
  const lawDropdownRef = useRef<HTMLDivElement>(null)
  const [deletingConversationId, setDeletingConversationId] = useState<string | null>(null)
  const [isSavingToVault, setIsSavingToVault] = useState(false)
  const chatEndRef = useRef<HTMLDivElement>(null)
  
  // Voice recording
  const {
    isRecording,
    isProcessing,
    error: voiceError,
    transcript,
    startRecording,
    stopRecording,
    reset: resetVoiceRecorder,
  } = useVoiceRecorder()
  
  const [isSidebarOpen, setIsSidebarOpen] = useState(true)
  const [isGuideOpen, setIsGuideOpen] = useState(false)
  const [isActionMenuOpen, setIsActionMenuOpen] = useState(false)
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)
  const [isSigningOut, setIsSigningOut] = useState(false)
  const [currentUser, setCurrentUser] = useState<{ email?: string; name?: string; avatar_url?: string } | null>(null)
  const [avatarError, setAvatarError] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const [showHelp, setShowHelp] = useState(false)
  const [language, setLanguage] = useState<'FR' | 'AR'>('AR')
  const [theme, setTheme] = useState<'light' | 'dark'>('dark')
  const [isEditorOpen, setIsEditorOpen] = useState(false)
  const [selectedCharCount, setSelectedCharCount] = useState(0)
  const [messageFeedback, setMessageFeedback] = useState<Record<string, 'like' | 'dislike' | null>>({})
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null)
  const [isTableurAIActivated, setIsTableurAIActivated] = useState(false)
  const [isEditeurAIActivated, setIsEditeurAIActivated] = useState(false)
  const [activeAIMode, setActiveAIMode] = useState<'tableur' | 'editeur' | null>(null)
  const [webSearchUsage, setWebSearchUsage] = useState<{
    searchCount: number
    firstSearchAt: string | null
    canSearch: boolean
    daysRemaining: number | null
  } | null>(null)
  const isLoadingWebSearchUsage = useRef(false) // Prevent concurrent calls
  const [chatMessageUsage, setChatMessageUsage] = useState<{
    messageCount: number
    firstMessageAt: string | null
    canSend: boolean
    hoursRemaining: number | null
  } | null>(null)
  const [showChatMessageLimitTooltip, setShowChatMessageLimitTooltip] = useState(false)
  const sendButtonRef = useRef<HTMLButtonElement>(null)
  const [isAnalysisPanelOpen, setIsAnalysisPanelOpen] = useState(false)
  const [isEditorContentVisible, setIsEditorContentVisible] = useState(true)
  const [isEditorMinimized, setIsEditorMinimized] = useState(false)
  const [activeMenuButton, setActiveMenuButton] = useState<string>('Édition')
  const [isEditorNavCollapsed, setIsEditorNavCollapsed] = useState<boolean>(false)
  const [layoutMode, setLayoutMode] = useState<'editor' | 'analysis'>('editor') // 'editor' = 70/30, 'analysis' = 40/60
  const [lineSpacing, setLineSpacing] = useState(1.0) // Line spacing for editor
  const [fontFamily, setFontFamily] = useState('Times New Roman') // Font family for editor
  const [fontSize, setFontSize] = useState(12) // Font size for editor
  const [selectedSidebarItem, setSelectedSidebarItem] = useState<string>('new-chat') // Track selected sidebar item - default to Nouveau chat
  const [selectedActionMode, setSelectedActionMode] = useState<string | null>(null) // Track selected action from + menu
  const [isTableurOpen, setIsTableurOpen] = useState(false)
  const [isVaultOpen, setIsVaultOpen] = useState(false)
  const [currentChatId, setCurrentChatId] = useState<string | null>(null)
  const [savedConversations, setSavedConversations] = useState<Array<{id: string, title: string, created_at: string, updated_at?: string}>>([])
  const [isLoadingConversations, setIsLoadingConversations] = useState(false)
  
  // Unified Vault state
  const [openVaultMenuId, setOpenVaultMenuId] = useState<string | null>(null)
  
  const [vaultItems, setVaultItems] = useState<Array<{
    id: string
    type: 'chat' | 'document' | 'spreadsheet'
    title: string
    content: any // JSONB content
    metadata: any // JSONB metadata
    created_at: string
    updated_at?: string
    isLegacy?: boolean // Legacy conversations from conversations table
  }>>([])
  const [isLoadingVaultItems, setIsLoadingVaultItems] = useState(false)
  const [currentVaultItemId, setCurrentVaultItemId] = useState<string | null>(null)
  const [loadingVaultItemId, setLoadingVaultItemId] = useState<string | null>(null)
  const [vaultError, setVaultError] = useState<{
    type: 'fetch' | 'save' | 'load' | null,
    category: 'network' | 'auth' | 'database' | 'timeout' | 'rate-limit' | 'unknown',
    message: string,
    retryable: boolean
  } | null>(null)
  const [vaultSuccess, setVaultSuccess] = useState<{
    type: 'save' | 'load',
    message: string
  } | null>(null)
  const [saveLoadError, setSaveLoadError] = useState<{
    message: string,
    retryable: boolean,
    onRetry?: () => void
  } | null>(null)
  const [itemErrors, setItemErrors] = useState<Map<string, {
    message: string,
    retryable: boolean
  }>>(new Map())
  const [isOnline, setIsOnline] = useState<boolean>(typeof navigator !== 'undefined' ? navigator.onLine : true)
  const [vaultSearchQuery, setVaultSearchQuery] = useState<string>('')
  
  const [currentTableData, setCurrentTableData] = useState<TableData | null>(null)
  const [selectedColumnIndex, setSelectedColumnIndex] = useState<number | null>(null)
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null)
  const spreadsheetTableRef = useRef<SpreadsheetTableRef>(null)
  
  // State for spreadsheet dropdown menus in chat interface
  const [openColumnMenu, setOpenColumnMenu] = useState<number | null>(null)
  const [openRowMenu, setOpenRowMenu] = useState<number | null>(null)
  
  // Modal state
  const [modalState, setModalState] = useState<{
    isOpen: boolean
    type: 'rename' | 'confirm' | 'success' | 'delete-account' | null
    title: string
    message?: string
    defaultValue?: string
    confirmButtonText?: string
    onConfirm?: (value?: string) => void
    onCancel?: () => void
  }>({
    isOpen: false,
    type: null,
    title: ''
  })
  
  // Helper function to update table data
  const updateTableData = (updater: (data: TableData) => TableData) => {
    setCurrentTableData(prev => {
      if (!prev) return null
      return updater(prev)
    })
  }
  
  // Convert activeSpreadsheet.data (2D array) to TableData format
  const convertSpreadsheetToTableData = (data: (string | number | null)[][]): TableData => {
    if (!data || data.length === 0) {
      return {
        columns: ['Colonne 1', 'Colonne 2', 'Colonne 3'],
        rows: []
      }
    }
    
    // First row contains column headers
    const columns = data[0].map(cell => String(cell || ''))
    // Rest are data rows
    const rows = data.slice(1).map(row => 
      row.map(cell => String(cell ?? ''))
    )
    
    return { columns, rows }
  }

  // Convert TableData to CSV format
  const convertTableToCSV = (tableData: TableData): string => {
    // Combine columns and rows
    const allRows = [tableData.columns, ...tableData.rows]
    
    // Convert each row to CSV format (handle commas, quotes, newlines)
    return allRows.map(row => {
      return row.map(cell => {
        const cellValue = String(cell || '')
        // Escape quotes and wrap in quotes if contains comma, quote, or newline
        if (cellValue.includes(',') || cellValue.includes('"') || cellValue.includes('\n')) {
          return `"${cellValue.replace(/"/g, '""')}"`
        }
        return cellValue
      }).join(',')
    }).join('\n')
  }

  // Download table as CSV file
  const downloadTableAsCSV = (tableData: TableData, filename: string = 'tableau.csv') => {
    const csvContent = convertTableToCSV(tableData)
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', filename)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  // Convert TableData format to activeSpreadsheet.data (2D array)
  const convertTableDataToSpreadsheet = (tableData: TableData): (string | number | null)[][] => {
    if (!tableData) {
      return [['Colonne 1', 'Colonne 2', 'Colonne 3'], ['', '', '']]
    }
    
    // First row is headers (columns), rest are data rows
    return [tableData.columns, ...tableData.rows]
  }

  // Convert TableData to HTML table string for Tiptap editor
  const convertTableDataToHTML = (tableData: TableData): string => {
    const headerRow = `<tr>${tableData.columns.map(col => `<th>${col || ''}</th>`).join('')}</tr>`
    const bodyRows = tableData.rows.map(row => 
      `<tr>${row.map(cell => `<td>${cell || ''}</td>`).join('')}</tr>`
    ).join('')
    return `<table><thead>${headerRow}</thead><tbody>${bodyRows}</tbody></table>`
  }

  // Helper functions for table manipulation in chat interface
  const insertColumnLeft = (messageId: string, columnIndex: number) => {
    setChatMessages(prev => prev.map(msg => {
      if (msg.id === messageId && msg.aiResult?.kind === 'table' && msg.aiResult.table) {
        const newColumns = [...msg.aiResult.table.columns]
        const newRows = msg.aiResult.table.rows.map(row => [...row])
        
        // Insert new column header
        newColumns.splice(columnIndex, 0, 'Nouvelle colonne')
        
        // Insert empty cell in each row
        newRows.forEach(row => {
          row.splice(columnIndex, 0, '')
        })
        
        return {
          ...msg,
          aiResult: {
            ...msg.aiResult,
            table: {
              columns: newColumns,
              rows: newRows
            }
          }
        }
      }
      return msg
    }))
  }

  const insertColumnRight = (messageId: string, columnIndex: number) => {
    setChatMessages(prev => prev.map(msg => {
      if (msg.id === messageId && msg.aiResult?.kind === 'table' && msg.aiResult.table) {
        const newColumns = [...msg.aiResult.table.columns]
        const newRows = msg.aiResult.table.rows.map(row => [...row])
        
        // Insert new column header after the specified index
        newColumns.splice(columnIndex + 1, 0, 'Nouvelle colonne')
        
        // Insert empty cell in each row
        newRows.forEach(row => {
          row.splice(columnIndex + 1, 0, '')
        })
        
        return {
          ...msg,
          aiResult: {
            ...msg.aiResult,
            table: {
              columns: newColumns,
              rows: newRows
            }
          }
        }
      }
      return msg
    }))
  }

  const insertRowAbove = (messageId: string, rowIndex: number) => {
    setChatMessages(prev => prev.map(msg => {
      if (msg.id === messageId && msg.aiResult?.kind === 'table' && msg.aiResult.table) {
        const newRows = [...msg.aiResult.table.rows]
        const numColumns = msg.aiResult.table.columns.length
        
        // Create new empty row
        const newRow = Array(numColumns).fill('')
        
        // Insert before the specified index
        newRows.splice(rowIndex, 0, newRow)
        
        return {
          ...msg,
          aiResult: {
            ...msg.aiResult,
            table: {
              columns: msg.aiResult.table.columns,
              rows: newRows
            }
          }
        }
      }
      return msg
    }))
  }

  const insertRowBelow = (messageId: string, rowIndex: number) => {
    setChatMessages(prev => prev.map(msg => {
      if (msg.id === messageId && msg.aiResult?.kind === 'table' && msg.aiResult.table) {
        const newRows = [...msg.aiResult.table.rows]
        const numColumns = msg.aiResult.table.columns.length
        
        // Create new empty row
        const newRow = Array(numColumns).fill('')
        
        // Insert after the specified index
        newRows.splice(rowIndex + 1, 0, newRow)
        
        return {
          ...msg,
          aiResult: {
            ...msg.aiResult,
            table: {
              columns: msg.aiResult.table.columns,
              rows: newRows
            }
          }
        }
      }
      return msg
    }))
  }

  const updateCellValue = (messageId: string, rowIndex: number, columnIndex: number, value: string) => {
    setChatMessages(prev => prev.map(msg => {
      if (msg.id === messageId && msg.aiResult?.kind === 'table' && msg.aiResult.table) {
        const newRows = msg.aiResult.table.rows.map((row, rIdx) => {
          if (rIdx === rowIndex) {
            const newRow = [...row]
            newRow[columnIndex] = value
            return newRow
          }
          return row
        })
        
        return {
          ...msg,
          aiResult: {
            ...msg.aiResult,
            table: {
              columns: msg.aiResult.table.columns,
              rows: newRows
            }
          }
        }
      }
      return msg
    }))
  }
  
  // Open rename modal
  const openRenameModal = (currentName: string, onConfirm: (newName: string) => void, modalTitle?: string) => {
    setModalState({
      isOpen: true,
      type: 'rename',
      title: modalTitle || 'Renommer la colonne',
      defaultValue: currentName,
      onConfirm: (value) => {
        if (value && value.trim()) {
          onConfirm(value.trim())
        }
        setModalState({ isOpen: false, type: null, title: '' })
      },
      onCancel: () => {
        setModalState({ isOpen: false, type: null, title: '' })
      }
    })
  }
  
  // Open confirm modal
  const openConfirmModal = (message: string, onConfirm: () => void, confirmButtonText?: string) => {
    setModalState({
      isOpen: true,
      type: 'confirm',
      title: confirmButtonText === 'Se déconnecter' ? 'Avertissement' : 'Confirmation',
      message,
      confirmButtonText,
      onConfirm: async () => {
        // For sign out, don't close modal immediately - let it show loading
        if (confirmButtonText === 'Se déconnecter') {
          await onConfirm()
          // Modal will close when auth state changes, but close it here as fallback
          setTimeout(() => {
            setModalState({ isOpen: false, type: null, title: '' })
          }, 1000)
        } else {
          onConfirm()
          setModalState({ isOpen: false, type: null, title: '' })
        }
      },
      onCancel: () => {
        setModalState({ isOpen: false, type: null, title: '' })
      }
    })
  }

  // Open success modal
  const openSuccessModal = (message: string, onClose?: () => void) => {
    setModalState({
      isOpen: true,
      type: 'success',
      title: 'Succès',
      message,
      onConfirm: () => {
        onClose?.()
        setModalState({ isOpen: false, type: null, title: '' })
      },
      onCancel: () => {
        onClose?.()
        setModalState({ isOpen: false, type: null, title: '' })
      }
    })
  }

  // Open delete account modal
  const openDeleteAccountModal = (onClose?: () => void) => {
    setModalState({
      isOpen: true,
      type: 'delete-account',
      title: 'Supprimer le compte',
      onConfirm: (value) => {
        if (value === 'SUPPRIMER MON COMPTE') {
          handleDeleteAccount()
          onClose?.()
        }
        setModalState({ isOpen: false, type: null, title: '' })
      },
      onCancel: () => {
        setModalState({ isOpen: false, type: null, title: '' })
      }
    })
  }

  // Handle account deletion
  const handleDeleteAccount = async () => {
    try {
      // Get current user session
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.user) {
        alert('Vous devez être connecté pour supprimer votre compte')
        return
      }

      // First, delete all user data
      // Delete all messages
      await supabase
        .from('messages')
        .delete()
        .eq('user_id', session.user.id)

      // Delete all conversations
      await supabase
        .from('conversations')
        .delete()
        .eq('user_id', session.user.id)

      // Delete all vault items
      await supabase
        .from('vault_items')
        .delete()
        .eq('user_id', session.user.id)

      // Delete user account - Note: This requires admin privileges or an edge function
      // For now, we'll sign out the user. The actual account deletion should be handled
      // by a server-side function with admin privileges
      const { error: signOutError } = await supabase.auth.signOut()
      
      if (signOutError) {
        throw signOutError
      }

      // Clear all local state
      setSavedConversations([])
      setVaultItems([])
      setChatMessages([])
      setCurrentChatId(null)
      setCurrentVaultItemId(null)
      setInputValue('')
      setShowPromptSuggestions(true) // Show suggestions again when conversation is cleared
      setCurrentUser(null)

      // Clear sessionStorage
      sessionStorage.removeItem(SESSION_STORAGE_KEY)
      sessionStorage.removeItem(SESSION_STORAGE_EDITOR_KEY)
      sessionStorage.removeItem(SESSION_STORAGE_SPREADSHEET_KEY)

      // Redirect to home page or show success message
      window.location.href = '/'
    } catch (error: any) {
      console.error('Error deleting account:', error)
      const errorMessage = error?.message || error?.details || 'Erreur inconnue'
      alert(`Erreur lors de la suppression du compte: ${errorMessage}`)
    }
  }

  const [activeTableurMenuButton, setActiveTableurMenuButton] = useState<string>('Édition')
  const [isTableurNavCollapsed, setIsTableurNavCollapsed] = useState<boolean>(false)
  const [showColumnTypeDropdown, setShowColumnTypeDropdown] = useState(false)
  const columnTypeDropdownRef = useRef<HTMLDivElement>(null)
  const columnTypeButtonRef = useRef<HTMLButtonElement>(null)
  const [columnTypeDropdownPosition, setColumnTypeDropdownPosition] = useState<{top: number, left: number}>({top: 0, left: 0})
  const [selectedColumnType, setSelectedColumnType] = useState<string>('Changer le type de colonne')
  const [selectedRowType, setSelectedRowType] = useState<string>('Changer le type de ligne')
  const [showRowTypeDropdown, setShowRowTypeDropdown] = useState(false)
  const rowTypeDropdownRef = useRef<HTMLDivElement>(null)
  const rowTypeButtonRef = useRef<HTMLButtonElement>(null)
  const [rowTypeDropdownPosition, setRowTypeDropdownPosition] = useState<{top: number, left: number}>({top: 0, left: 0})
  const [showMergeDropdown, setShowMergeDropdown] = useState(false)
  const mergeDropdownRef = useRef<HTMLDivElement>(null)
  const mergeButtonRef = useRef<HTMLButtonElement>(null)
  const [mergeDropdownPosition, setMergeDropdownPosition] = useState<{top: number, left: number}>({top: 0, left: 0})
  const [mergeButtonText, setMergeButtonText] = useState('Fusionner')
  const [showWorkflowsTooltip, setShowWorkflowsTooltip] = useState(false)
  const workflowsButtonRef = useRef<HTMLButtonElement>(null)
  const workflowsButtonFullRef = useRef<HTMLButtonElement>(null)
  const [showNotesAtScaleTooltip, setShowNotesAtScaleTooltip] = useState(false)
  const notesAtScaleButtonRef = useRef<HTMLDivElement>(null)
  const [showAnalyseTooltip, setShowAnalyseTooltip] = useState(false)
  const analyseButtonRef = useRef<HTMLButtonElement>(null)
  const [showUploadTooltip, setShowUploadTooltip] = useState(false)
  const uploadButtonRef = useRef<HTMLButtonElement>(null)
  const actionButtonRef = useRef<HTMLButtonElement>(null)
  const [showCreateTableTooltip, setShowCreateTableTooltip] = useState(false)
  const [showSourcesTooltip, setShowSourcesTooltip] = useState(false)
  const sourcesButtonRef = useRef<HTMLButtonElement>(null)
  const [showTranslationTooltip, setShowTranslationTooltip] = useState(false)
  const translationButtonRef = useRef<HTMLButtonElement>(null)
  const createTableButtonRef = useRef<HTMLButtonElement>(null)
  const [showWebSearchLimitTooltip, setShowWebSearchLimitTooltip] = useState(false)
  const [showSerperLimitTooltip, setShowSerperLimitTooltip] = useState(false)
  const webSearchButtonRef = useRef<HTMLButtonElement>(null)
  
  // Close column type dropdown when clicking outside and update position on scroll/resize
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (columnTypeDropdownRef.current && !columnTypeDropdownRef.current.contains(event.target as Node)) {
        setShowColumnTypeDropdown(false)
      }
      if (mergeDropdownRef.current && !mergeDropdownRef.current.contains(event.target as Node)) {
        setShowMergeDropdown(false)
      }
    }
    
    const updatePosition = () => {
      if (showColumnTypeDropdown && columnTypeButtonRef.current) {
        const rect = columnTypeButtonRef.current.getBoundingClientRect()
        setColumnTypeDropdownPosition({
          top: rect.bottom + 4,
          left: rect.left
        })
      }
      if (showMergeDropdown && mergeButtonRef.current) {
        const rect = mergeButtonRef.current.getBoundingClientRect()
        setMergeDropdownPosition({
          top: rect.bottom + 4,
          left: rect.left
        })
      }
      // Update table selection bar position
      if (isTableurAIActivated && activeAIMode === 'tableur' && (selectedColumnIndex !== null || selectedRowIndex !== null) && spreadsheetTableRef.current) {
        const pos = spreadsheetTableRef.current.getSelectedElementPosition()
        if (pos) {
          setTableSelectionBarPosition(pos)
        }
      }
    }
    
    if (showColumnTypeDropdown || showMergeDropdown) {
      document.addEventListener('mousedown', handleClickOutside)
      window.addEventListener('scroll', updatePosition, true)
      window.addEventListener('resize', updatePosition)
      // Initial position update
      updatePosition()
    }
    
    // Also update table selection bar position on scroll/resize
    if (isTableurAIActivated && activeAIMode === 'tableur' && (selectedColumnIndex !== null || selectedRowIndex !== null)) {
      window.addEventListener('scroll', updatePosition, true)
      window.addEventListener('resize', updatePosition)
      updatePosition()
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      window.removeEventListener('scroll', updatePosition, true)
      window.removeEventListener('resize', updatePosition)
    }
  }, [showColumnTypeDropdown, showMergeDropdown, showRowTypeDropdown, isTableurAIActivated, activeAIMode, selectedColumnIndex, selectedRowIndex])
  
  // Auto-dismiss workflows tooltip after 3 seconds
  useEffect(() => {
    if (showWorkflowsTooltip) {
      const timer = setTimeout(() => {
        setShowWorkflowsTooltip(false)
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [showWorkflowsTooltip])
  
  // Close workflows tooltip when clicking outside or scrolling
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showWorkflowsTooltip) {
        const target = event.target as Node
        if (
          workflowsButtonRef.current &&
          !workflowsButtonRef.current.contains(target) &&
          workflowsButtonFullRef.current &&
          !workflowsButtonFullRef.current.contains(target)
        ) {
          setShowWorkflowsTooltip(false)
        }
      }
    }
    const handleScroll = () => {
      if (showWorkflowsTooltip) {
        setShowWorkflowsTooltip(false)
      }
    }
    if (showWorkflowsTooltip) {
      document.addEventListener('mousedown', handleClickOutside)
      window.addEventListener('scroll', handleScroll, true)
      window.addEventListener('resize', handleScroll)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
        window.removeEventListener('scroll', handleScroll, true)
        window.removeEventListener('resize', handleScroll)
      }
    }
  }, [showWorkflowsTooltip])
  
  // Auto-dismiss notes at scale tooltip after 3 seconds
  useEffect(() => {
    if (showNotesAtScaleTooltip) {
      const timer = setTimeout(() => {
        setShowNotesAtScaleTooltip(false)
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [showNotesAtScaleTooltip])
  
  // Close notes at scale tooltip when clicking outside or scrolling
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showNotesAtScaleTooltip) {
        const target = event.target as Node
        if (
          notesAtScaleButtonRef.current &&
          !notesAtScaleButtonRef.current.contains(target)
        ) {
          setShowNotesAtScaleTooltip(false)
        }
      }
    }
    const handleScroll = () => {
      if (showNotesAtScaleTooltip) {
        setShowNotesAtScaleTooltip(false)
      }
    }
    if (showNotesAtScaleTooltip) {
      document.addEventListener('mousedown', handleClickOutside)
      window.addEventListener('scroll', handleScroll, true)
      window.addEventListener('resize', handleScroll)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
        window.removeEventListener('scroll', handleScroll, true)
        window.removeEventListener('resize', handleScroll)
      }
    }
  }, [showNotesAtScaleTooltip])
  
  // Auto-dismiss analyse tooltip after 3 seconds
  useEffect(() => {
    if (showAnalyseTooltip) {
      const timer = setTimeout(() => {
        setShowAnalyseTooltip(false)
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [showAnalyseTooltip])
  
  // Close analyse tooltip when clicking outside or scrolling
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showAnalyseTooltip) {
        const target = event.target as Node
        if (
          actionButtonRef.current &&
          !actionButtonRef.current.contains(target) &&
          actionMenuRef.current &&
          !actionMenuRef.current.contains(target)
        ) {
          setShowAnalyseTooltip(false)
        }
      }
    }
    const handleScroll = () => {
      if (showAnalyseTooltip) {
        setShowAnalyseTooltip(false)
      }
    }
    if (showAnalyseTooltip) {
      document.addEventListener('mousedown', handleClickOutside)
      window.addEventListener('scroll', handleScroll, true)
      window.addEventListener('resize', handleScroll)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
        window.removeEventListener('scroll', handleScroll, true)
        window.removeEventListener('resize', handleScroll)
      }
    }
  }, [showAnalyseTooltip])
  
  // Auto-dismiss create table tooltip after 3 seconds
  useEffect(() => {
    if (showCreateTableTooltip) {
      const timer = setTimeout(() => {
        setShowCreateTableTooltip(false)
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [showCreateTableTooltip])
  
  // Close create table tooltip when clicking outside or scrolling
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showCreateTableTooltip) {
        const target = event.target as Node
        if (
          actionButtonRef.current &&
          !actionButtonRef.current.contains(target) &&
          actionMenuRef.current &&
          !actionMenuRef.current.contains(target)
        ) {
          setShowCreateTableTooltip(false)
        }
      }
    }
    const handleScroll = () => {
      if (showCreateTableTooltip) {
        setShowCreateTableTooltip(false)
      }
    }
    if (showCreateTableTooltip) {
      document.addEventListener('mousedown', handleClickOutside)
      window.addEventListener('scroll', handleScroll, true)
      window.addEventListener('resize', handleScroll)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
        window.removeEventListener('scroll', handleScroll, true)
        window.removeEventListener('resize', handleScroll)
      }
    }
  }, [showCreateTableTooltip])
  
  // Handle loading message stage transitions
  useEffect(() => {
    if (isChatLoading && loadingActionMode !== 'websearch') {
      // Set to 'analyse' stage
      setLoadingMessageStage('analyse')
    } else {
      // Reset when loading stops or websearch mode
      setLoadingMessageStage(null)
    }
  }, [isChatLoading, loadingActionMode])
  
  // Auto-dismiss upload tooltip after 3 seconds
  useEffect(() => {
    if (showUploadTooltip) {
      const timer = setTimeout(() => {
        setShowUploadTooltip(false)
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [showUploadTooltip])
  
  // Close upload tooltip when clicking outside or scrolling
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showUploadTooltip) {
        const target = event.target as Node
        if (
          actionButtonRef.current &&
          !actionButtonRef.current.contains(target) &&
          actionMenuRef.current &&
          !actionMenuRef.current.contains(target)
        ) {
          setShowUploadTooltip(false)
        }
      }
    }
    const handleScroll = () => {
      if (showUploadTooltip) {
        setShowUploadTooltip(false)
      }
    }
    if (showUploadTooltip) {
      document.addEventListener('mousedown', handleClickOutside)
      window.addEventListener('scroll', handleScroll, true)
      window.addEventListener('resize', handleScroll)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
        window.removeEventListener('scroll', handleScroll, true)
        window.removeEventListener('resize', handleScroll)
      }
    }
  }, [showUploadTooltip])
  
  // Auto-dismiss sources tooltip after 3 seconds
  useEffect(() => {
    if (showSourcesTooltip) {
      const timer = setTimeout(() => {
        setShowSourcesTooltip(false)
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [showSourcesTooltip])
  
  // Close sources tooltip when clicking outside or scrolling
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showSourcesTooltip) {
        const target = event.target as Node
        if (
          sourcesButtonRef.current &&
          !sourcesButtonRef.current.contains(target) &&
          actionMenuRef.current &&
          !actionMenuRef.current.contains(target)
        ) {
          setShowSourcesTooltip(false)
        }
      }
    }
    const handleScroll = () => {
      if (showSourcesTooltip) {
        setShowSourcesTooltip(false)
      }
    }
    if (showSourcesTooltip) {
      document.addEventListener('mousedown', handleClickOutside)
      window.addEventListener('scroll', handleScroll, true)
      window.addEventListener('resize', handleScroll)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
        window.removeEventListener('scroll', handleScroll, true)
        window.removeEventListener('resize', handleScroll)
      }
    }
  }, [showSourcesTooltip])
  
  // Auto-dismiss translation tooltip after 3 seconds
  useEffect(() => {
    if (showTranslationTooltip) {
      const timer = setTimeout(() => {
        setShowTranslationTooltip(false)
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [showTranslationTooltip])
  
  // Close translation tooltip when clicking outside or scrolling
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showTranslationTooltip) {
        const target = event.target as Node
        if (
          translationButtonRef.current &&
          !translationButtonRef.current.contains(target) &&
          actionMenuRef.current &&
          !actionMenuRef.current.contains(target)
        ) {
          setShowTranslationTooltip(false)
        }
      }
    }
    const handleScroll = () => {
      if (showTranslationTooltip) {
        setShowTranslationTooltip(false)
      }
    }
    if (showTranslationTooltip) {
      document.addEventListener('mousedown', handleClickOutside)
      window.addEventListener('scroll', handleScroll, true)
      window.addEventListener('resize', handleScroll)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
        window.removeEventListener('scroll', handleScroll, true)
        window.removeEventListener('resize', handleScroll)
      }
    }
  }, [showTranslationTooltip])
  
  // Auto-dismiss web search limit tooltip after 5 seconds
  useEffect(() => {
    if (showWebSearchLimitTooltip) {
      const timer = setTimeout(() => {
        setShowWebSearchLimitTooltip(false)
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [showWebSearchLimitTooltip])
  
  // Close web search limit tooltip when clicking outside or scrolling
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showWebSearchLimitTooltip) {
        const target = event.target as Node
        if (
          webSearchButtonRef.current &&
          !webSearchButtonRef.current.contains(target)
        ) {
          setShowWebSearchLimitTooltip(false)
        }
      }
    }
    const handleScroll = () => {
      if (showWebSearchLimitTooltip) {
        setShowWebSearchLimitTooltip(false)
      }
    }
    if (showWebSearchLimitTooltip) {
      document.addEventListener('mousedown', handleClickOutside)
      window.addEventListener('scroll', handleScroll, true)
      window.addEventListener('resize', handleScroll)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
        window.removeEventListener('scroll', handleScroll, true)
        window.removeEventListener('resize', handleScroll)
      }
    }
  }, [showWebSearchLimitTooltip])
  
  // Auto-dismiss Serper limit tooltip after 5 seconds
  useEffect(() => {
    if (showSerperLimitTooltip) {
      const timer = setTimeout(() => {
        setShowSerperLimitTooltip(false)
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [showSerperLimitTooltip])
  
  // Close Serper limit tooltip when clicking outside or scrolling
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showSerperLimitTooltip) {
        const target = event.target as Node
        if (
          webSearchButtonRef.current &&
          !webSearchButtonRef.current.contains(target)
        ) {
          setShowSerperLimitTooltip(false)
        }
      }
    }
    const handleScroll = () => {
      if (showSerperLimitTooltip) {
        setShowSerperLimitTooltip(false)
      }
    }
    if (showSerperLimitTooltip) {
      document.addEventListener('mousedown', handleClickOutside)
      window.addEventListener('scroll', handleScroll, true)
      window.addEventListener('resize', handleScroll)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
        window.removeEventListener('scroll', handleScroll, true)
        window.removeEventListener('resize', handleScroll)
      }
    }
  }, [showSerperLimitTooltip])
  
  // Auto-dismiss chat message limit tooltip after 5 seconds
  useEffect(() => {
    if (showChatMessageLimitTooltip) {
      const timer = setTimeout(() => {
        setShowChatMessageLimitTooltip(false)
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [showChatMessageLimitTooltip])
  
  // Close chat message limit tooltip when clicking anywhere on the screen
  useEffect(() => {
    const handleClick = () => {
      if (showChatMessageLimitTooltip) {
        setShowChatMessageLimitTooltip(false)
      }
    }
    const handleScroll = () => {
      if (showChatMessageLimitTooltip) {
        setShowChatMessageLimitTooltip(false)
      }
    }
    if (showChatMessageLimitTooltip) {
      document.addEventListener('mousedown', handleClick)
      window.addEventListener('scroll', handleScroll, true)
      window.addEventListener('resize', handleScroll)
      return () => {
        document.removeEventListener('mousedown', handleClick)
        window.removeEventListener('scroll', handleScroll, true)
        window.removeEventListener('resize', handleScroll)
      }
    }
  }, [showChatMessageLimitTooltip])
  
  // Close law dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isLawDropdownOpen && lawDropdownRef.current && !lawDropdownRef.current.contains(event.target as Node)) {
        setIsLawDropdownOpen(false)
      }
    }
    if (isLawDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isLawDropdownOpen])
  
  // Track which panel is active/expanded when multiple are open: 'editor', 'vault', or 'tableur'
  const [activePanel, setActivePanel] = useState<'editor' | 'vault' | 'tableur'>('editor')
  
  // Network connection detection
  const checkNetworkConnection = (): boolean => {
    if (typeof navigator === 'undefined') {
      return true // Assume online in SSR environments
    }
    return navigator.onLine
  }

  // Timeout wrapper for promises
  const withTimeout = <T,>(promise: Promise<T>, timeoutMs: number, operationName?: string): Promise<T> => {
    return Promise.race([
      promise,
      new Promise<T>((_, reject) => {
        const timeoutError = new Error(
          operationName 
            ? `L'opération "${operationName}" a pris trop de temps (${timeoutMs / 1000}s). Veuillez réessayer.`
            : `L'opération a pris trop de temps (${timeoutMs / 1000}s). Veuillez réessayer.`
        ) as any
        timeoutError.isTimeout = true
        timeoutError.timeoutMs = timeoutMs
        setTimeout(() => reject(timeoutError), timeoutMs)
      })
    ])
  }

  // Default timeout constants
  const TIMEOUT_FETCH = 10000 // 10 seconds for fetch operations
  const TIMEOUT_SAVE = 60000  // 60 seconds for save operations
  
  // Categorize error for better user feedback
  const categorizeError = (error: any): {
    category: 'network' | 'auth' | 'database' | 'timeout' | 'rate-limit' | 'unknown',
    message: string,
    retryable: boolean
  } => {
    // Check if offline first
    if (!checkNetworkConnection() || error?.isOffline || error?.message?.includes('No internet connection')) {
      return {
        category: 'network',
        message: 'Vous êtes hors ligne. Vérifiez votre connexion internet.',
        retryable: false // Don't retry when offline
      }
    }
    
    // Network errors
    if (
      error?.message?.includes('fetch') ||
      error?.message?.includes('network') ||
      error?.message?.includes('Failed to fetch') ||
      error?.name === 'NetworkError' ||
      (error?.name === 'TypeError' && error?.message?.includes('fetch'))
    ) {
      return {
        category: 'network',
        message: 'Problème de connexion. Vérifiez votre internet et réessayez.',
        retryable: checkNetworkConnection() // Only retryable if online
      }
    }
    
    // Timeout errors
    if (
      error?.message?.includes('timeout') ||
      error?.code === 'ETIMEDOUT' ||
      error?.status === 408
    ) {
      return {
        category: 'timeout',
        message: 'Chargement interrompu. Cela est généralement dû à une instabilité du réseau Internet. Veuillez vérifier votre connexion et réessayer.',
        retryable: true
      }
    }
    
    // Authentication errors
    if (
      error?.message?.includes('JWT') ||
      error?.message?.includes('session') ||
      error?.message?.includes('unauthorized') ||
      error?.code === 'PGRST301' ||
      error?.status === 401 ||
      error?.status === 403
    ) {
      return {
        category: 'auth',
        message: 'Session expirée. Veuillez vous reconnecter.',
        retryable: false
      }
    }
    
    // Rate limiting
    if (error?.status === 429 || error?.code === 'PGRST116') {
      return {
        category: 'rate-limit',
        message: 'Trop de requêtes. Veuillez patienter quelques instants avant de réessayer.',
        retryable: true
      }
    }
    
    // Database/server errors
    if (
      error?.status >= 500 ||
      error?.code?.startsWith('PGRST') ||
      error?.code?.startsWith('23') || // PostgreSQL errors
      error?.message?.includes('database') ||
      error?.message?.includes('server')
    ) {
      return {
        category: 'database',
        message: 'Erreur serveur temporaire. Réessayez dans quelques instants.',
        retryable: true
      }
    }
    
    // Unknown errors
    return {
      category: 'unknown',
      message: error?.message || 'Une erreur inattendue s\'est produite. Réessayez.',
      retryable: true
    }
  }
  
  // Retry utility function with exponential backoff
  const retryWithBackoff = async <T,>(
    fn: () => Promise<T>,
    maxRetries: number = 3,
    baseDelay: number = 1000
  ): Promise<T> => {
    let lastError: any
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      // Check network connection before attempting
      if (!checkNetworkConnection()) {
        const offlineError = new Error('No internet connection') as any
        offlineError.isOffline = true
        offlineError.category = 'network'
        throw offlineError
      }
      
      try {
        return await fn()
      } catch (error: any) {
        lastError = error
        
        // Check if user is offline
        if (!checkNetworkConnection()) {
          const offlineError = new Error('No internet connection') as any
          offlineError.isOffline = true
          offlineError.category = 'network'
          throw offlineError
        }
        
        // Check if error is a network error (offline, fetch failed, etc.)
        const isNetworkError = 
          error?.message?.includes('fetch') ||
          error?.message?.includes('network') ||
          error?.message?.includes('Failed to fetch') ||
          error?.message?.includes('No internet connection') ||
          error?.name === 'NetworkError' ||
          (error?.name === 'TypeError' && error?.message?.includes('fetch'))
        
        // Check if error is a timeout
        const isTimeoutError = 
          error?.message?.includes('timeout') ||
          error?.code === 'ETIMEDOUT' ||
          error?.status === 408
        
        // Extract status code from various error formats
        let statusCode: number | null = null
        if (error?.status) statusCode = error.status
        else if (error?.code && typeof error.code === 'number') statusCode = error.code
        else if (error?.code && error.code.startsWith('PGRST')) {
          // Supabase PostgREST errors: PGRST301 = not found, etc.
          // These are typically client errors, don't retry
          if (error.code === 'PGRST301' || error.code === '23505') {
            throw error
          }
        }
        
        // Don't retry on client errors (4xx) except 408 (timeout) and 429 (rate limit)
        if (statusCode !== null) {
          if (statusCode >= 400 && statusCode < 500 && statusCode !== 408 && statusCode !== 429) {
            throw error
          }
        }
        
        // Don't retry on authentication errors
        const isAuthError = 
          error?.message?.includes('JWT') ||
          error?.message?.includes('session') ||
          error?.message?.includes('unauthorized') ||
          error?.code === 'PGRST301' ||
          statusCode === 401 ||
          statusCode === 403
        
        if (isAuthError) {
          throw error
        }
        
        // Don't retry on validation errors (client-side issues)
        const isValidationError = 
          error?.code === '23505' || // Unique constraint violation
          error?.code === '23503' || // Foreign key violation
          statusCode === 400
        
        if (isValidationError && !isNetworkError && !isTimeoutError) {
          throw error
        }
        
        // If this was the last attempt, throw the error
        if (attempt === maxRetries) {
          throw error
        }
        
        // Calculate delay with exponential backoff (1s, 2s, 4s)
        const delay = baseDelay * Math.pow(2, attempt)
        
        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, delay))
      }
    }
    
    throw lastError
  }
  
  // Tableur Avocat spreadsheet data (modular for future backend integration)
  const [spreadsheets, setSpreadsheets] = useState<Array<{id: string, title: string, data: (string | number | null)[][]}>>([])
  const [activeSpreadsheetId, setActiveSpreadsheetId] = useState<string | null>(null)
  
  // Get active spreadsheet data
  const activeSpreadsheet = activeSpreadsheetId ? spreadsheets.find(s => s.id === activeSpreadsheetId) : undefined
  const [showExportMenu, setShowExportMenu] = useState(false)
  const [selectedDocuments, setSelectedDocuments] = useState<Set<number>>(new Set())
  const [documentFilters, setDocumentFilters] = useState({
    source: {
      'IA': false,
      'Éditeur': false,
      'Tableur': false,
      'workflow': false
    },
    type: {
      'Reformulé': false,
      'Analyse': false,
      'Résumé': false,
      'Brouillon': false
    },
    date: {
      'Aujourd\'hui': false,
      '7 jours': false,
      '30 jours': false
    }
  })
  
  // Selection locking state (for Editeur Avocat)
  const [isSelectionLocked, setIsSelectionLocked] = useState(false)
  const [lockedSelection, setLockedSelection] = useState<{from: number, to: number} | null>(null)
  const [showSelectionBar, setShowSelectionBar] = useState(false)
  const [selectionBarPosition, setSelectionBarPosition] = useState<{top: number, left: number}>({ top: 0, left: 0 })
  
  // Table selection locking state (for Tableur Avocat)
  const [isTableSelectionLocked, setIsTableSelectionLocked] = useState(false)
  const [lockedTableSelection, setLockedTableSelection] = useState<{type: 'column' | 'row', index: number} | null>(null)
  const [tableSelectionBarPosition, setTableSelectionBarPosition] = useState<{top: number, left: number}>({ top: 0, left: 0 })
  const tableSelectionBarRef = useRef<HTMLDivElement>(null)
  
  // Multi-document state
  const [documents, setDocuments] = useState<Array<{id: string, title: string, content: string}>>([
    { 
      id: '1', 
      title: 'Nouveau document', 
      content: `<p>Cet espace vous permet d'éditer vos documents en toute sécurité avant toute <strong style="font-size: 14pt;">analyse complète</strong> par l'intelligence artificielle.</p>
<p>Vous pouvez y importer un fichier, modifier librement son contenu et surtout <strong style="font-size: 14pt;">supprimer toute donnée personnelle ou sensible</strong> afin de garantir la confidentialité de vos documents.</p>
<p>Les <strong style="font-size: 14pt;">réponses générées par Avocat AI</strong> peuvent également être ouvertes et retravaillées ici, vous gardant ainsi pleinement maître du contenu final.</p>
<p>Après avoir sélectionné un passage, choisissez une <strong style="font-size: 14pt;">action</strong> ou <strong style="font-size: 14pt;">formulez</strong> votre demande dans le chat situé à gauche.</p>
<p><strong style="font-size: 14pt;">Avocat AI</strong> générera alors une proposition que vous pourrez accepter ou rejeter.</p>` 
    }
  ])
  const [activeDocumentId, setActiveDocumentId] = useState<string>('1')
  
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const actionMenuRef = useRef<HTMLDivElement>(null)
  const userMenuRef = useRef<HTMLDivElement>(null)
  const exportMenuRef = useRef<HTMLDivElement>(null)
  const selectionBarRef = useRef<HTMLDivElement>(null)

  // Get active document
  const activeDocument = documents.find(doc => doc.id === activeDocumentId) || documents[0]

  // Tiptap editor setup - active and editable
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // StarterKit doesn't include underline, so we add it separately
        underline: false, // Explicitly disable if it exists
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
        alignments: ['left', 'center', 'right', 'justify'],
      }),
      Underline,
      TextStyle.extend({
        addAttributes() {
          return {
            ...this.parent?.(),
            fontSize: {
              default: null,
              parseHTML: element => element.style.fontSize,
              renderHTML: attributes => {
                if (!attributes.fontSize) {
                  return {}
                }
                return {
                  style: `font-size: ${attributes.fontSize}`,
                }
              },
            },
          }
        },
      }),
      FontFamily,
      Highlight.configure({
        multicolor: true,
        HTMLAttributes: {
          class: 'locked-selection-highlight',
        },
      }),
      TableKit.configure({
        resizable: true,
      }),
    ],
    editable: true,
    content: activeDocument.content,
    onUpdate: ({ editor }) => {
      // Save content as user types
      const content = editor.getHTML()
      setDocuments(prev => 
        prev.map(doc => 
          doc.id === activeDocumentId ? { ...doc, content } : doc
        )
      )
    },
    onSelectionUpdate: ({ editor }) => {
      const { from, to } = editor.state.selection
      const selectedText = editor.state.doc.textBetween(from, to, '')
      setSelectedCharCount(selectedText.length)
      
      // Show selection bar when AI is activated and there's a new selection (not locked yet)
      if (isEditeurAIActivated && !isSelectionLocked && from !== to) {
        // Get the position of the selection to position the bar
        const selection = window.getSelection()
        if (selection && selection.rangeCount > 0) {
          const range = selection.getRangeAt(0)
          const rect = range.getBoundingClientRect()
          
          // Position above selection, but if not enough space, position below
          let topPosition = rect.top - 45
          if (topPosition < 80) { // If too close to top (accounting for header)
            topPosition = rect.bottom + 5 // Position below instead
          }
          
          setSelectionBarPosition({
            top: topPosition,
            left: rect.left + (rect.width / 2)
          })
        }
        setShowSelectionBar(true)
      } else if (!isSelectionLocked && from === to) {
        setShowSelectionBar(false)
      }
    },
  })

  // Save current document content before switching
  const saveCurrentDocument = () => {
    if (editor) {
      const content = editor.getHTML()
      setDocuments(prev => 
        prev.map(doc => 
          doc.id === activeDocumentId ? { ...doc, content } : doc
        )
      )
    }
  }

  // Switch to a different document
  const switchToDocument = (docId: string) => {
    saveCurrentDocument()
    setActiveDocumentId(docId)
  }

  // Create a new document (max 5 tabs)
  const createNewDocument = () => {
    if (documents.length >= 5) return // Max 5 tabs
    saveCurrentDocument()
    const newDoc = {
      id: Date.now().toString(),
      title: 'Nouveau document',
      content: `<p>Cet espace vous permet d'éditer vos documents en toute sécurité avant toute <strong style="font-size: 14pt;">analyse complète</strong> par l'intelligence artificielle.</p>
<p>Vous pouvez y importer un fichier, modifier librement son contenu et surtout <strong style="font-size: 14pt;">supprimer toute donnée personnelle ou sensible</strong> afin de garantir la confidentialité de vos documents.</p>
<p>Les <strong style="font-size: 14pt;">réponses générées par Avocat AI</strong> peuvent également être ouvertes et retravaillées ici, vous gardant ainsi pleinement maître du contenu final.</p>
<p>Après avoir sélectionné un passage, choisissez une <strong style="font-size: 14pt;">action</strong> ou <strong style="font-size: 14pt;">formulez</strong> votre demande dans le chat situé à gauche.</p>
<p><strong style="font-size: 14pt;">Avocat AI</strong> générera alors une proposition que vous pourrez accepter ou rejeter.</p>`
    }
    setDocuments(prev => [...prev, newDoc])
    setActiveDocumentId(newDoc.id)
  }

  // Close a document
  const closeDocument = (docId: string) => {
    if (documents.length === 1) return // Don't close the last document
    
    const docIndex = documents.findIndex(doc => doc.id === docId)
    const newDocuments = documents.filter(doc => doc.id !== docId)
    setDocuments(newDocuments)
    
    // Switch to adjacent document
    if (docId === activeDocumentId) {
      const newActiveDoc = newDocuments[Math.max(0, docIndex - 1)]
      setActiveDocumentId(newActiveDoc.id)
    }
  }

  // Create a new spreadsheet (max 5 tabs)
  const createNewSpreadsheet = () => {
    if (spreadsheets.length >= 5) return // Max 5 tabs
    const newSpreadsheet = {
      id: Date.now().toString(),
      title: 'Nouveau tableur',
      data: [
        ['Colonne 1', 'Colonne 2', 'Colonne 3'], // Header row with columns (hidden)
        ['', '', ''], // Row 1
        ['', '', ''], // Row 2
        ['', '', ''], // Row 3
        ['', '', ''], // Row 4
        ['', '', ''], // Row 5
        ['', '', ''], // Row 6
        ['', '', ''], // Row 7
        ['', '', ''], // Row 8
        ['', '', ''], // Row 9
        ['', '', ''], // Row 10
      ]
    }
    setSpreadsheets([...spreadsheets, newSpreadsheet])
    setActiveSpreadsheetId(newSpreadsheet.id)
  }

  // Switch to a different spreadsheet
  const switchToSpreadsheet = (spreadsheetId: string) => {
    setActiveSpreadsheetId(spreadsheetId)
  }

  // Close a spreadsheet tab
  const closeSpreadsheet = (spreadsheetId: string) => {
    const newSpreadsheets = spreadsheets.filter(s => s.id !== spreadsheetId)
    setSpreadsheets(newSpreadsheets)
    // If closing active tab, switch to another one if available
    if (spreadsheetId === activeSpreadsheetId) {
      if (newSpreadsheets.length > 0) {
        setActiveSpreadsheetId(newSpreadsheets[0].id)
      } else {
        setActiveSpreadsheetId(null)
        setCurrentTableData(null)
      }
    }
  }

  // Handle "Valider" - Lock the current selection with persistent highlight
  const handleValidateSelection = () => {
    if (editor) {
      const { from, to } = editor.state.selection
      if (from !== to) {
        // Apply a persistent highlight mark to the selected text
        editor.chain().focus().setHighlight({ color: '#2E9E6F' }).run()
        setLockedSelection({ from, to })
        setIsSelectionLocked(true)
        setShowSelectionBar(true)
      }
    }
  }

  // Handle "Annuler" - Unlock and remove the highlight
  const handleCancelSelection = () => {
    if (editor && lockedSelection) {
      // Remove the highlight from the previously locked selection
      editor.chain()
        .focus()
        .setTextSelection(lockedSelection)
        .unsetHighlight()
        .setTextSelection(lockedSelection.from)
        .run()
    }
    setIsSelectionLocked(false)
    setLockedSelection(null)
    setShowSelectionBar(false)
  }
  
  // Handle table selection validation (for Tableur Avocat)
  const handleValidateTableSelection = () => {
    if (selectedColumnIndex !== null) {
      setLockedTableSelection({ type: 'column', index: selectedColumnIndex })
      setIsTableSelectionLocked(true)
      
      // Send automatic assistant message explaining what AI can do
      const assistantMessage: ChatMessage = {
        id: Date.now().toString(),
        text: `Sélection validée.
Je peux maintenant vous aider à travailler sur cette sélection.

Vous pouvez par exemple :
• reformuler ou corriger les données
• traduire le contenu
• nettoyer ou normaliser les valeurs
• résumer ou extraire des informations

Dites-moi simplement ce que vous souhaitez faire.`,
        sender: 'assistant',
        timestamp: new Date()
      }
      setChatMessages(prev => [...prev, assistantMessage])
    } else if (selectedRowIndex !== null) {
      setLockedTableSelection({ type: 'row', index: selectedRowIndex })
      setIsTableSelectionLocked(true)
      
      // Send automatic assistant message explaining what AI can do
      const assistantMessage: ChatMessage = {
        id: Date.now().toString(),
        text: `Sélection validée.
Je peux maintenant vous aider à travailler sur cette sélection.

Vous pouvez par exemple :
• reformuler ou corriger les données
• traduire le contenu
• nettoyer ou normaliser les valeurs
• résumer ou extraire des informations

Dites-moi simplement ce que vous souhaitez faire.`,
        sender: 'assistant',
        timestamp: new Date()
      }
      setChatMessages(prev => [...prev, assistantMessage])
    }
  }
  
  // Handle table selection cancellation (for Tableur Avocat)
  const handleCancelTableSelection = () => {
    setIsTableSelectionLocked(false)
    setLockedTableSelection(null)
    setSelectedColumnIndex(null)
    setSelectedRowIndex(null)
  }
  
  // Helper function to update table with locked selection
  const updateTableWithLockedSelection = (aiTable: TableData) => {
    const hasLockedTableSelection = isTableSelectionLocked && lockedTableSelection && currentTableData
    
    if (hasLockedTableSelection) {
      // Update only the selected column or row
      const newTableData = { ...currentTableData }
      
      if (lockedTableSelection.type === 'column') {
        // Update selected column with AI data (first column of AI response)
        const columnIndex = lockedTableSelection.index
        newTableData.rows = newTableData.rows.map((row, rowIdx) => {
          const newRow = [...row]
          if (aiTable.rows[rowIdx] && aiTable.rows[rowIdx][0] !== undefined) {
            newRow[columnIndex] = aiTable.rows[rowIdx][0]
          }
          return newRow
        })
      } else {
        // Update selected row with AI data (first row of AI response)
        const rowIndex = lockedTableSelection.index
        if (aiTable.rows[0]) {
          newTableData.rows[rowIndex] = aiTable.rows[0].slice(0, newTableData.columns.length)
        }
      }
      setCurrentTableData(newTableData)
    } else {
      // No locked selection - update entire table
      setCurrentTableData(aiTable)
    }
  }

  // Helper function to process AI response and determine if it's a table or text
  const processAIResponse = (aiText: string, isSpreadsheetMode: boolean = false): AiSpreadsheetResult => {
    if (!aiText || typeof aiText !== 'string') {
      return { kind: 'text', text: aiText || '' }
    }

    // In spreadsheet mode, be very aggressive about finding tables
    if (isSpreadsheetMode) {
      // First, try to extract table from code blocks (```markdown or ```)
      let extractedTable = aiText.trim()
      
      // Remove code block markers if present
      const codeBlockRegex = /```(?:markdown)?\s*\n?([\s\S]*?)\n?```/g
      const codeBlockMatch = codeBlockRegex.exec(aiText)
      if (codeBlockMatch && codeBlockMatch[1]) {
        extractedTable = codeBlockMatch[1].trim()
      }
      
      // Try to find a markdown table - be very lenient in spreadsheet mode
      // Look for any lines that start and end with |
      const lines = extractedTable.split('\n').map(line => line.trim()).filter(line => line.length > 0)
      const tableLines = lines.filter(line => line.startsWith('|') && line.endsWith('|'))
      
      // If we have at least 2 lines that look like table rows, try to parse
      if (tableLines.length >= 2) {
        const tableText = tableLines.join('\n')
        console.log('Found potential table in spreadsheet mode:', tableText.substring(0, 200))
        
        // Try to parse even if validation fails
        try {
          const tableData = parseMarkdownTable(tableText)
          console.log('Successfully parsed table in spreadsheet mode:', tableData.columns.length, 'columns,', tableData.rows.length, 'rows')
          return { kind: 'table', table: tableData }
        } catch (error) {
          console.warn('Failed to parse table, trying with validation:', error)
          // If parseMarkdownTable fails, try isValidMarkdownTable first
          if (isValidMarkdownTable(tableText)) {
            try {
              const tableData = parseMarkdownTable(tableText)
              return { kind: 'table', table: tableData }
            } catch (e) {
              console.warn('Parse failed even with valid table:', e)
            }
          }
        }
      }
      
      // If still no table found, check if entire text is a table
      if (isValidMarkdownTable(extractedTable)) {
        try {
          const tableData = parseMarkdownTable(extractedTable)
          console.log('Successfully parsed entire text as table:', tableData.columns.length, 'columns')
          return { kind: 'table', table: tableData }
        } catch (error) {
          console.warn('Failed to parse entire text as table:', error)
        }
      }
      
      // Last resort: if text contains | characters and multiple lines, try to force parse
      if (extractedTable.includes('|') && extractedTable.split('\n').length >= 2) {
        console.log('Attempting force parse in spreadsheet mode')
        try {
          const tableData = parseMarkdownTable(extractedTable)
          console.log('Force parse succeeded:', tableData.columns.length, 'columns')
          return { kind: 'table', table: tableData }
        } catch (error) {
          console.warn('Force parse failed:', error)
          console.log('Text that failed:', extractedTable.substring(0, 500))
        }
      }
    }

    // Regular mode: use original logic
    // First, try to extract table from code blocks (```markdown or ```)
    let extractedTable = aiText
    
    // Remove code block markers if present
    const codeBlockRegex = /```(?:markdown)?\s*\n?([\s\S]*?)\n?```/g
    const codeBlockMatch = codeBlockRegex.exec(aiText)
    if (codeBlockMatch && codeBlockMatch[1]) {
      extractedTable = codeBlockMatch[1].trim()
    }
    
    // Try to find a markdown table in the text (even if there's other text)
    // Look for table pattern: | header | header | followed by |---|---| followed by | data | data |
    // Be more flexible - allow tables that might have extra whitespace or not perfect formatting
    const tablePattern = /(\s*\|[^\n]+\|\s*\n\s*\|[-\s|:]+\|\s*\n(?:\s*\|[^\n]+\|\s*\n?)+)/m
    let tableMatch = tablePattern.exec(extractedTable)
    
    // If no match, try a more lenient pattern (just look for multiple lines starting with |)
    if (!tableMatch) {
      const lenientPattern = /(\|.+\|\s*\n(?:\|.+\|\s*\n)+)/m
      tableMatch = lenientPattern.exec(extractedTable)
    }
    
    if (tableMatch && tableMatch[1]) {
      const tableText = tableMatch[1].trim()
      console.log('Found potential table:', tableText.substring(0, 200))
      if (isValidMarkdownTable(tableText)) {
        try {
          const tableData = parseMarkdownTable(tableText)
          console.log('Successfully parsed table:', tableData.columns.length, 'columns,', tableData.rows.length, 'rows')
          return { kind: 'table', table: tableData }
        } catch (error) {
          console.warn('Failed to parse markdown table:', error)
          // If parsing fails, treat as text
          return { kind: 'text', text: aiText }
        }
      }
    }
    
    // Fallback: check if the entire text is a valid table
    if (isValidMarkdownTable(extractedTable)) {
      try {
        const tableData = parseMarkdownTable(extractedTable)
        console.log('Successfully parsed entire text as table:', tableData.columns.length, 'columns,', tableData.rows.length, 'rows')
        return { kind: 'table', table: tableData }
      } catch (error) {
        console.warn('Failed to parse markdown table:', error)
        return { kind: 'text', text: aiText }
      }
    }
    
    return { kind: 'text', text: aiText }
  }

  // Handle AI toggle - reset selection state when deactivated
  // Handle Tableur "Activer Avocat AI" button - activates AI mode
  const handleTableurActivateAI = () => {
    if (isChatLoading || isTableurAIActivated) return

    // Activate AI state
    setIsTableurAIActivated(true)
    setActiveAIMode('tableur')
  }

  // Handle "Créer un tableau" action button - shows premium message
  const handleCreateTableAction = () => {
    setShowCreateTableTooltip(true)
    setIsActionMenuOpen(false)
  }

  // Handle Editeur "Activer Avocat AI" button - activates AI mode
  const handleEditeurActivateAI = () => {
    if (isChatLoading || isEditeurAIActivated) return

    // Activate AI state
    setIsEditeurAIActivated(true)
    setActiveAIMode('editeur')
  }

  const handleTableurAIToggle = () => {
    const newState = !isTableurAIActivated
    setIsTableurAIActivated(newState)
    if (!newState) {
      // Reset AI mode when deactivated (only if it was tableur mode)
      if (activeAIMode === 'tableur') {
        setActiveAIMode(null)
      }
      // Clear any locked table selection when AI is deactivated
      setIsTableSelectionLocked(false)
      setLockedTableSelection(null)
      setSelectedColumnIndex(null)
      setSelectedRowIndex(null)
    }
  }

  const handleEditeurAIToggle = () => {
    const newState = !isEditeurAIActivated
    setIsEditeurAIActivated(newState)
    if (!newState) {
      // Reset AI mode when deactivated (only if it was editeur mode)
      if (activeAIMode === 'editeur') {
        setActiveAIMode(null)
      }
      // Remove highlight and reset selection locking when AI is deactivated
      if (editor && lockedSelection) {
        editor.chain()
          .focus()
          .setTextSelection(lockedSelection)
          .unsetHighlight()
          .setTextSelection(lockedSelection.from)
          .run()
      }
      setIsSelectionLocked(false)
      setLockedSelection(null)
      setShowSelectionBar(false)
    }
  }

  // Selection is now persisted via highlight mark - no need for complex restoration
  // The green highlight stays visible regardless of where the user clicks or types

  // Update editor content when active document changes
  useEffect(() => {
    if (editor && activeDocument) {
      const currentContent = editor.getHTML()
      // Only update if content is different to avoid unnecessary re-renders
      if (currentContent !== activeDocument.content) {
        editor.commands.setContent(activeDocument.content)
      }
    }
  }, [activeDocumentId, editor, activeDocument])

  // Prevent body scroll when editor modal is open
  useEffect(() => {
    if (isEditorOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isEditorOpen])


  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      const newHeight = Math.min(textareaRef.current.scrollHeight, 160)
      textareaRef.current.style.height = `${newHeight}px`
    }
  }, [inputValue])

  // Handle voice transcript - insert into input when received
  useEffect(() => {
    if (transcript) {
      const transcriptText = transcript.trim()
      
      // Only insert if transcript is not empty (silent failure handling)
      if (transcriptText) {
        console.log('📝 Received transcript:', transcriptText)
        
        // Insert transcript into input immediately
        setInputValue(prev => {
          const newValue = prev.trim() ? `${prev} ${transcriptText}` : transcriptText
          console.log('📝 Setting input value to:', newValue)
          return newValue
        })
        
        // Focus textarea and reset after state update
        setTimeout(() => {
          textareaRef.current?.focus()
          // Reset voice recorder states (but transcript will be cleared on next recording start)
          resetVoiceRecorder()
        }, 300)
      } else {
        // Empty transcript = silent failure (rate limit or error) - just reset without inserting
        resetVoiceRecorder()
      }
    }
  }, [transcript, resetVoiceRecorder])

  // Auto-focus textarea on mount
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus()
    }
  }, [])

  // Close dropdown menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      if (!target.closest('.spreadsheet-dropdown') && !target.closest('.spreadsheet-add-button')) {
        setOpenColumnMenu(null)
        setOpenRowMenu(null)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // Load chat messages from sessionStorage on component mount
  useEffect(() => {
    // Only load once on mount, and only if we haven't loaded before
    if (hasLoadedFromStorage.current) {
      setIsLoadingSession(false)
      return
    }
    
    const loadSession = async () => {
      // Add a small delay to show smooth loading animation
      await new Promise(resolve => setTimeout(resolve, 300))
      
      try {
        const savedMessages = sessionStorage.getItem(SESSION_STORAGE_KEY)
        if (savedMessages) {
          const parsedMessages = JSON.parse(savedMessages)
          // Convert timestamp strings back to Date objects
          const messagesWithDates: ChatMessage[] = parsedMessages.map((msg: any) => ({
            ...msg,
            timestamp: new Date(msg.timestamp)
          }))
          // Always load if we have saved messages and no current messages
          // This ensures sessionStorage is restored on refresh
          if (messagesWithDates.length > 0) {
            setChatMessages(messagesWithDates)
          }
        }

        // Load editor documents from sessionStorage
        // Only load if documents array is still in default state (has only the initial document)
        const defaultDocumentContent = `<p>Cet espace vous permet d'éditer vos documents en toute sécurité avant toute <strong style="font-size: 14pt;">analyse complète</strong> par l'intelligence artificielle.</p>
<p>Vous pouvez y importer un fichier, modifier librement son contenu et surtout <strong style="font-size: 14pt;">supprimer toute donnée personnelle ou sensible</strong> afin de garantir la confidentialité de vos documents.</p>
<p>Les <strong style="font-size: 14pt;">réponses générées par Avocat AI</strong> peuvent également être ouvertes et retravaillées ici, vous gardant ainsi pleinement maître du contenu final.</p>
<p>Après avoir sélectionné un passage, choisissez une <strong style="font-size: 14pt;">action</strong> ou <strong style="font-size: 14pt;">formulez</strong> votre demande dans le chat situé à gauche.</p>
<p><strong style="font-size: 14pt;">Avocat AI</strong> générera alors une proposition que vous pourrez accepter ou rejeter.</p>`
        
        const isDefaultState = documents.length === 1 && 
          documents[0].id === '1' && 
          documents[0].title === 'Nouveau document' &&
          documents[0].content === defaultDocumentContent

        if (isDefaultState) {
          const savedDocuments = sessionStorage.getItem(SESSION_STORAGE_EDITOR_KEY)
          if (savedDocuments) {
            try {
              const parsedData = JSON.parse(savedDocuments)
              if (parsedData.documents && Array.isArray(parsedData.documents) && parsedData.documents.length > 0) {
                setDocuments(parsedData.documents)
                if (parsedData.activeDocumentId) {
                  setActiveDocumentId(parsedData.activeDocumentId)
                }
                // Mark that we've loaded from storage (editor will be updated in separate useEffect)
                hasLoadedEditorFromStorage.current = false // Reset flag so editor can be updated
              }
            } catch (docError) {
              console.error('Error loading editor documents from sessionStorage:', docError)
              // Clear corrupted sessionStorage data
              sessionStorage.removeItem(SESSION_STORAGE_EDITOR_KEY)
            }
          }
        }

        // Load spreadsheet data from sessionStorage
        // Only load if spreadsheets array is still in default state (has only the initial spreadsheet)
        const defaultSpreadsheetData = [
          ['Colonne 1', 'Colonne 2', 'Colonne 3'], // Header row
          ['', '', ''], ['', '', ''], ['', '', ''], ['', '', ''], ['', '', ''],
          ['', '', ''], ['', '', ''], ['', '', ''], ['', '', ''], ['', '', '']
        ]
        
        const isSpreadsheetDefaultState = spreadsheets.length === 1 && 
          spreadsheets[0].id === '1' && 
          spreadsheets[0].title === 'Nouveau tableur' &&
          JSON.stringify(spreadsheets[0].data) === JSON.stringify(defaultSpreadsheetData)

        if (isSpreadsheetDefaultState) {
          const savedSpreadsheets = sessionStorage.getItem(SESSION_STORAGE_SPREADSHEET_KEY)
          if (savedSpreadsheets) {
            try {
              const parsedData = JSON.parse(savedSpreadsheets)
              if (parsedData.spreadsheets && Array.isArray(parsedData.spreadsheets) && parsedData.spreadsheets.length > 0) {
                setSpreadsheets(parsedData.spreadsheets)
                if (parsedData.activeSpreadsheetId) {
                  setActiveSpreadsheetId(parsedData.activeSpreadsheetId)
                  
                  // Update currentTableData immediately when loading from sessionStorage
                  const loadedSpreadsheet = parsedData.spreadsheets.find((s: any) => s.id === parsedData.activeSpreadsheetId) || parsedData.spreadsheets[0]
                  if (loadedSpreadsheet && loadedSpreadsheet.data) {
                    const newTableData = convertSpreadsheetToTableData(loadedSpreadsheet.data)
                    setCurrentTableData(newTableData)
                  }
                } else {
                  // No activeSpreadsheetId, use first spreadsheet
                  const firstSpreadsheet = parsedData.spreadsheets[0]
                  if (firstSpreadsheet && firstSpreadsheet.data) {
                    const newTableData = convertSpreadsheetToTableData(firstSpreadsheet.data)
                    setCurrentTableData(newTableData)
                  }
                }
              }
            } catch (spreadsheetError) {
              console.error('Error loading spreadsheet data from sessionStorage:', spreadsheetError)
              // Clear corrupted sessionStorage data
              sessionStorage.removeItem(SESSION_STORAGE_SPREADSHEET_KEY)
            }
          }
        }
      } catch (error) {
        console.error('Error loading chat messages from sessionStorage:', error)
        // Clear corrupted sessionStorage data
        sessionStorage.removeItem(SESSION_STORAGE_KEY)
      } finally {
        setIsLoadingSession(false)
        hasLoadedFromStorage.current = true
      }
    }
    
    loadSession()
  }, []) // Only run on mount

  // Save chat messages to sessionStorage whenever they change
  useEffect(() => {
    // Skip saving if we're still loading from sessionStorage (to avoid race conditions)
    if (isLoadingSession) return
    
    // Skip saving if we haven't loaded from storage yet (prevent overwriting before load)
    if (!hasLoadedFromStorage.current) return
    
    try {
      if (chatMessages.length > 0) {
        // Convert Date objects to ISO strings for JSON serialization
        const messagesToSave = chatMessages.map(msg => ({
          ...msg,
          timestamp: msg.timestamp.toISOString()
        }))
        sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(messagesToSave))
      }
      // Don't clear sessionStorage when messages are empty - only clear explicitly on "new chat"
      // This prevents accidental clearing during component re-renders
    } catch (error) {
      console.error('Error saving chat messages to sessionStorage:', error)
    }
  }, [chatMessages, isLoadingSession])

  // Save editor documents to sessionStorage whenever they change
  useEffect(() => {
    // Skip saving if we're still loading from sessionStorage (to avoid race conditions)
    if (isLoadingSession) return
    
    // Skip saving if we haven't loaded from storage yet (prevent overwriting before load)
    if (!hasLoadedFromStorage.current) return
    
    try {
      // Save documents array and activeDocumentId
      const dataToSave = {
        documents: documents,
        activeDocumentId: activeDocumentId
      }
      sessionStorage.setItem(SESSION_STORAGE_EDITOR_KEY, JSON.stringify(dataToSave))
    } catch (error) {
      console.error('Error saving editor documents to sessionStorage:', error)
    }
  }, [documents, activeDocumentId, isLoadingSession])

  // Save spreadsheet data to sessionStorage whenever they change
  useEffect(() => {
    // Skip saving if we're still loading from sessionStorage (to avoid race conditions)
    if (isLoadingSession) return
    
    // Skip saving if we haven't loaded from storage yet (prevent overwriting before load)
    if (!hasLoadedFromStorage.current) return
    
    try {
      // Save spreadsheets array and activeSpreadsheetId
      const dataToSave = {
        spreadsheets: spreadsheets,
        activeSpreadsheetId: activeSpreadsheetId
      }
      sessionStorage.setItem(SESSION_STORAGE_SPREADSHEET_KEY, JSON.stringify(dataToSave))
    } catch (error) {
      console.error('Error saving spreadsheet data to sessionStorage:', error)
    }
  }, [spreadsheets, activeSpreadsheetId, isLoadingSession])

  // Sync currentTableData with activeSpreadsheet when activeSpreadsheet changes
  useEffect(() => {
    if (!activeSpreadsheet || !activeSpreadsheet.data) return
    
    // Convert activeSpreadsheet.data to TableData format
    const newTableData = convertSpreadsheetToTableData(activeSpreadsheet.data)
    
    // Only update if different to avoid infinite loops
    // Use JSON.stringify for deep comparison
    if (JSON.stringify(currentTableData) !== JSON.stringify(newTableData)) {
      setCurrentTableData(newTableData)
    }
  }, [activeSpreadsheet, activeSpreadsheetId]) // Note: currentTableData not in deps to avoid infinite loops

  // Sync activeSpreadsheet.data when currentTableData changes (user edits)
  useEffect(() => {
    if (!currentTableData || !activeSpreadsheet) return
    
    // Convert TableData back to 2D array format
    const newData = convertTableDataToSpreadsheet(currentTableData)
    
    // Only update if different
    const currentData = activeSpreadsheet.data
    if (JSON.stringify(currentData) !== JSON.stringify(newData)) {
      setSpreadsheets(prev => prev.map(s => 
        s.id === activeSpreadsheetId 
          ? { ...s, data: newData }
          : s
      ))
    }
  }, [currentTableData, activeSpreadsheetId]) // Note: activeSpreadsheet not in deps to avoid infinite loops

  // Update editor content when documents are loaded from sessionStorage
  useEffect(() => {
    // Only update if:
    // 1. We've loaded from storage (hasLoadedFromStorage.current is true)
    // 2. Editor is initialized
    // 3. We haven't already updated the editor from storage
    // 4. We're not still loading the session
    if (!hasLoadedFromStorage.current || !editor || hasLoadedEditorFromStorage.current || isLoadingSession) {
      return
    }

    // Get the active document content
    const activeDoc = documents.find(doc => doc.id === activeDocumentId) || documents[0]
    if (!activeDoc) return

    // Get current editor content
    const currentContent = editor.getHTML()
    
    // Only update if content is different
    if (currentContent !== activeDoc.content) {
      try {
        editor.commands.setContent(activeDoc.content)
        hasLoadedEditorFromStorage.current = true // Mark as updated to avoid infinite loops
      } catch (error) {
        console.error('Error updating editor content from sessionStorage:', error)
      }
    } else {
      // Content is the same, mark as updated anyway
      hasLoadedEditorFromStorage.current = true
    }
  }, [editor, documents, activeDocumentId, isLoadingSession]) // Note: hasLoadedFromStorage is a ref, checked inside effect

  // Close action menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (actionMenuRef.current && !actionMenuRef.current.contains(event.target as Node)) {
        setIsActionMenuOpen(false)
        // Do NOT reset selectedActionMode here - only reset when clicking the icon itself
      }
    }

    if (isActionMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isActionMenuOpen])

  // Fetch current user session
  useEffect(() => {
    const fetchUser = async () => {
      const { data: { session } } = await supabase.auth.getSession()
      if (session?.user) {
        setCurrentUser({
          email: session.user.email,
          name: session.user.user_metadata?.full_name || session.user.user_metadata?.name || session.user.email?.split('@')[0],
          avatar_url: session.user.user_metadata?.avatar_url || session.user.user_metadata?.picture
        })
        // Fetch saved conversations after user session is confirmed
        await fetchSavedConversations()
        // Load web search usage (non-blocking - don't await to prevent blocking component initialization)
        loadWebSearchUsage().catch(err => {
          console.warn('Failed to load web search usage on mount (non-blocking):', err)
        })
        // Load chat message usage (non-blocking)
        loadChatMessageUsage().catch(err => {
          console.warn('Failed to load chat message usage on mount (non-blocking):', err)
        })
      }
    }
    fetchUser()

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      // Only fetch conversations on actual auth state changes, not token refreshes
      if (event === 'SIGNED_IN' || event === 'SIGNED_OUT') {
        if (session?.user) {
          setCurrentUser({
            email: session.user.email,
            name: session.user.user_metadata?.full_name || session.user.user_metadata?.name || session.user.email?.split('@')[0],
            avatar_url: session.user.user_metadata?.avatar_url || session.user.user_metadata?.picture
          })
          setAvatarError(false) // Reset avatar error on new session
          // Fetch saved conversations on actual sign in
          await fetchSavedConversations()
          // Load web search usage (non-blocking)
          loadWebSearchUsage().catch(err => {
            console.warn('Failed to load web search usage on auth change (non-blocking):', err)
          })
          // Load chat message usage (non-blocking)
          loadChatMessageUsage().catch(err => {
            console.warn('Failed to load chat message usage on auth change (non-blocking):', err)
          })
        } else {
          setCurrentUser(null)
          setAvatarError(false)
          // Clear conversations when user logs out
          setSavedConversations([])
          setWebSearchUsage(null)
          setChatMessageUsage(null)
        }
      } else if (session?.user) {
        // For other events (TOKEN_REFRESHED, USER_UPDATED, etc.), just update user state without fetching
        setCurrentUser({
          email: session.user.email,
          name: session.user.user_metadata?.full_name || session.user.user_metadata?.name || session.user.email?.split('@')[0],
          avatar_url: session.user.user_metadata?.avatar_url || session.user.user_metadata?.picture
        })
        setAvatarError(false)
      } else {
        // No session for non-sign events (shouldn't happen, but handle gracefully)
        setCurrentUser(null)
        setAvatarError(false)
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  // Handle OAuth callback and auto-retry export
  useEffect(() => {
    const handleOAuthCallback = async () => {
      // Check if we have a pending export after OAuth
      const pendingExport = sessionStorage.getItem('pending_google_export')
      
      try {
        // Wait a bit for session to be fully established
        await new Promise(resolve => setTimeout(resolve, 1500))
        
        const { data: { session } } = await supabase.auth.getSession()
        if (!session) {
          console.log('No session after OAuth callback')
          if (pendingExport) sessionStorage.removeItem('pending_google_export')
          return
        }

        // Try to store provider token in user metadata if available
        if (session.provider_token) {
          try {
            console.log('Storing Google token in user metadata')
            // Update user metadata to store the Google token
            const { data: updateData, error: updateError } = await supabase.auth.updateUser({
              data: {
                google_access_token: session.provider_token
              }
            })
            if (updateError) {
              console.warn('Could not store Google token in metadata:', updateError)
            } else {
              console.log('Successfully stored Google token in metadata')
            }
          } catch (err) {
            console.warn('Error storing Google token:', err)
          }
        } else {
          console.warn('No provider_token in session after OAuth')
        }

        if (!pendingExport) return

        const { tableData, messageId } = JSON.parse(pendingExport)
        const spreadsheetData = [tableData.columns, ...tableData.rows]

        // Call Edge Function - it will get the token from user metadata
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
        const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

        if (!supabaseUrl || !supabaseAnonKey) {
          throw new Error('Supabase configuration missing')
        }

        // Check if we have provider_token in session (available right after OAuth)
        let googleAccessToken = session.provider_token
        
        // If not in session, try to get from user metadata
        if (!googleAccessToken && session.user?.user_metadata?.google_access_token) {
          googleAccessToken = session.user.user_metadata.google_access_token
        }

        console.log('Sending export request (OAuth callback):', {
          spreadsheetDataLength: spreadsheetData.length,
          spreadsheetDataPreview: spreadsheetData.slice(0, 2),
          hasUserAccessToken: !!session.access_token,
          hasGoogleToken: !!googleAccessToken,
          hasProviderToken: !!session.provider_token
        })

        // Build request body
        const requestBody: any = {
          spreadsheetData,
          userAccessToken: session.access_token
        }
        if (googleAccessToken) {
          requestBody.googleAccessToken = googleAccessToken
        }

        const response = await fetch(`${supabaseUrl}/functions/v1/export-to-google-sheets`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${supabaseAnonKey}`, // Use anon key for Edge Function access
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestBody)
        })

        const result = await response.json()
        
        console.log('Export response:', { 
          ok: response.ok, 
          status: response.status,
          result 
        })
        
        if (!response.ok) {
          console.error('Export error details:', {
            status: response.status,
            statusText: response.statusText,
            error: result.error,
            fullResult: result
          })
        }

        if (response.ok && result.success && result.spreadsheetUrl) {
          // Success - clear pending export and open sheet
          sessionStorage.removeItem('pending_google_export')
          console.log('Opening spreadsheet:', result.spreadsheetUrl)
          
          // Try to open in new tab
          const newWindow = window.open(result.spreadsheetUrl, '_blank')
          
          // If popup blocked, show URL to user
          if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
            alert(`Export réussi! Ouvrez ce lien dans un nouvel onglet:\n${result.spreadsheetUrl}`)
          }
        } else {
          // Error - clear pending export and show error
          sessionStorage.removeItem('pending_google_export')
          console.error('Export failed:', result)
          if (result.error?.includes('Token Google introuvable')) {
            alert('Token Google introuvable. Veuillez réessayer en cliquant sur le bouton d\'export.')
          } else {
            alert(`Erreur lors de l'export: ${result.error || 'Une erreur est survenue'}`)
          }
        }
      } catch (error) {
        console.error('Error processing pending export after OAuth:', error)
        if (pendingExport) sessionStorage.removeItem('pending_google_export')
      }
    }

    // Check URL for OAuth callback indicators
    const urlParams = new URLSearchParams(window.location.search)
    const hashParams = new URLSearchParams(window.location.hash.substring(1))
    
    // Check if we're returning from OAuth (Supabase adds these params)
    if (urlParams.has('code') || hashParams.has('access_token') || hashParams.has('provider_token')) {
      handleOAuthCallback()
    }
  }, [])

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false)
      }
    }

    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isUserMenuOpen])

  // Close export menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target as Node)) {
        setShowExportMenu(false)
      }
    }

    if (showExportMenu) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showExportMenu])

  // Scroll to bottom of chat when messages change
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [chatMessages])

  // Network status monitoring
  useEffect(() => {
    // Set initial network status
    setIsOnline(checkNetworkConnection())
    
    // Handle online event
    const handleOnline = () => {
      setIsOnline(true)
      // Clear network errors when coming back online
      setVaultError(prev => {
        if (prev?.category === 'network') {
          return null
        }
        return prev
      })
    }
    
    // Handle offline event
    const handleOffline = () => {
      setIsOnline(false)
      // Set network error when going offline
      setVaultError({
        type: 'fetch',
        category: 'network',
        message: 'Vous êtes hors ligne. Vérifiez votre connexion internet.',
        retryable: false
      })
    }
    
    // Add event listeners
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    
    // Cleanup
    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  // Auto-dismiss saveLoadError toast after 5 seconds
  useEffect(() => {
    if (saveLoadError) {
      const timer = setTimeout(() => {
        setSaveLoadError(null)
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [saveLoadError])

  // Fetch vault items when Vault panel opens
  useEffect(() => {
    if (isVaultOpen) {
      fetchVaultItems()
    }
  }, [isVaultOpen])

  // Close vault menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      if (openVaultMenuId !== null && !target.closest('.vault-menu-container')) {
        setOpenVaultMenuId(null)
      }
    }
    
    if (openVaultMenuId !== null) {
      // Use setTimeout to avoid immediate closure when opening
      setTimeout(() => {
        document.addEventListener('click', handleClickOutside)
      }, 0)
      return () => {
        document.removeEventListener('click', handleClickOutside)
      }
    }
  }, [openVaultMenuId])

  // Fetch all vault items from Supabase (unified system)
  // This includes both new vault_items and legacy conversations
  const fetchVaultItems = async () => {
    setIsLoadingVaultItems(true)
    setVaultError(null) // Clear previous errors
    
    try {
      // Get current user session with timeout and retry
      const sessionResult = await retryWithBackoff(async () => {
        return await withTimeout(
          supabase.auth.getSession(),
          10000 // 10 second timeout for session check
        )
      })
      
      const { data: { session } } = sessionResult
      if (!session?.user) {
        setVaultItems([])
        setIsLoadingVaultItems(false)
        return
      }
      
      // Fetch new vault_items with retry and timeout
      const itemsResult = await retryWithBackoff(async () => {
        const result = await withTimeout(
          supabase
            .from('vault_items')
            .select('id, item_type, title, content, metadata, created_at, updated_at')
            .eq('user_id', session.user.id)
            .order('created_at', { ascending: false }),
          TIMEOUT_FETCH,
          'Chargement des éléments du Vault'
        )
        
        if (result.error) {
          throw result.error
        }
        
        return result
      })
      
      const { data: items, error: itemsError } = itemsResult

      if (itemsError) {
        console.error('Error fetching vault items:', {
          message: itemsError.message,
          details: itemsError.details,
          hint: itemsError.hint,
          code: itemsError.code
        })
        // Continue to fetch conversations even if vault_items fails
      }

      // Fetch legacy conversations with retry and timeout
      let conversations: any[] = []
      let conversationsError: any = null
      
      try {
        const conversationsResult = await retryWithBackoff(async () => {
          const result = await withTimeout(
            supabase
              .from('conversations')
              .select('id, title, created_at')
              .eq('user_id', session.user.id)
              .order('created_at', { ascending: false }),
            TIMEOUT_FETCH,
            'Chargement des conversations'
          )
          
          if (result.error) {
            throw result.error
          }
          
          return result
        })
        
        conversations = conversationsResult.data || []
        conversationsError = conversationsResult.error
      } catch (error: any) {
        conversationsError = error
        console.error('Error fetching conversations:', {
          message: error?.message,
          details: error?.details,
          hint: error?.hint,
          code: error?.code
        })
        // Continue with what we have
      }

      // Map vault_items
      const mappedVaultItems = (items || []).map((item: any) => ({
        id: item.id,
        type: item.item_type as 'chat' | 'document' | 'spreadsheet',
        title: item.title,
        content: item.content,
        metadata: item.metadata || {},
        created_at: item.created_at,
        updated_at: item.updated_at || item.created_at,
        isLegacy: false
      }))

      // Map legacy conversations to vault item format
      const mappedConversations = (conversations || []).map((conv: any) => ({
        id: conv.id,
        type: 'chat' as const,
        title: conv.title,
        content: null, // Will be loaded when clicked
        metadata: {},
        created_at: conv.created_at,
        updated_at: conv.created_at,
        isLegacy: true // Mark as legacy conversation
      }))

      // Combine both arrays and sort by created_at
      const allItems = [...mappedVaultItems, ...mappedConversations].sort((a, b) => {
        const dateA = new Date(a.created_at).getTime()
        const dateB = new Date(b.created_at).getTime()
        return dateB - dateA // Newest first
      })
      
      setVaultItems(allItems)
      setVaultError(null) // Clear any previous errors on success
    } catch (error: any) {
      // Handle errors with categorization
      console.error('Error fetching vault items:', error)
      
      const errorInfo = categorizeError(error)
      setVaultError({
        type: 'fetch',
        category: errorInfo.category,
        message: errorInfo.message,
        retryable: errorInfo.retryable
      })
      
      // Keep existing items if available, don't clear on error
      // setVaultItems([]) // Removed - keep existing items
    } finally {
      setIsLoadingVaultItems(false)
    }
  }

  const fetchSavedConversations = async () => {
    setIsLoadingConversations(true)
    setSaveLoadError(null) // Clear any previous errors
    
    try {
      // Wrap entire fetch operation with retry logic
      await retryWithBackoff(async () => {
        // Get current user session with timeout
        let sessionResult
        try {
          sessionResult = await withTimeout(
            supabase.auth.getSession(),
            TIMEOUT_FETCH,
            'Vérification de la session'
          )
        } catch (timeoutError: any) {
          // If session check times out, skip fetching conversations silently
          if (timeoutError?.isTimeout || timeoutError?.message?.includes('temps')) {
            console.warn('Session verification timed out, skipping conversation fetch')
            setSavedConversations([])
            setIsLoadingConversations(false)
            return
          }
          throw timeoutError
        }
        
        const { data: { session } } = sessionResult
        if (!session?.user) {
          setSavedConversations([])
          setIsLoadingConversations(false)
          return
        }
        
        // Query Supabase conversations table with timeout
        // Only select columns that definitely exist: id, title, created_at
        const conversationsResult = await withTimeout(
          supabase
            .from('conversations')
            .select('id, title, created_at')
            .eq('user_id', session.user.id)
            .order('created_at', { ascending: false }),
          TIMEOUT_FETCH,
          'Chargement des conversations'
        )

        const { data: conversations, error } = conversationsResult

        if (error) {
          console.error('Supabase query error:', {
            message: error.message,
            details: error.details,
            hint: error.hint,
            code: error.code
          })
          throw error
        }

        // Update savedConversations state
        // Map to include updated_at with fallback to created_at (for UI display)
        const mappedConversations = (conversations || []).map((conv: any) => ({
          id: conv.id,
          title: conv.title,
          created_at: conv.created_at,
          updated_at: conv.updated_at || conv.created_at // Use created_at as fallback for UI
        }))
        
        setSavedConversations(mappedConversations)
      })
    } catch (error: any) {
      // Handle errors gracefully
      // Check if it's a timeout error first
      const isTimeout = error?.message?.includes('timeout') || 
                       error?.message?.includes('temps') || 
                       error?.isTimeout
      const errorInfo = categorizeError(error)
      const isTimeoutCategory = errorInfo.category === 'timeout'
      
      if (isTimeout || isTimeoutCategory) {
        // Timeout errors are logged at warn level but not shown to user
        console.warn('Conversation fetch timed out (this is normal if network is slow):', error?.message)
        setSavedConversations([])
        return // Exit early for timeout errors
      }
      
      // For non-timeout errors, log them
      console.error('Error fetching saved conversations:', error)
      console.error('Error details:', {
        message: error?.message,
        details: error?.details,
        hint: error?.hint,
        code: error?.code
      })
      
      setSavedConversations([])
    } finally {
      setIsLoadingConversations(false)
    }
  }

  // Load vault item (chat, document, or spreadsheet)
  const loadVaultItem = async (itemId: string, itemType: 'chat' | 'document' | 'spreadsheet') => {
    // Set loading state
    setLoadingVaultItemId(itemId)
    setVaultError(null) // Clear previous errors
    // Clear item-specific error
    setItemErrors(prev => {
      const newMap = new Map(prev)
      newMap.delete(itemId)
      return newMap
    })
    
    try {
      // Try to find item in local state first
      let vaultItem = vaultItems.find(item => item.id === itemId)
      
      // If not found locally, try to fetch from Supabase with retry
      if (!vaultItem) {
        try {
          const sessionResult = await retryWithBackoff(async () => {
            return await withTimeout(
              supabase.auth.getSession(),
              10000 // 10 second timeout for session check
            )
          })
          
          const { data: { session } } = sessionResult
          if (!session?.user) {
            const errorMsg = 'Vous devez être connecté pour charger cet élément'
            setVaultError({
              type: 'load',
              category: 'auth',
              message: errorMsg,
              retryable: false
            })
            setItemErrors(prev => {
              const newMap = new Map(prev)
              newMap.set(itemId, { message: errorMsg, retryable: false })
              return newMap
            })
            setLoadingVaultItemId(null)
            return
          }
          
          // Fetch the item from Supabase with retry and timeout
          const fetchResult = await retryWithBackoff(async () => {
            const result = await withTimeout(
              supabase
                .from('vault_items')
                .select('id, item_type, title, content, metadata, created_at, updated_at')
                .eq('id', itemId)
                .eq('user_id', session.user.id)
                .single(),
              TIMEOUT_FETCH,
              'Chargement de l\'élément'
            )
            
            if (result.error) {
              throw result.error
            }
            
            return result
          })
          
          const { data: fetchedItem } = fetchResult
          
          if (!fetchedItem) {
            const errorMsg = 'Élément introuvable. Il a peut-être été supprimé.'
            setVaultError({
              type: 'load',
              category: 'database',
              message: errorMsg,
              retryable: false
            })
            setItemErrors(prev => {
              const newMap = new Map(prev)
              newMap.set(itemId, { message: errorMsg, retryable: false })
              return newMap
            })
            setLoadingVaultItemId(null)
            return
          }
          
          // Map fetched item to vault item format
          vaultItem = {
            id: fetchedItem.id,
            type: fetchedItem.item_type as 'chat' | 'document' | 'spreadsheet',
            title: fetchedItem.title,
            content: fetchedItem.content,
            metadata: fetchedItem.metadata || {},
            created_at: fetchedItem.created_at,
            updated_at: fetchedItem.updated_at || fetchedItem.created_at,
            isLegacy: false
          }
        } catch (error: any) {
          const errorInfo = categorizeError(error)
          const errorMsg = `Erreur lors du chargement: ${errorInfo.message}`
          setVaultError({
            type: 'load',
            category: errorInfo.category,
            message: errorMsg,
            retryable: errorInfo.retryable
          })
          setItemErrors(prev => {
            const newMap = new Map(prev)
            newMap.set(itemId, { message: errorMsg, retryable: errorInfo.retryable })
            return newMap
          })
          setLoadingVaultItemId(null)
          return
        }
      }

      // Validate item exists
      if (!vaultItem) {
        const errorMsg = 'Élément introuvable dans le Vault'
        setVaultError({
          type: 'load',
          category: 'database',
          message: errorMsg,
          retryable: false
        })
        setItemErrors(prev => {
          const newMap = new Map(prev)
          newMap.set(itemId, { message: errorMsg, retryable: false })
          return newMap
        })
        setLoadingVaultItemId(null)
        return
      }

      // Handle legacy conversations (from conversations table) with retry
      if (vaultItem.isLegacy && itemType === 'chat') {
        try {
          await loadSavedChat(itemId)
          setCurrentVaultItemId(itemId)
          setVaultError(null) // Clear errors on success
          setLoadingVaultItemId(null)
          return
        } catch (error: any) {
          const errorInfo = categorizeError(error)
          const errorMsg = `Erreur lors du chargement de la conversation: ${errorInfo.message}`
          setVaultError({
            type: 'load',
            category: errorInfo.category,
            message: errorMsg,
            retryable: errorInfo.retryable
          })
          setItemErrors(prev => {
            const newMap = new Map(prev)
            newMap.set(itemId, { message: errorMsg, retryable: errorInfo.retryable })
            return newMap
          })
          setLoadingVaultItemId(null)
          return
        }
      }

      // Validate content structure based on type
      if (!vaultItem.content) {
        const errorMsg = 'Le contenu de cet élément est vide ou corrompu'
        setVaultError({
          type: 'load',
          category: 'unknown',
          message: errorMsg,
          retryable: false
        })
        setItemErrors(prev => {
          const newMap = new Map(prev)
          newMap.set(itemId, { message: errorMsg, retryable: false })
          return newMap
        })
        setLoadingVaultItemId(null)
        return
      }

      if (itemType === 'chat') {
        // Load chat messages from vault_items with validation
        try {
          const messages = vaultItem.content?.messages
          
          if (!Array.isArray(messages)) {
            throw new Error('Format de messages invalide')
          }
          
          const loadedMessages: ChatMessage[] = messages.map((msg: any, index: number) => {
            try {
              // Validate message structure
              if (!msg || typeof msg !== 'object') {
                console.warn(`Message ${index} is invalid, skipping`)
                return null
              }
              
              return {
                id: msg.id || `${itemId}-${index}-${Date.now()}`,
                text: msg.text || '',
                sender: (msg.sender === 'user' || msg.sender === 'assistant') ? msg.sender : 'user',
                timestamp: msg.timestamp ? new Date(msg.timestamp) : new Date(),
                isProposal: msg.isProposal || false,
                isLoading: msg.isLoading || false,
                isWebSearch: msg.isWebSearch || false,
                sources: Array.isArray(msg.sources) ? msg.sources : null,
                proposalData: msg.proposalData || null,
                aiResult: msg.aiResult || null
              }
            } catch (msgError) {
              console.warn(`Error parsing message ${index}:`, msgError)
              return null
            }
          }).filter((msg): msg is ChatMessage => msg !== null) // Remove null entries
          
          if (loadedMessages.length === 0 && messages.length > 0) {
            throw new Error('Aucun message valide trouvé dans le contenu')
          }
          
          setChatMessages(loadedMessages)
          setCurrentChatId(itemId)
          setCurrentVaultItemId(itemId)
          setInputValue('')
          setIsVaultOpen(false)
          setSelectedSidebarItem('new-chat')
          setVaultError(null) // Clear errors on success
          setItemErrors(prev => {
            const newMap = new Map(prev)
            newMap.delete(itemId)
            return newMap
          })
          setLoadingVaultItemId(null)
        } catch (error: any) {
          const errorMsg = `Erreur lors du chargement des messages: ${error?.message || 'Contenu corrompu'}`
          setVaultError({
            type: 'load',
            category: 'unknown',
            message: errorMsg,
            retryable: false
          })
          setItemErrors(prev => {
            const newMap = new Map(prev)
            newMap.set(itemId, { message: errorMsg, retryable: false })
            return newMap
          })
          setLoadingVaultItemId(null)
        }
      } else if (itemType === 'document') {
        // Load document with validation
        try {
          const htmlContent = vaultItem.content?.html
          
          if (typeof htmlContent !== 'string') {
            throw new Error('Format de document invalide')
          }
          
          // Validate HTML content is not too large (sanity check)
          if (htmlContent.length > 50 * 1024 * 1024) { // 50MB limit
            throw new Error('Le document est trop volumineux pour être chargé')
          }
          
          // Clear sessionStorage for editor when loading from Vault
          sessionStorage.removeItem(SESSION_STORAGE_EDITOR_KEY)
          loadedFromVault.current = { type: 'document', timestamp: Date.now() }
          
          // Update the currently active document with the vault item content and title
          // This replaces "Nouveau document" with the loaded document
          setDocuments(prev => prev.map((doc) => 
            doc.id === activeDocumentId
              ? { ...doc, title: vaultItem!.title, content: htmlContent }
              : doc
          ))
          
          // Open editor if not open
          if (!isEditorOpen) {
            setIsEditorOpen(true)
            setActivePanel('editor')
          }
          
          setCurrentVaultItemId(itemId)
          setIsVaultOpen(false)
          setVaultError(null) // Clear errors on success
          setItemErrors(prev => {
            const newMap = new Map(prev)
            newMap.delete(itemId)
            return newMap
          })
          setLoadingVaultItemId(null)
          
          // Update editor content after a brief delay to ensure editor is ready
          setTimeout(() => {
            if (editor) {
              try {
                editor.commands.setContent(htmlContent)
              } catch (editorError) {
                console.error('Error setting editor content:', editorError)
                const errorMsg = 'Erreur lors de l\'affichage du document dans l\'éditeur'
                setVaultError({
                  type: 'load',
                  category: 'unknown',
                  message: errorMsg,
                  retryable: false
                })
                setItemErrors(prev => {
                  const newMap = new Map(prev)
                  newMap.set(itemId, { message: errorMsg, retryable: false })
                  return newMap
                })
              }
            }
          }, 100)
        } catch (error: any) {
          const errorMsg = `Erreur lors du chargement du document: ${error?.message || 'Contenu corrompu'}`
          setVaultError({
            type: 'load',
            category: 'unknown',
            message: errorMsg,
            retryable: false
          })
          setItemErrors(prev => {
            const newMap = new Map(prev)
            newMap.set(itemId, { message: errorMsg, retryable: false })
            return newMap
          })
          setLoadingVaultItemId(null)
        }
      } else if (itemType === 'spreadsheet') {
        // Load spreadsheet with validation
        try {
          const spreadsheetData = vaultItem.content?.data
          
          if (!Array.isArray(spreadsheetData)) {
            throw new Error('Format de tableur invalide')
          }
          
          // Validate spreadsheet structure
          if (spreadsheetData.length > 0 && !Array.isArray(spreadsheetData[0])) {
            throw new Error('Structure de données du tableur invalide')
          }
          
          // Clear sessionStorage for spreadsheet when loading from Vault
          sessionStorage.removeItem(SESSION_STORAGE_SPREADSHEET_KEY)
          loadedFromVault.current = { type: 'spreadsheet', timestamp: Date.now() }
          
          // Find or create spreadsheet
          const existingSpreadsheetIndex = spreadsheets.findIndex(s => s.id === itemId)
          if (existingSpreadsheetIndex >= 0) {
            // Update existing spreadsheet
            setSpreadsheets(prev => prev.map((s, idx) => 
              idx === existingSpreadsheetIndex 
                ? { ...s, title: vaultItem!.title, data: spreadsheetData }
                : s
            ))
            setActiveSpreadsheetId(itemId)
          } else {
            // Create new spreadsheet
            const newSpreadsheet = {
              id: itemId,
              title: vaultItem.title,
              data: spreadsheetData
            }
            setSpreadsheets(prev => [...prev, newSpreadsheet])
            setActiveSpreadsheetId(itemId)
          }
          
          // Update currentTableData immediately when loading from Vault
          const newTableData = convertSpreadsheetToTableData(spreadsheetData)
          setCurrentTableData(newTableData)
          
          // Open Editeur with Tableau menu if not open
          if (!isEditorOpen) {
            setIsEditorOpen(true)
            setActivePanel('editor')
            setSelectedSidebarItem('editeur-avocat')
            setActiveMenuButton('Édition')
          } else if (activeMenuButton !== 'Édition') {
            setActiveMenuButton('Édition')
          }
          
          setCurrentVaultItemId(itemId)
          setIsVaultOpen(false)
          setVaultError(null) // Clear errors on success
          setItemErrors(prev => {
            const newMap = new Map(prev)
            newMap.delete(itemId)
            return newMap
          })
          setLoadingVaultItemId(null)
        } catch (error: any) {
          const errorMsg = `Erreur lors du chargement du tableur: ${error?.message || 'Contenu corrompu'}`
          setVaultError({
            type: 'load',
            category: 'unknown',
            message: errorMsg,
            retryable: false
          })
          setItemErrors(prev => {
            const newMap = new Map(prev)
            newMap.set(itemId, { message: errorMsg, retryable: false })
            return newMap
          })
          setLoadingVaultItemId(null)
        }
      }
    } catch (error: any) {
      const errorInfo = categorizeError(error)
      const errorMsg = `Erreur lors du chargement: ${errorInfo.message}`
      setVaultError({
        type: 'load',
        category: errorInfo.category,
        message: errorMsg,
        retryable: errorInfo.retryable
      })
      setItemErrors(prev => {
        const newMap = new Map(prev)
        newMap.set(itemId, { message: errorMsg, retryable: errorInfo.retryable })
        return newMap
      })
      console.error('Error loading vault item:', error)
      setLoadingVaultItemId(null)
    }
  }

  // Load saved chat from Supabase
  const loadSavedChat = async (conversationId: string) => {
    try {
      // Get current user session with retry and timeout
      const sessionResult = await retryWithBackoff(async () => {
        return await withTimeout(
          supabase.auth.getSession(),
          10000 // 10 second timeout for session check
        )
      })
      
      const { data: { session } } = sessionResult
      if (!session?.user) {
        throw new Error('Vous devez être connecté pour charger une conversation')
      }

      // First, verify the conversation exists with retry and timeout
      const conversationResult = await retryWithBackoff(async () => {
        const result = await withTimeout(
          supabase
            .from('conversations')
            .select('id, title')
            .eq('id', conversationId)
            .eq('user_id', session.user.id)
            .single(),
          TIMEOUT_FETCH,
          'Vérification de la conversation'
        )
        
        if (result.error) {
          // If conversation not found (PGRST116), that's a specific error
          if (result.error.code === 'PGRST116') {
            throw {
              ...result.error,
              message: 'Conversation introuvable. Elle a peut-être été supprimée.',
              isNotFound: true
            }
          }
          throw result.error
        }
        
        return result
      })

      const { data: conversation, error: conversationError } = conversationResult

      if (conversationError) {
        throw conversationError
      }

      if (!conversation) {
        throw new Error('Conversation introuvable. Elle a peut-être été supprimée.')
      }

      // Query Supabase messages table with retry and timeout
      const messagesResult = await retryWithBackoff(async () => {
        const result = await withTimeout(
          supabase
            .from('messages')
            .select('*')
            .eq('conversation_id', conversationId)
            .eq('user_id', session.user.id)
            .order('created_at', { ascending: true }),
          TIMEOUT_FETCH,
          'Chargement des messages'
        )
        
        if (result.error) {
          throw result.error
        }
        
        return result
      })

      const { data: messages, error: messagesError } = messagesResult

      if (messagesError) {
        // Handle specific error cases
        if (messagesError.code === 'PGRST116' || messagesError.message?.includes('not found')) {
          throw {
            ...messagesError,
            message: 'Aucun message trouvé pour cette conversation.',
            isNotFound: true
          }
        }
        throw messagesError
      }

      // Validate messages array
      if (!Array.isArray(messages)) {
        throw new Error('Format de messages invalide')
      }

      // Handle empty conversations gracefully
      if (messages.length === 0) {
        console.warn(`Conversation ${conversationId} exists but has no messages`)
        // Still load it, just with empty messages
      }

      // Convert Supabase message format to ChatMessage[] with validation
      const loadedMessages: ChatMessage[] = (messages || []).map((msg: any, index: number) => {
        try {
          // Validate message structure
          if (!msg || typeof msg !== 'object') {
            console.warn(`Message ${index} is invalid (not an object), skipping`)
            return null
          }
          
          // Validate required fields
          if (msg.content === undefined && msg.text === undefined) {
            console.warn(`Message ${index} is missing content, skipping`)
            return null
          }
          
          // Validate sender/role
          const sender = msg.role === 'user' ? 'user' : (msg.role === 'assistant' ? 'assistant' : 'user')
          
          // Validate timestamp
          let timestamp: Date
          try {
            timestamp = msg.created_at ? new Date(msg.created_at) : new Date()
            // Check if date is valid
            if (isNaN(timestamp.getTime())) {
              console.warn(`Message ${index} has invalid timestamp, using current date`)
              timestamp = new Date()
            }
          } catch (dateError) {
            console.warn(`Message ${index} has invalid timestamp format, using current date`)
            timestamp = new Date()
          }
          
          const chatMessage: ChatMessage = {
            id: msg.id || msg.message_id || `${conversationId}-${index}-${Date.now()}`,
            text: msg.content || msg.text || '',
            sender: sender,
            timestamp: timestamp,
            isProposal: Boolean(msg.is_proposal),
            isLoading: Boolean(msg.is_loading),
            isWebSearch: Boolean(msg.is_web_search)
          }

          // Map optional fields if present with validation
          if (msg.sources) {
            if (Array.isArray(msg.sources)) {
              // Validate each source object
              chatMessage.sources = msg.sources.filter((source: any) => 
                source && typeof source === 'object' && source.title && source.link
              )
            } else {
              console.warn(`Message ${index} has invalid sources format, skipping`)
            }
          }
          
          if (msg.proposal_data) {
            if (typeof msg.proposal_data === 'object' && msg.proposal_data !== null) {
              chatMessage.proposalData = msg.proposal_data
            } else {
              console.warn(`Message ${index} has invalid proposal_data format, skipping`)
            }
          }
          
          if (msg.ai_result) {
            if (typeof msg.ai_result === 'object' && msg.ai_result !== null) {
              chatMessage.aiResult = msg.ai_result
            } else {
              console.warn(`Message ${index} has invalid ai_result format, skipping`)
            }
          }

          return chatMessage
        } catch (msgError: any) {
          console.warn(`Error parsing message ${index}:`, msgError?.message || msgError)
          return null
        }
      }).filter((msg): msg is ChatMessage => msg !== null) // Remove null entries

      // Validate we have at least some valid messages (only if original array had messages)
      if (loadedMessages.length === 0 && messages.length > 0) {
        throw new Error('Aucun message valide trouvé dans la conversation. Les messages peuvent être corrompus.')
      }
      
      // Log warning if some messages were skipped
      if (loadedMessages.length < messages.length) {
        console.warn(`Loaded ${loadedMessages.length} valid messages out of ${messages.length} total (${messages.length - loadedMessages.length} skipped due to errors)`)
      }

      // Update chatMessages state with loaded messages
      setChatMessages(loadedMessages)
      
      // Set currentChatId to the loaded conversation ID
      setCurrentChatId(conversationId)
      setCurrentVaultItemId(conversationId)
      
      // Clear chat input
      setInputValue('')
      
      // Close Vault panel (optional)
      setIsVaultOpen(false)
      setSelectedSidebarItem('new-chat')
      hasLoadedFromStorage.current = true // Mark as loaded to prevent sessionStorage from overwriting
      setIsLoadingSession(false) // Ensure loading state is cleared
    } catch (error: any) {
      // Handle errors gracefully
      const errorInfo = categorizeError(error)
      console.error('Error loading saved chat:', {
        conversationId,
        error: error?.message || error,
        code: error?.code,
        details: error?.details,
        isNotFound: error?.isNotFound
      })
      
      // Check if it's a timeout error
      const isTimeout = error?.message?.includes('timeout') || error?.message?.includes('Timeout') || error?.isTimeout || errorInfo.category === 'timeout'
      
      // Provide more specific error messages
      let errorMessage = errorInfo.message
      
      if (error?.isNotFound) {
        errorMessage = error.message || 'Conversation introuvable. Elle a peut-être été supprimée.'
      } else if (error?.code === 'PGRST116') {
        errorMessage = 'Conversation introuvable. Elle a peut-être été supprimée.'
      } else if (isTimeout) {
        errorMessage = 'Chargement interrompu. Cela est généralement dû à une instabilité du réseau Internet. Veuillez vérifier votre connexion et réessayer.'
        // Timeout errors are logged but not shown to user to avoid annoying notifications
        // setSaveLoadError({
        //   message: errorMessage,
        //   retryable: true,
        //   onRetry: () => loadSavedChat(conversationId)
        // })
      } else if (error?.message?.includes('network') || error?.message?.includes('fetch')) {
        errorMessage = 'Erreur de connexion. Vérifiez votre connexion internet et réessayez.'
      } else if (error?.message?.includes('JWT') || error?.message?.includes('session')) {
        errorMessage = 'Votre session a expiré. Veuillez vous reconnecter.'
      }
      
      // Re-throw to be handled by caller
      throw {
        ...error,
        message: errorMessage,
        category: errorInfo.category,
        retryable: errorInfo.retryable,
        isNotFound: error?.isNotFound || false
      }
    }
  }

  // Delete conversation from Supabase
  const handleDeleteConversation = async (conversationId: string) => {
    // Prevent multiple simultaneous deletions
    if (isDeletingConversation) {
      console.warn('A deletion is already in progress')
      return
    }

    // Prevent deleting the same conversation twice
    if (deletingConversationId === conversationId) {
      console.warn('This conversation is already being deleted')
      return
    }

    setIsDeletingConversation(true)
    setDeletingConversationId(conversationId)

    try {
      // Get current user session
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.user) {
        alert('Vous devez être connecté pour supprimer une conversation')
        setIsDeletingConversation(false)
        setDeletingConversationId(null)
        return
      }

      // Delete all messages for this conversation first (cascade)
      const { error: deleteMessagesError } = await supabase
        .from('messages')
        .delete()
        .eq('conversation_id', conversationId)
        .eq('user_id', session.user.id)

      if (deleteMessagesError) {
        console.error('Error deleting messages:', {
          message: deleteMessagesError.message,
          details: deleteMessagesError.details,
          hint: deleteMessagesError.hint,
          code: deleteMessagesError.code
        })
        throw deleteMessagesError
      }

      // Delete the conversation from conversations table
      const { error: deleteConversationError } = await supabase
        .from('conversations')
        .delete()
        .eq('id', conversationId)
        .eq('user_id', session.user.id)

      if (deleteConversationError) {
        console.error('Error deleting conversation:', {
          message: deleteConversationError.message,
          details: deleteConversationError.details,
          hint: deleteConversationError.hint,
          code: deleteConversationError.code
        })
        throw deleteConversationError
      }

      // Only update local state if deletion succeeds
      // Update local state: remove conversation from savedConversations array
      setSavedConversations(prev => prev.filter(conv => conv.id !== conversationId))

      // Also remove from vaultItems state if this conversation is in the vault
      setVaultItems(prev => prev.filter(vItem => !(vItem.id === conversationId && vItem.isLegacy)))

      // If deleted conversation is currently active, clear chat interface
      if (currentChatId === conversationId) {
        setCurrentChatId(null)
        setChatMessages([])
        setInputValue('')
        setShowPromptSuggestions(true) // Show suggestions again when conversation is cleared
      }

      // Also clear currentVaultItemId if it matches
      if (currentVaultItemId === conversationId) {
        setCurrentVaultItemId(null)
      }

      // Success - no modal needed, state update is sufficient
    } catch (error: any) {
      // Handle errors gracefully - don't update local state on error
      console.error('Error deleting conversation:', error)
      const errorMessage = error?.message || error?.details || 'Erreur inconnue'
      console.error('Error details:', errorMessage)
      alert(`Erreur lors de la suppression: ${errorMessage}. Veuillez vérifier la console pour plus de détails.`)
      // Don't update local state if deletion failed
    } finally {
      // Always reset loading states
      setIsDeletingConversation(false)
      setDeletingConversationId(null)
    }
  }

  // Delete all history (conversations, messages, and vault items)
  const handleDeleteAllHistory = async () => {
    try {
      // Get current user session
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.user) {
        alert('Vous devez être connecté pour supprimer l\'historique')
        return
      }

      // Delete all messages for the user
      const { error: deleteMessagesError } = await supabase
        .from('messages')
        .delete()
        .eq('user_id', session.user.id)

      if (deleteMessagesError) {
        console.error('Error deleting messages:', deleteMessagesError)
        throw deleteMessagesError
      }

      // Delete all conversations for the user
      const { error: deleteConversationsError } = await supabase
        .from('conversations')
        .delete()
        .eq('user_id', session.user.id)

      if (deleteConversationsError) {
        console.error('Error deleting conversations:', deleteConversationsError)
        throw deleteConversationsError
      }

      // Delete all vault items for the user
      const { error: deleteVaultItemsError } = await supabase
        .from('vault_items')
        .delete()
        .eq('user_id', session.user.id)

      if (deleteVaultItemsError) {
        console.error('Error deleting vault items:', deleteVaultItemsError)
        throw deleteVaultItemsError
      }

      // Clear all local state
      setSavedConversations([])
      setVaultItems([])
      setChatMessages([])
      setCurrentChatId(null)
      setCurrentVaultItemId(null)
      setInputValue('')
      setShowPromptSuggestions(true) // Show suggestions again when conversation is cleared

      // Clear sessionStorage
      sessionStorage.removeItem(SESSION_STORAGE_KEY)
      sessionStorage.removeItem(SESSION_STORAGE_EDITOR_KEY)
      sessionStorage.removeItem(SESSION_STORAGE_SPREADSHEET_KEY)

      // Show success message
      openSuccessModal('Tout l\'historique a été supprimé avec succès')
    } catch (error: any) {
      console.error('Error deleting all history:', error)
      const errorMessage = error?.message || error?.details || 'Erreur inconnue'
      alert(`Erreur lors de la suppression de l'historique: ${errorMessage}`)
    }
  }

  // Rename conversation in Supabase
  const handleRenameConversation = async (conversationId: string, newTitle: string) => {
    try {
      // Get current user session
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.user) {
        alert('Vous devez être connecté pour renommer une conversation')
        return
      }

      // Validate new title
      if (!newTitle || !newTitle.trim()) {
        alert('Le titre ne peut pas être vide')
        return
      }

      // Update conversation title in Supabase
      const { error: updateError } = await supabase
        .from('conversations')
        .update({ 
          title: newTitle.trim()
        })
        .eq('id', conversationId)
        .eq('user_id', session.user.id)

      if (updateError) {
        console.error('Error renaming conversation:', {
          message: updateError.message,
          details: updateError.details,
          hint: updateError.hint,
          code: updateError.code
        })
        throw updateError
      }

      // Update local state: update conversation title in savedConversations array
      setSavedConversations(prev => 
        prev.map(conv => 
          conv.id === conversationId 
            ? { ...conv, title: newTitle.trim() }
            : conv
        )
      )

      // Also update vaultItems state if this conversation is in the vault
      setVaultItems(prev =>
        prev.map(vItem =>
          vItem.id === conversationId && vItem.isLegacy
            ? { ...vItem, title: newTitle.trim() }
            : vItem
        )
      )

      // Success - no modal needed, state update is sufficient
    } catch (error: any) {
      // Handle errors gracefully
      console.error('Error renaming conversation:', error)
      const errorMessage = error?.message || error?.details || 'Erreur inconnue'
      console.error('Error details:', errorMessage)
      alert(`Erreur lors du renommage: ${errorMessage}. Veuillez vérifier la console pour plus de détails.`)
    }
  }

  // Generic function to save any content type to Vault
  const saveToVault = async (
    type: 'chat' | 'document' | 'spreadsheet',
    content: any,
    title: string,
    metadata?: any
  ): Promise<string | null> => {
    setVaultError(null) // Clear previous errors
    
    try {
      // Get user session with retry and timeout
      const sessionResult = await retryWithBackoff(async () => {
        return await withTimeout(
          supabase.auth.getSession(),
          10000 // 10 second timeout for session check
        )
      })
      
      const { data: { session } } = sessionResult
      if (!session?.user) {
        setVaultError({
          type: 'save',
          category: 'auth',
          message: 'Vous devez être connecté pour sauvegarder',
          retryable: false
        })
        return null
      }

      // Validate content based on type
      if (type === 'chat') {
        if (!Array.isArray(content) || content.length === 0) {
          setVaultError({
            type: 'save',
            category: 'unknown',
            message: 'Le contenu de la conversation ne peut pas être vide',
            retryable: false
          })
          return null
        }
      } else if (type === 'document') {
        if (!content || typeof content !== 'string') {
          setVaultError({
            type: 'save',
            category: 'unknown',
            message: 'Le contenu du document n\'est pas valide',
            retryable: false
          })
          return null
        }
        // Check content size (Supabase JSONB limit is ~1GB, but we'll set a reasonable limit)
        const contentSize = new Blob([content]).size
        const maxSize = 10 * 1024 * 1024 // 10MB limit
        if (contentSize > maxSize) {
          setVaultError({
            type: 'save',
            category: 'unknown',
            message: `Le document est trop volumineux (${(contentSize / 1024 / 1024).toFixed(2)} MB). Maximum: 10 MB.`,
            retryable: false
          })
          return null
        }
      } else if (type === 'spreadsheet') {
        if (!Array.isArray(content) || content.length === 0) {
          setVaultError({
            type: 'save',
            category: 'unknown',
            message: 'Le contenu du tableur ne peut pas être vide',
            retryable: false
          })
          return null
        }
        // Check spreadsheet size
        const contentSize = new Blob([JSON.stringify(content)]).size
        const maxSize = 10 * 1024 * 1024 // 10MB limit
        if (contentSize > maxSize) {
          setVaultError({
            type: 'save',
            category: 'unknown',
            message: `Le tableur est trop volumineux (${(contentSize / 1024 / 1024).toFixed(2)} MB). Maximum: 10 MB.`,
            retryable: false
          })
          return null
        }
      }

      // Validate title
      if (!title || !title.trim()) {
        setVaultError({
          type: 'save',
          category: 'unknown',
          message: 'Le titre ne peut pas être vide',
          retryable: false
        })
        return null
      }
      
      // Validate title length
      if (title.trim().length > 255) {
        setVaultError({
          type: 'save',
          category: 'unknown',
          message: 'Le titre est trop long (maximum 255 caractères)',
          retryable: false
        })
        return null
      }

      // Prepare content JSONB structure based on type
      let contentJsonb: any
      let metadataJsonb: any = metadata || {}

      if (type === 'chat') {
        // Content: array of chat messages
        contentJsonb = {
          messages: content.map((msg: any) => ({
            id: msg.id,
            text: msg.text,
            sender: msg.sender,
            timestamp: msg.timestamp?.toISOString() || new Date().toISOString(),
            isProposal: msg.isProposal || false,
            isLoading: msg.isLoading || false,
            isWebSearch: msg.isWebSearch || false,
            sources: msg.sources || null,
            proposalData: msg.proposalData || null,
            aiResult: msg.aiResult || null
          }))
        }
        // Metadata: message count, etc.
        metadataJsonb = {
          messageCount: content.length,
          ...metadataJsonb
        }
      } else if (type === 'document') {
        // Content: HTML string
        contentJsonb = {
          html: content
        }
        // Metadata: character count, word count, etc.
        const textContent = content.replace(/<[^>]*>/g, '') // Strip HTML tags for counting
        metadataJsonb = {
          characterCount: textContent.length,
          wordCount: textContent.split(/\s+/).filter((word: string) => word.length > 0).length,
          ...metadataJsonb
        }
      } else if (type === 'spreadsheet') {
        // Content: 2D array of spreadsheet data
        contentJsonb = {
          data: content
        }
        // Metadata: row count, column count, etc.
        metadataJsonb = {
          rowCount: content.length,
          columnCount: content[0]?.length || 0,
          ...metadataJsonb
        }
      }

      // Check if currentVaultItemId exists and matches type for UPDATE
      if (currentVaultItemId) {
        // Check if the existing item matches the type
        const existingItem = vaultItems.find(item => item.id === currentVaultItemId)
        if (existingItem && existingItem.type === type) {
          // UPDATE existing item with retry and timeout
          try {
            const updateResult = await retryWithBackoff(async () => {
              const result = await withTimeout(
                supabase
                  .from('vault_items')
                  .update({
                    title: title.trim(),
                    content: contentJsonb,
                    metadata: metadataJsonb,
                    updated_at: new Date().toISOString()
                  })
                  .eq('id', currentVaultItemId)
                  .eq('user_id', session.user.id)
                  .eq('item_type', type),
                TIMEOUT_SAVE,
                'Mise à jour de l\'élément'
              )
              
              if (result.error) {
                throw result.error
              }
              
              return result
            })

            // Update local state
            setVaultItems(prev =>
              prev.map(item =>
                item.id === currentVaultItemId
                  ? { ...item, title: title.trim(), content: contentJsonb, metadata: metadataJsonb, updated_at: new Date().toISOString() }
                  : item
              )
            )

            setVaultError(null) // Clear errors on success
            return currentVaultItemId
          } catch (error: any) {
            const errorInfo = categorizeError(error)
            
            // Check if it's a timeout error
            const isTimeout = error?.message?.includes('timeout') || error?.isTimeout || errorInfo.category === 'timeout'
            
            // Handle duplicate key errors specifically
            if (error?.code === '23505' || error?.message?.includes('duplicate')) {
              setVaultError({
                type: 'save',
                category: 'database',
                message: 'Un élément avec ce titre existe déjà. Veuillez utiliser un titre différent.',
                retryable: false
              })
            } else if (isTimeout) {
              // Use timeout message directly without prefix
              setVaultError({
                type: 'save',
                category: 'timeout',
                message: errorInfo.message, // Already contains the correct timeout message
                retryable: true
              })
            } else {
              setVaultError({
                type: 'save',
                category: errorInfo.category,
                message: `Erreur lors de la mise à jour: ${errorInfo.message}`,
                retryable: errorInfo.retryable
              })
            }
            
            console.error('Error updating vault item:', {
              message: error?.message,
              details: error?.details,
              hint: error?.hint,
              code: error?.code
            })
            return null
          }
        }
      }

      // CREATE new item with retry and timeout
      try {
        const insertResult = await retryWithBackoff(async () => {
          const result = await withTimeout(
            supabase
              .from('vault_items')
              .insert({
                user_id: session.user.id,
                item_type: type,
                title: title.trim(),
                content: contentJsonb,
                metadata: metadataJsonb,
                created_at: new Date().toISOString()
              })
              .select()
              .single(),
            60000 // 60 second timeout for insert
          )
          
          if (result.error) {
            throw result.error
          }
          
          return result
        })

        const { data: vaultItem } = insertResult

        // Update local state
        setVaultItems(prev => [
          {
            id: vaultItem.id,
            type: vaultItem.item_type as 'chat' | 'document' | 'spreadsheet',
            title: vaultItem.title,
            content: vaultItem.content,
            metadata: vaultItem.metadata || {},
            created_at: vaultItem.created_at,
            updated_at: vaultItem.updated_at
          },
          ...prev
        ])

        // Set as current vault item
        setCurrentVaultItemId(vaultItem.id)
        setVaultError(null) // Clear errors on success

        return vaultItem.id
      } catch (error: any) {
        const errorInfo = categorizeError(error)
        
        // Check if it's a timeout error
        const isTimeout = error?.message?.includes('timeout') || error?.isTimeout || errorInfo.category === 'timeout'
        
        // Handle duplicate key errors specifically
        if (error?.code === '23505' || error?.message?.includes('duplicate') || error?.message?.includes('unique')) {
          setVaultError({
            type: 'save',
            category: 'database',
            message: 'Un élément avec ce titre existe déjà. Veuillez utiliser un titre différent.',
            retryable: false
          })
        } else if (error?.message?.includes('too large') || error?.message?.includes('size')) {
          setVaultError({
            type: 'save',
            category: 'unknown',
            message: 'Le contenu est trop volumineux. Veuillez réduire la taille du document.',
            retryable: false
          })
        } else if (isTimeout) {
          // Use timeout message directly without prefix
          setVaultError({
            type: 'save',
            category: 'timeout',
            message: errorInfo.message, // Already contains the correct timeout message
            retryable: true
          })
        } else {
          setVaultError({
            type: 'save',
            category: errorInfo.category,
            message: `Erreur lors de la sauvegarde: ${errorInfo.message}`,
            retryable: errorInfo.retryable
          })
        }
        
        console.error('Error creating vault item:', {
          message: error?.message,
          details: error?.details,
          hint: error?.hint,
          code: error?.code
        })
        return null
      }
    } catch (error: any) {
      // Handle errors gracefully
      const errorInfo = categorizeError(error)
      setVaultError({
        type: 'save',
        category: errorInfo.category,
        message: errorInfo.message,
        retryable: errorInfo.retryable
      })
      console.error('Error saving to vault:', error)
      return null
    }
  }

  // Save conversation to Supabase
  const handleSaveConversation = async () => {
    // Validate that chatMessages.length > 0
    if (chatMessages.length === 0) return
    
    setIsSavingConversation(true)
    setSaveLoadError(null) // Clear any previous errors
    
    try {
      // Wrap entire save operation with retry logic
      await retryWithBackoff(async () => {
        // Check if user is authenticated (get current session) with timeout
        const sessionResult = await withTimeout(
          supabase.auth.getSession(),
          TIMEOUT_SAVE,
          'Vérification de la session'
        )
        
        const { data: { session } } = sessionResult
        if (!session?.user) {
          alert('Vous devez être connecté pour sauvegarder')
          setIsSavingConversation(false)
          return
        }
        
        // Generate title from first user message (first 50 chars) or "Nouvelle conversation"
        const firstUserMessage = chatMessages.find(msg => msg.sender === 'user')
        const title = firstUserMessage?.text?.substring(0, 50) || 'Nouvelle conversation'
        
        // Check if currentChatId is not null
        if (currentChatId) {
          // UPDATE existing conversation
          // Generate new title from first user message and update conversation record
          const updateResult = await withTimeout(
            supabase
              .from('conversations')
              .update({ 
                title // Always update with new title generated from first user message
                // Note: updated_at column doesn't exist in the table, so we only update title
              })
              .eq('id', currentChatId)
              .eq('user_id', session.user.id),
            TIMEOUT_SAVE,
            'Mise à jour de la conversation'
          )
          
          if (updateResult.error) throw updateResult.error
          
          // Delete all existing messages for this conversation
          const deleteResult = await withTimeout(
            supabase
              .from('messages')
              .delete()
              .eq('conversation_id', currentChatId)
              .eq('user_id', session.user.id),
            TIMEOUT_SAVE,
            'Suppression des messages existants'
          )
          
          if (deleteResult.error) throw deleteResult.error
          
          // Insert all current messages (complete replacement)
          const messagesToInsert = chatMessages.map(msg => {
            const messageData: any = {
              conversation_id: currentChatId,
              user_id: session.user.id,
              role: msg.sender === 'user' ? 'user' : 'assistant',
              content: msg.text,
              created_at: msg.timestamp.toISOString()
            }
            
            // Optional fields - uncomment/add only if these columns exist in your messages table
            // if (msg.isProposal !== undefined) messageData.is_proposal = Boolean(msg.isProposal)
            // if (msg.isLoading !== undefined) messageData.is_loading = Boolean(msg.isLoading)
            // if (msg.isWebSearch !== undefined) messageData.is_web_search = Boolean(msg.isWebSearch)
            // if (msg.sources) messageData.sources = msg.sources
            // if (msg.proposalData) messageData.proposal_data = msg.proposalData
            // if (msg.aiResult) messageData.ai_result = msg.aiResult
            
            return messageData
          })
          
          const insertResult = await withTimeout(
            supabase
              .from('messages')
              .insert(messagesToInsert),
            TIMEOUT_SAVE,
            'Insertion des messages'
          )
          
          if (insertResult.error) {
            console.error('Messages insert error:', {
              message: insertResult.error.message,
              details: insertResult.error.details,
              hint: insertResult.error.hint,
              code: insertResult.error.code
            })
            throw insertResult.error
          }
          
          // Show success toast: "Conversation mise à jour avec succès!"
          setVaultError(null) // Clear any previous errors
          setSaveLoadError(null) // Clear any previous toast errors
          setVaultSuccess({
            type: 'save',
            message: 'Conversation mise à jour avec succès dans le Vault'
          })
          // Auto-dismiss success message after 3 seconds
          setTimeout(() => {
            setVaultSuccess(null)
          }, 3000)
        } else {
          // CREATE new conversation
          // Create conversation record in Supabase conversations table
          const conversationResult = await withTimeout(
            supabase
              .from('conversations')
              .insert({ 
                user_id: session.user.id, 
                title,
                created_at: new Date().toISOString()
              })
              .select()
              .single(),
            TIMEOUT_SAVE,
            'Création de la conversation'
          )
          
          if (conversationResult.error) throw conversationResult.error
          
          const { data: conversation } = conversationResult
          
          // Insert all messages
          const messagesToInsert = chatMessages.map(msg => {
            const messageData: any = {
              conversation_id: conversation.id,
              user_id: session.user.id,
              role: msg.sender === 'user' ? 'user' : 'assistant',
              content: msg.text,
              created_at: msg.timestamp.toISOString()
            }
            
            // Optional fields - uncomment/add only if these columns exist in your messages table
            // if (msg.isProposal !== undefined) messageData.is_proposal = Boolean(msg.isProposal)
            // if (msg.isLoading !== undefined) messageData.is_loading = Boolean(msg.isLoading)
            // if (msg.isWebSearch !== undefined) messageData.is_web_search = Boolean(msg.isWebSearch)
            // if (msg.sources) messageData.sources = msg.sources
            // if (msg.proposalData) messageData.proposal_data = msg.proposalData
            // if (msg.aiResult) messageData.ai_result = msg.aiResult
            
            return messageData
          })
          
          const insertResult = await withTimeout(
            supabase
              .from('messages')
              .insert(messagesToInsert),
            TIMEOUT_SAVE,
            'Insertion des messages'
          )
          
          if (insertResult.error) {
            console.error('Messages insert error:', {
              message: insertResult.error.message,
              details: insertResult.error.details,
              hint: insertResult.error.hint,
              code: insertResult.error.code
            })
            throw insertResult.error
          }
          
          // Set currentChatId to newly created conversation ID
          setCurrentChatId(conversation.id)
          
          // Show success toast: "Conversation sauvegardée avec succès!"
          setVaultError(null) // Clear any previous errors
          setSaveLoadError(null) // Clear any previous toast errors
          setVaultSuccess({
            type: 'save',
            message: 'Conversation sauvegardée avec succès dans le Vault'
          })
          // Auto-dismiss success message after 3 seconds
          setTimeout(() => {
            setVaultSuccess(null)
          }, 3000)
        }
        
        // Refresh conversations list after save (with timeout)
        await withTimeout(
          fetchSavedConversations(),
          TIMEOUT_FETCH,
          'Actualisation de la liste des conversations'
        )
      })
    } catch (error: any) {
      // Handle errors gracefully
      console.error('Error saving conversation:', error)
      
      // Check if it's a timeout error
      const isTimeout = error?.message?.includes('timeout') || error?.isTimeout
      const errorInfo = categorizeError(error)
      
      if (isTimeout || errorInfo.category === 'timeout') {
        // Timeout errors are logged but not shown to user to avoid annoying notifications
        // setSaveLoadError({
        //   message: 'Chargement interrompu. Cela est généralement dû à une instabilité du réseau Internet. Veuillez vérifier votre connexion et réessayer.',
        //   retryable: true,
        //   onRetry: handleSaveConversation
        // })
      } else {
        // For other errors, show alert (keeping existing behavior for non-timeout errors)
        const errorMessage = error?.message || error?.details || errorInfo.message || 'Erreur inconnue'
        console.error('Error details:', errorMessage)
        alert(`Erreur lors de la sauvegarde: ${errorMessage}. Veuillez vérifier la console pour plus de détails.`)
      }
    } finally {
      setIsSavingConversation(false)
    }
  }

  // Load web search usage from database
  const loadWebSearchUsage = async () => {
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/a302786d-6f82-4ad4-a337-c823ecbab34b',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'LandingChatScreen.tsx:4192',message:'loadWebSearchUsage called',data:{isLoading:isLoadingWebSearchUsage.current,stack:new Error().stack?.split('\n').slice(1,4).join('|')},timestamp:Date.now(),runId:'run1',hypothesisId:'A'})}).catch(()=>{});
    // #endregion
    // Prevent concurrent calls
    if (isLoadingWebSearchUsage.current) {
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/a302786d-6f82-4ad4-a337-c823ecbab34b',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'LandingChatScreen.tsx:4195',message:'loadWebSearchUsage early return - already loading',data:{},timestamp:Date.now(),runId:'run1',hypothesisId:'C'})}).catch(()=>{});
      // #endregion
      return
    }
    
    isLoadingWebSearchUsage.current = true
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/a302786d-6f82-4ad4-a337-c823ecbab34b',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'LandingChatScreen.tsx:4198',message:'loadWebSearchUsage started',data:{},timestamp:Date.now(),runId:'run1',hypothesisId:'A'})}).catch(()=>{});
    // #endregion
    
    try {
      // Add timeout to prevent hanging (5 seconds)
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => {
          // #region agent log
          fetch('http://127.0.0.1:7242/ingest/a302786d-6f82-4ad4-a337-c823ecbab34b',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'LandingChatScreen.tsx:4203',message:'Timeout triggered',data:{},timestamp:Date.now(),runId:'run1',hypothesisId:'B'})}).catch(()=>{});
          // #endregion
          reject(new Error('Timeout loading web search usage'))
        }, 5000)
      })
      
      const loadPromise = (async () => {
        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/a302786d-6f82-4ad4-a337-c823ecbab34b',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'LandingChatScreen.tsx:4206',message:'loadPromise started',data:{},timestamp:Date.now(),runId:'run1',hypothesisId:'A'})}).catch(()=>{});
        // #endregion
        // Get session, refresh if needed
        let { data: { session }, error: sessionError } = await supabase.auth.getSession()
        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/a302786d-6f82-4ad4-a337-c823ecbab34b',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'LandingChatScreen.tsx:4208',message:'Session retrieved',data:{hasSession:!!session?.user},timestamp:Date.now(),runId:'run1',hypothesisId:'A'})}).catch(()=>{});
        // #endregion
        
        if (!session?.user) {
          setWebSearchUsage(null)
          return
        }

        // Try to load usage
        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/a302786d-6f82-4ad4-a337-c823ecbab34b',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'LandingChatScreen.tsx:4216',message:'Starting database query',data:{userId:session.user.id},timestamp:Date.now(),runId:'run1',hypothesisId:'A'})}).catch(()=>{});
        // #endregion
        let { data: usage, error } = await supabase
          .from('web_search_usage')
          .select('search_count, first_search_at, last_search_at')
          .eq('user_id', session.user.id)
          .single()
        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/a302786d-6f82-4ad4-a337-c823ecbab34b',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'LandingChatScreen.tsx:4220',message:'Database query completed',data:{hasUsage:!!usage,errorCode:error?.code,errorMessage:error?.message},timestamp:Date.now(),runId:'run1',hypothesisId:'A'})}).catch(()=>{});
        // #endregion

        // Handle 406 error (Not Acceptable) - check multiple error properties
        const is406Error = error && (
          error.code === '406' || 
          error.status === 406 || 
          error.statusCode === 406 ||
          error.message?.includes('406') ||
          error.message?.includes('Not Acceptable') ||
          (error as any)?.response?.status === 406
        )
        
        if (is406Error) {
          // 406 error usually means RLS issue or session problem
          // Don't retry - just set default state and return early to avoid blocking
          console.warn('406 error loading web search usage - setting default state (non-blocking)')
          setWebSearchUsage({
            searchCount: 0,
            firstSearchAt: null,
            canSearch: true,
            daysRemaining: null
          })
          isLoadingWebSearchUsage.current = false
          return
        }

      // Handle other errors (except PGRST116 = no rows returned)
      if (error && error.code !== 'PGRST116') {
        console.error('Error loading web search usage:', error)
        // Fallback: set default state (allow search, backend will enforce)
        setWebSearchUsage({
          searchCount: 0,
          firstSearchAt: null,
          canSearch: true,
          daysRemaining: null
        })
        isLoadingWebSearchUsage.current = false
        return
      }

      if (usage) {
        const now = new Date()
        // For "once per day" limit, check last_search_at (when they last used it)
        // If last_search_at is null, they can search (first time or no previous search)
        const lastSearchAt = usage.last_search_at ? new Date(usage.last_search_at) : null
        const daysSinceLastSearch = lastSearchAt 
          ? (now.getTime() - lastSearchAt.getTime()) / (1000 * 60 * 60 * 24)
          : null
        
        // 1 search per day: if they have a last_search_at and it's been less than 1 day, disable
        const canSearch = !lastSearchAt || daysSinceLastSearch === null || daysSinceLastSearch >= 1
        const daysRemaining = lastSearchAt && daysSinceLastSearch !== null && daysSinceLastSearch < 1
          ? Math.ceil(1 - daysSinceLastSearch) 
          : null

        setWebSearchUsage({
          searchCount: usage.search_count,
          firstSearchAt: usage.first_search_at,
          canSearch,
          daysRemaining
        })
      } else {
        // No usage record - user can search
        setWebSearchUsage({
          searchCount: 0,
          firstSearchAt: null,
          canSearch: true,
          daysRemaining: null
        })
      }
      })()
      
      // Race between load and timeout
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/a302786d-6f82-4ad4-a337-c823ecbab34b',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'LandingChatScreen.tsx:4317',message:'Starting Promise.race',data:{},timestamp:Date.now(),runId:'run1',hypothesisId:'A'})}).catch(()=>{});
      // #endregion
      await Promise.race([loadPromise, timeoutPromise])
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/a302786d-6f82-4ad4-a337-c823ecbab34b',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'LandingChatScreen.tsx:4317',message:'Promise.race resolved',data:{},timestamp:Date.now(),runId:'run1',hypothesisId:'A'})}).catch(()=>{});
      // #endregion
    } catch (error) {
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/a302786d-6f82-4ad4-a337-c823ecbab34b',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'LandingChatScreen.tsx:4318',message:'loadWebSearchUsage error caught',data:{errorMessage:error instanceof Error?error.message:String(error),isTimeout:error instanceof Error&&error.message.includes('Timeout')},timestamp:Date.now(),runId:'run1',hypothesisId:'B'})}).catch(()=>{});
      // #endregion
      console.error('Error loading web search usage:', error)
      // Fallback: set default state (allow search, backend will enforce)
      setWebSearchUsage({
        searchCount: 0,
        firstSearchAt: null,
        canSearch: true,
        daysRemaining: null
      })
    } finally {
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/a302786d-6f82-4ad4-a337-c823ecbab34b',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'LandingChatScreen.tsx:4327',message:'loadWebSearchUsage finally block',data:{},timestamp:Date.now(),runId:'run1',hypothesisId:'A'})}).catch(()=>{});
      // #endregion
      isLoadingWebSearchUsage.current = false
    }
  }

  // Load chat message usage from database
  const loadChatMessageUsage = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.user) {
        setChatMessageUsage(null)
        return
      }

      const { data: usage, error } = await supabase
        .from('chat_message_usage')
        .select('message_count, first_message_at')
        .eq('user_id', session.user.id)
        .single()

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
        console.error('Error loading chat message usage:', error)
        return
      }

      if (usage) {
        const now = new Date()
        const firstMessageAt = new Date(usage.first_message_at)
        // Check if same day (reset at midnight)
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
        const firstMessageDay = new Date(firstMessageAt.getFullYear(), firstMessageAt.getMonth(), firstMessageAt.getDate())
        
        const isSameDay = today.getTime() === firstMessageDay.getTime()
        
        if (!isSameDay) {
          // New day - reset
          setChatMessageUsage({
            messageCount: 0,
            firstMessageAt: null,
            canSend: true,
            hoursRemaining: null
          })
        } else {
          // Same day - check limit
          const canSend = usage.message_count < 10
          // Calculate hours until midnight
          const tomorrow = new Date(today)
          tomorrow.setDate(tomorrow.getDate() + 1)
          const hoursUntilMidnight = Math.ceil((tomorrow.getTime() - now.getTime()) / (1000 * 60 * 60))
          
          setChatMessageUsage({
            messageCount: usage.message_count,
            firstMessageAt: usage.first_message_at,
            canSend,
            hoursRemaining: canSend ? null : hoursUntilMidnight
          })
        }
      } else {
        // No usage record - user can send
        setChatMessageUsage({
          messageCount: 0,
          firstMessageAt: null,
          canSend: true,
          hoursRemaining: null
        })
      }
    } catch (error) {
      console.error('Error loading chat message usage:', error)
    }
  }

  // Check chat message limit before sending
  const checkChatMessageLimit = (): { canSend: boolean; errorMessage: string | null } => {
    if (!chatMessageUsage) {
      // If usage not loaded yet, allow (backend will enforce)
      return { canSend: true, errorMessage: null }
    }

    if (!chatMessageUsage.canSend && chatMessageUsage.hoursRemaining !== null) {
      const errorMessage = `Vous avez atteint la limite de 10 messages par jour. Veuillez réessayer dans ${chatMessageUsage.hoursRemaining} heure${chatMessageUsage.hoursRemaining > 1 ? 's' : ''}.`
      return { canSend: false, errorMessage }
    }

    return { canSend: true, errorMessage: null }
  }

  // Check web search limit before performing search
  const checkWebSearchLimit = (): { canSearch: boolean; errorMessage: string | null } => {
    if (!webSearchUsage) {
      // If usage not loaded yet, don't allow (prevent race condition)
      // The button will be disabled while loading
      return { canSearch: false, errorMessage: 'Chargement...' }
    }

    if (!webSearchUsage.canSearch) {
      // User has already used their 1 search per day
      const errorMessage = 'Fonctionnalité premium — Veuillez réessayer demain.'
      return { canSearch: false, errorMessage }
    }

    return { canSearch: true, errorMessage: null }
  }

  const handleSend = async () => {
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/a302786d-6f82-4ad4-a337-c823ecbab34b',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'LandingChatScreen.tsx:4430',message:'handleSend called',data:{actionMode:selectedActionMode,isChatLoading},timestamp:Date.now(),runId:'run1',hypothesisId:'E'})}).catch(()=>{});
    // #endregion
    if (!inputValue.trim() || isChatLoading) return
    
    // Validate message length (7K tokens max ≈ 28,000 characters) - silently block if exceeded
    const MAX_MESSAGE_LENGTH = 28000 // Conservative limit for 7K tokens (4 chars per token)
    if (inputValue.length > MAX_MESSAGE_LENGTH) {
      return // Silently block - don't send the message
    }
    
    // Store the current action mode before resetting
    const currentActionMode = selectedActionMode
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/a302786d-6f82-4ad4-a337-c823ecbab34b',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'LandingChatScreen.tsx:4440',message:'handleSend continuing',data:{currentActionMode},timestamp:Date.now(),runId:'run1',hypothesisId:'E'})}).catch(()=>{});
    // #endregion
    
    // Check web search limit BEFORE adding message to chat
    if (currentActionMode === 'websearch') {
      const limitCheck = checkWebSearchLimit()
      if (!limitCheck.canSearch) {
        setShowWebSearchLimitTooltip(true)
        return // Exit early - don't add message or make request
      }
    }
    
    // Check chat message limit for ALL messages (including web search)
    // Web search has its own limit (1 per day) but also counts towards the 10 messages/day limit
    const messageLimitCheck = checkChatMessageLimit()
    if (!messageLimitCheck.canSend) {
      setShowChatMessageLimitTooltip(true)
      return // Exit early - don't add message or make request
    }
    
    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      text: inputValue,
      sender: 'user',
      timestamp: new Date(),
      isWebSearch: currentActionMode === 'websearch'
    }

    setChatMessages(prev => [...prev, userMessage])
    setInputValue('')
    // Hide prompt suggestions after first message is sent
    setShowPromptSuggestions(false)
    // Don't reset action mode if it's 'spreadsheet' - let user reset it via + button
    // Other action modes are reset when sending a message (they're one-time actions)
    if (currentActionMode !== 'spreadsheet') {
      setSelectedActionMode(null)
    }
    setIsChatLoading(true)
    setLoadingActionMode(currentActionMode)

    try {
      // Check if web search mode is active
      if (currentActionMode === 'websearch') {

        // Call the web search Edge Function
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
        const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

        if (!supabaseUrl || !supabaseAnonKey) {
          throw new Error('Supabase configuration missing')
        }

        const response = await fetch(`${supabaseUrl}/functions/v1/web-search`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${supabaseAnonKey}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ query: inputValue }),
        })

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))
          
          // Handle Serper API limit error (simplest check)
          if (errorData.error === 'SERPER_LIMIT_REACHED' || response.status === 503) {
            setShowSerperLimitTooltip(true)
            setIsChatLoading(false)
            setLoadingActionMode(null)
            // Remove the user message since search failed
            setChatMessages(prev => prev.filter(msg => msg.id !== userMessage.id))
            return
          }
          
          // Handle 429 (rate limit) from backend
          if (response.status === 429) {
            // Reload usage from backend to get accurate state (non-blocking)
            loadWebSearchUsage().catch(err => {
              console.warn('Failed to reload web search usage (non-blocking):', err)
            })
            setShowWebSearchLimitTooltip(true)
            setIsChatLoading(false)
            setLoadingActionMode(null)
            // Remove the user message since search failed
            setChatMessages(prev => prev.filter(msg => msg.id !== userMessage.id))
            return
          }
          
          throw new Error(errorData.error || 'Failed to search the web')
        }

        const data = await response.json()

        // Update frontend usage state immediately after successful search
        // This ensures the button is disabled right away (1 search per day)
        const now = new Date()
        // Preserve existing firstSearchAt if it exists, otherwise use current time
        const existingFirstSearchAt = webSearchUsage?.firstSearchAt || now.toISOString()
        setWebSearchUsage({
          searchCount: (webSearchUsage?.searchCount || 0) + 1,
          firstSearchAt: existingFirstSearchAt,
          canSearch: false, // Disable after first use - will be recalculated when loadWebSearchUsage() runs
          daysRemaining: 1 // 1 day until next day
        })

        // Also update database in background (non-blocking)
        loadWebSearchUsage().catch(err => {
          console.warn('Failed to reload web search usage (non-blocking):', err)
        })

        const aiResult = processAIResponse(data.reply, selectedActionMode === 'spreadsheet')
        const assistantMessage: ChatMessage = {
          id: (Date.now() + 1).toString(),
          text: data.reply,
          sender: 'assistant',
          timestamp: new Date(),
          isWebSearch: true,
          sources: data.sources || [],
          aiResult
        }
        setChatMessages(prev => [...prev, assistantMessage])
        
        // Update chat message usage after successful web search (counts towards 10 messages/day limit)
        try {
          const { data: { session } } = await supabase.auth.getSession()
          if (session?.user) {
            const now = new Date()
            const { data: existingUsage } = await supabase
              .from('chat_message_usage')
              .select('message_count, first_message_at')
              .eq('user_id', session.user.id)
              .single()
            
            if (existingUsage) {
              const firstMessageAt = new Date(existingUsage.first_message_at)
              const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
              const firstMessageDay = new Date(firstMessageAt.getFullYear(), firstMessageAt.getMonth(), firstMessageAt.getDate())
              
              if (today.getTime() === firstMessageDay.getTime()) {
                // Same day - increment
                await supabase
                  .from('chat_message_usage')
                  .update({
                    message_count: existingUsage.message_count + 1,
                    last_message_at: now.toISOString(),
                    updated_at: now.toISOString()
                  })
                  .eq('user_id', session.user.id)
              } else {
                // New day - reset
                await supabase
                  .from('chat_message_usage')
                  .update({
                    message_count: 1,
                    first_message_at: now.toISOString(),
                    last_message_at: now.toISOString(),
                    updated_at: now.toISOString()
                  })
                  .eq('user_id', session.user.id)
              }
            } else {
              // No record - create one
              await supabase
                .from('chat_message_usage')
                .insert({
                  user_id: session.user.id,
                  message_count: 1,
                  first_message_at: now.toISOString(),
                  last_message_at: now.toISOString()
                })
            }
            
            // Reload usage to update state
            await loadChatMessageUsage()
          }
        } catch (error) {
          console.error('Error updating chat message usage after web search:', error)
          // Still reload to get latest state
          await loadChatMessageUsage()
        }
        
        return
      }

      // Check if there's a locked selection in the editor
      const hasLockedSelection = isSelectionLocked && lockedSelection && editor
      
      if (hasLockedSelection) {
        // Get selected text from editor
        const selectedText = editor.state.doc.textBetween(
          lockedSelection.from,
          lockedSelection.to,
          ' '
        )

        // Call edit-proposal Edge Function
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
        const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

        if (!supabaseUrl || !supabaseAnonKey) {
          throw new Error('Supabase configuration missing')
        }

        const response = await fetch(`${supabaseUrl}/functions/v1/edit-proposal`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${supabaseAnonKey}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ 
            instruction: inputValue,
            selectedText: selectedText,
            selectionRange: lockedSelection
          }),
        })

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))
          throw new Error(errorData.error || 'Failed to get proposal')
        }

        const data = await response.json()

        if (!data.success) {
          throw new Error(data.error || 'Failed to get proposal')
        }

        // Display proposal as assistant message with action buttons
        const proposalMessage: ChatMessage = {
          id: (Date.now() + 1).toString(),
          text: data.proposal,
          sender: 'assistant',
          timestamp: new Date(),
          isProposal: true,
          proposalData: {
            proposal: data.proposal,
            selectionRange: lockedSelection
          }
        }
        setChatMessages(prev => [...prev, proposalMessage])

      } else {
        // Check if there's a locked table selection (for Tableur Avocat)
        const hasLockedTableSelection = isTableSelectionLocked && lockedTableSelection && currentTableData
        
        // Prepare messages for the API (convert to OpenAI format)
        const apiMessages = chatMessages.map(m => ({
          role: m.sender === 'user' ? 'user' : 'assistant',
          content: m.text
        }))
        
        
        // Add table selection context if locked
        let userMessage = inputValue
        if (hasLockedTableSelection) {
          // Get selected column or row data
          let selectedData: string = ''
          if (lockedTableSelection.type === 'column') {
            const columnIndex = lockedTableSelection.index
            selectedData = currentTableData.rows
              .map(row => row[columnIndex] || '')
              .join('\n')
            userMessage = `Colonne sélectionnée (${currentTableData.columns[columnIndex]}):\n${selectedData}\n\n${inputValue}`
          } else {
            const rowIndex = lockedTableSelection.index
            selectedData = currentTableData.rows[rowIndex]?.join('\t') || ''
            userMessage = `Ligne sélectionnée (ligne ${rowIndex + 1}):\n${selectedData}\n\n${inputValue}`
          }
        }
        
        // Add the new user message
        apiMessages.push({ role: 'user', content: userMessage })

        // Map selectedLaw to namespace
        const namespaceMap = {
          'tunisien': 'tunisia',
          'algerien': 'algeria',
          'marocain': 'morocco'
        }
        const namespace = namespaceMap[selectedLaw] || 'tunisia'

        // Call the Edge Function with namespace and language (with fallback to local server)
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
        const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

        if (!supabaseUrl || !supabaseAnonKey) {
          throw new Error('Supabase configuration missing')
        }

        let response
        let data
        
        try {
          // Get user session token for authentication
          // First try to refresh the session to ensure we have a valid token
          const { data: { session: refreshedSession }, error: refreshError } = await supabase.auth.refreshSession()
          const { data: { session }, error: sessionError } = await supabase.auth.getSession()
          
          // Use refreshed session if available, otherwise fall back to current session
          const activeSession = refreshedSession || session
          
          // #region agent log
          fetch('http://127.0.0.1:7242/ingest/a302786d-6f82-4ad4-a337-c823ecbab34b',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'LandingChatScreen.tsx:4271',message:'Session check after refresh',data:{hasRefreshedSession:!!refreshedSession,hasSession:!!session,hasActiveSession:!!activeSession,refreshError:refreshError?.message||'none',sessionError:sessionError?.message||'none',timestamp:Date.now()},runId:'run1',hypothesisId:'D'})}).catch(()=>{});
          // #endregion
          
          if (refreshError && sessionError) {
            console.error('Session errors:', { refreshError, sessionError })
            throw new Error('Erreur de session. Veuillez vous reconnecter.')
          }
          
          if (!activeSession || !activeSession.access_token) {
            throw new Error('Vous devez être connecté pour utiliser cette fonctionnalité.')
          }

          // Regular chat flow
          // Try Edge Function first
          // Use anon key for gateway auth, pass user token in body
          // #region agent log
          fetch('http://127.0.0.1:7242/ingest/a302786d-6f82-4ad4-a337-c823ecbab34b',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'LandingChatScreen.tsx:4295',message:'About to call chat-legal-fast with anon key',data:{hasActiveSession:!!activeSession,hasAccessToken:!!activeSession?.access_token,usingAnonKey:true,url:`${supabaseUrl}/functions/v1/chat-legal-fast`,timestamp:Date.now()},runId:'run1',hypothesisId:'E'})}).catch(()=>{});
          // #endregion
          response = await fetch(`${supabaseUrl}/functions/v1/chat-legal-fast`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${supabaseAnonKey}`,  // Use anon key to pass gateway
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ 
              messages: apiMessages,
              namespace: namespace,
              language: language,
              userAccessToken: activeSession.access_token  // Pass user token in body
            }),
          })
          // #region agent log
          fetch('http://127.0.0.1:7242/ingest/a302786d-6f82-4ad4-a337-c823ecbab34b',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'LandingChatScreen.tsx:4297',message:'Response received',data:{status:response.status,statusText:response.statusText,ok:response.ok,timestamp:Date.now()},runId:'run1',hypothesisId:'B'})}).catch(()=>{});
          // #endregion

          // Check if response is ok before parsing JSON
          if (!response.ok) {
            let errorMessage = 'Failed to get response'
            let errorData = {}
            try {
              const responseText = await response.text()
              // #region agent log
              fetch('http://127.0.0.1:7242/ingest/a302786d-6f82-4ad4-a337-c823ecbab34b',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'LandingChatScreen.tsx:4292',message:'Error response body',data:{status:response.status,responseText:responseText.substring(0,200),timestamp:Date.now()},runId:'run1',hypothesisId:'A'})}).catch(()=>{});
              // #endregion
              try {
                errorData = JSON.parse(responseText)
              errorMessage = errorData.error || errorMessage
              } catch {
                errorMessage = responseText || `Server error: ${response.status} ${response.statusText}`
              }
            } catch (e) {
              // If JSON parsing fails, use status text
              errorMessage = `Server error: ${response.status} ${response.statusText}`
            }
            throw new Error(errorMessage)
          }
          
          data = await response.json()
        } catch (fetchError: any) {
          // Re-throw the error - no localhost fallback
          throw fetchError
        }

        if (!data.success) {
          throw new Error(data.error || 'Failed to get response')
        }

        // Debug: Log backend response
        console.log('📥 Backend response:', {
          replyLength: data.reply?.length,
          replyPreview: data.reply?.substring(0, 200),
          hasDuplicates: data.reply?.split('\n').filter((line: string, idx: number, arr: string[]) => 
            line.trim() && arr.indexOf(line.trim()) !== idx
          ).length > 0
        })
        
        // Frontend deduplication as safety net
        let deduplicatedReply = data.reply
        if (deduplicatedReply) {
          // Remove duplicate lines
          const lines = deduplicatedReply.split('\n')
          const uniqueLines: string[] = []
          const seenLines = new Set<string>()
          
          for (const line of lines) {
            const normalized = line.trim().toLowerCase()
            if (normalized && !seenLines.has(normalized)) {
              seenLines.add(normalized)
              uniqueLines.push(line)
            } else if (!normalized) {
              uniqueLines.push(line) // Keep empty lines
            }
          }
          
          // Remove duplicate paragraphs
          const paragraphs = uniqueLines.join('\n').split('\n\n')
          const uniqueParagraphs: string[] = []
          const seenParagraphs = new Set<string>()
          
          for (const paragraph of paragraphs) {
            const normalized = paragraph.trim().toLowerCase()
            if (normalized && !seenParagraphs.has(normalized)) {
              seenParagraphs.add(normalized)
              uniqueParagraphs.push(paragraph)
            } else if (!normalized) {
              uniqueParagraphs.push(paragraph) // Keep empty paragraphs
            }
          }
          
          deduplicatedReply = uniqueParagraphs.join('\n\n')
          
          if (deduplicatedReply.length !== data.reply.length) {
            console.log('🔍 Frontend deduplication: Removed duplicates', {
              originalLength: data.reply.length,
              deduplicatedLength: deduplicatedReply.length,
              removed: data.reply.length - deduplicatedReply.length
            })
          }
        }
        
        // Stop loading immediately when response arrives, before adding message
        setIsChatLoading(false)
        setLoadingActionMode(null)
        
        const aiResult = processAIResponse(deduplicatedReply, selectedActionMode === 'spreadsheet')
        const messageId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
        
        // Debug: Log message details before adding
        console.log('💬 Creating assistant message:', {
          id: messageId,
          textLength: deduplicatedReply?.length,
          textPreview: deduplicatedReply?.substring(0, 100)
        })
        
        const assistantMessage: ChatMessage = {
          id: messageId,
          text: deduplicatedReply,
          sender: 'assistant',
          timestamp: new Date(),
          aiResult,
          confidence: data.confidence,
          legalSources: data.sources,
          metadata: data.metadata
        }
        
        // Debug: Check for duplicate IDs before adding
        setChatMessages(prev => {
          const existingIds = new Set(prev.map(m => m.id))
          let finalMessageId = messageId
          if (existingIds.has(messageId)) {
            console.warn('⚠️ Duplicate message ID detected:', messageId, 'Generating new ID')
            // Generate new ID if collision detected
            finalMessageId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
            assistantMessage.id = finalMessageId
          }
          console.log('📊 Current messages count:', prev.length, 'Adding message with ID:', finalMessageId)
          return [...prev, assistantMessage]
        })
        // Add message ID to streaming set for typewriter effect (only if not a table result)
        if (!aiResult || aiResult.kind !== 'table') {
          setStreamingMessageIds(prev => new Set(prev).add(assistantMessage.id))
        }
        
        // Update chat message usage after successful message (including web search)
        // Web search counts towards the 10 messages/day limit
        try {
            const { data: { session } } = await supabase.auth.getSession()
            if (session?.user) {
              // Update or insert chat message usage
              const now = new Date()
              const { data: existingUsage } = await supabase
                .from('chat_message_usage')
                .select('message_count, first_message_at')
                .eq('user_id', session.user.id)
                .single()
              
              if (existingUsage) {
                const firstMessageAt = new Date(existingUsage.first_message_at)
                const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
                const firstMessageDay = new Date(firstMessageAt.getFullYear(), firstMessageAt.getMonth(), firstMessageAt.getDate())
                
                if (today.getTime() === firstMessageDay.getTime()) {
                  // Same day - increment
                  await supabase
                    .from('chat_message_usage')
                    .update({
                      message_count: existingUsage.message_count + 1,
                      last_message_at: now.toISOString(),
                      updated_at: now.toISOString()
                    })
                    .eq('user_id', session.user.id)
                } else {
                  // New day - reset
                  await supabase
                    .from('chat_message_usage')
                    .update({
                      message_count: 1,
                      first_message_at: now.toISOString(),
                      last_message_at: now.toISOString(),
                      updated_at: now.toISOString()
                    })
                    .eq('user_id', session.user.id)
                }
              } else {
                // No record - create one
                await supabase
                  .from('chat_message_usage')
                  .insert({
                    user_id: session.user.id,
                    message_count: 1,
                    first_message_at: now.toISOString(),
                    last_message_at: now.toISOString()
                  })
              }
              
              // Reload usage to update state
              await loadChatMessageUsage()
            }
          } catch (error) {
            console.error('Error updating chat message usage:', error)
            // Still reload to get latest state
            await loadChatMessageUsage()
          }
      }
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/a302786d-6f82-4ad4-a337-c823ecbab34b',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'LandingChatScreen.tsx:4942',message:'handleSend try block completed',data:{},timestamp:Date.now(),runId:'run1',hypothesisId:'E'})}).catch(()=>{});
      // #endregion

    } catch (error) {
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/a302786d-6f82-4ad4-a337-c823ecbab34b',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'LandingChatScreen.tsx:4944',message:'handleSend error caught',data:{errorMessage:error instanceof Error?error.message:String(error)},timestamp:Date.now(),runId:'run1',hypothesisId:'E'})}).catch(()=>{});
      // #endregion
      console.error('Chat error:', error)
      
      // Capture error in Sentry
      Sentry.captureException(error, {
        tags: {
          component: 'LandingChatScreen',
          action: 'handleSend'
        },
        extra: {
          inputValue: inputValue.substring(0, 100), // First 100 chars
          messagesCount: chatMessages.length,
          actionMode: loadingActionMode || selectedActionMode
        }
      })
      
      const errorMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        text: error instanceof Error 
          ? `⚠️ ${error.message}` 
          : '⚠️ Une erreur est survenue. Veuillez réessayer.',
        sender: 'assistant',
        timestamp: new Date()
      }
      setChatMessages(prev => [...prev, errorMessage])
    } finally {
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/a302786d-6f82-4ad4-a337-c823ecbab34b',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'LandingChatScreen.tsx:4975',message:'handleSend finally block',data:{},timestamp:Date.now(),runId:'run1',hypothesisId:'E'})}).catch(()=>{});
      // #endregion
      setIsChatLoading(false)
      setLoadingActionMode(null)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  // Handle suggestion chip click - autofill input without sending
  const handleSuggestionClick = (suggestion: string) => {
    setInputValue(suggestion)
    // Focus the textarea after a short delay to ensure it's rendered
    setTimeout(() => {
      textareaRef.current?.focus()
    }, 0)
  }

  // Improve user input with AI
  const handleImproveInput = async () => {
    if (!inputValue.trim() || isImproving || isChatLoading) return

    // Check if Avocat AI is activated
    if (!isEditeurAIActivated) {
      alert("Veuillez d'abord activer Avocat AI pour utiliser cette fonctionnalité.")
      return
    }

    setIsImproving(true)

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

      if (!supabaseUrl || !supabaseAnonKey) {
        throw new Error('Supabase configuration missing')
      }

      const response = await fetch(`${supabaseUrl}/functions/v1/improve-text`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${supabaseAnonKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ text: inputValue }),
      })

      // Check if response is ok before parsing JSON
      if (!response.ok) {
        let errorMessage = 'Failed to improve text'
        try {
          const errorData = await response.json()
          errorMessage = errorData.error || errorMessage
        } catch (e) {
          // If JSON parsing fails, use status text
          errorMessage = `Server error: ${response.status} ${response.statusText}`
        }
        throw new Error(errorMessage)
      }

      const data = await response.json()

      if (!data.success) {
        throw new Error(data.error || 'Failed to improve text')
      }

      // Replace input with improved text
      setInputValue(data.improvedText)

    } catch (error) {
      console.error('Improve error:', error)
      // Show error but don't clear input
      alert(error instanceof Error ? error.message : 'Erreur lors de l\'amélioration du texte')
    } finally {
      setIsImproving(false)
    }
  }

  // Load last AI response into editor
  const handleEditWithAI = () => {
    // Find the last assistant message
    const lastAssistantMessage = [...chatMessages]
      .reverse()
      .find(msg => msg.sender === 'assistant')

    if (!lastAssistantMessage) return

    // Check if the message contains a table
    if (lastAssistantMessage.aiResult?.kind === 'table' && lastAssistantMessage.aiResult.table) {
      // It's a table - convert to HTML and display in Tiptap editor
      const tableData = lastAssistantMessage.aiResult.table
      
      if (!editor) return
      
      // Build proper HTML table structure with all content
      const headerCells = tableData.columns.map(col => `<th>${String(col || '').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</th>`).join('')
      const headerRow = `<tr>${headerCells}</tr>`
      
      const bodyRows = tableData.rows.map(row => {
        const cells = row.map(cell => `<td>${String(cell || '').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</td>`).join('')
        return `<tr>${cells}</tr>`
      }).join('')
      
      const htmlTable = `<table><thead>${headerRow}</thead><tbody>${bodyRows}</tbody></table>`
      
      // Convert HTML to ProseMirror JSON format for Tiptap
      const extensions = editor.extensionManager.extensions
      const jsonContent = generateJSON(htmlTable, extensions)
      
      // Insert into Tiptap editor using JSON format
      editor.commands.setContent(jsonContent)
      
      // Update the active document with the table content (store as HTML)
      setDocuments(prev => 
        prev.map(doc => 
          doc.id === activeDocumentId 
            ? { ...doc, content: htmlTable } 
            : doc
        )
      )
      
      // Open the editor with Édition menu selected
      setIsEditorOpen(true)
      setIsEditorContentVisible(true)
      setActivePanel('editor')
      setSelectedSidebarItem('editeur-avocat')
      setActiveMenuButton('Édition')
      setLayoutMode('editor')
      
      return
    }

    // It's not a table - open in Editeur Avocat tab (original behavior)
    if (!editor) return

    console.log('🟡 handleEditWithAI CALLED')
    console.log('🟡 Last assistant message text (first 200):', lastAssistantMessage.text.substring(0, 200))

    try {
      // Convert markdown to HTML using the same utility as handleApplyProposal
      const htmlContent = markdownToHtml(lastAssistantMessage.text)
      
      console.log('🟡 Converted HTML (first 400):', htmlContent.substring(0, 400))

      // Convert HTML to ProseMirror JSON format for Tiptap
      const extensions = editor.extensionManager.extensions
      const jsonContent = generateJSON(htmlContent, extensions)
      
      console.log('🟡 ProseMirror JSON type:', jsonContent?.type)
      console.log('🟡 ProseMirror JSON preview:', JSON.stringify(jsonContent).substring(0, 500))

      // Convert JSON back to HTML for document storage (Tiptap can work with JSON too, but we'll use HTML for consistency)
      // Actually, let's use the HTML directly since setContent can handle HTML
      const finalHtmlContent = htmlContent || '<p></p>'

      // Update the active document with the converted HTML content
      setDocuments(prev => 
        prev.map(doc => 
          doc.id === activeDocumentId 
            ? { ...doc, content: finalHtmlContent } 
            : doc
        )
      )

      // Also set the editor content directly to ensure it's updated
      // Use a small delay to ensure the editor is ready
      setTimeout(() => {
        if (editor) {
          editor.commands.setContent(finalHtmlContent)
        }
      }, 100)

      // Open the editor with Édition menu selected
      setIsEditorOpen(true)
      setIsEditorContentVisible(true)
      setActivePanel('editor')
      setSelectedSidebarItem('editeur-avocat')
      setActiveMenuButton('Édition')
      setLayoutMode('editor')
      
      console.log('🟡 Editor opened with converted content')
    } catch (error) {
      console.error('🟡 Error in handleEditWithAI:', error)
      // Fallback: use the original text splitting method
      const htmlContent = lastAssistantMessage.text
        .split('\n')
        .filter(line => line.trim())
        .map(line => `<p>${line}</p>`)
        .join('')
      
      setDocuments(prev => 
        prev.map(doc => 
          doc.id === activeDocumentId 
            ? { ...doc, content: htmlContent || '<p></p>' } 
            : doc
        )
      )
      
      setIsEditorOpen(true)
      setIsEditorContentVisible(true)
      setActivePanel('editor')
      setLayoutMode('editor')
    }
  }

  // Apply proposal to editor
  const handleApplyProposal = (messageId: string) => {
    console.log('🔵 handleApplyProposal CALLED with messageId:', messageId)
    const message = chatMessages.find(m => m.id === messageId)
    console.log('🔵 Message found:', message ? 'YES' : 'NO')
    console.log('🔵 Proposal data:', message?.proposalData ? 'YES' : 'NO')
    console.log('🔵 Editor available:', editor ? 'YES' : 'NO')
    
    if (!message || !message.proposalData || !editor) {
      console.log('🔴 EARLY RETURN - Missing data')
      return
    }

    const { proposal, selectionRange } = message.proposalData

    // Handle empty proposals gracefully
    if (!proposal || proposal.trim().length === 0) {
      // Just remove the highlight and clear selection state
      editor.chain()
        .focus()
        .setTextSelection(selectionRange)
        .unsetHighlight()
        .setTextSelection(selectionRange.from)
        .run()
      
      setIsSelectionLocked(false)
      setLockedSelection(null)
      setShowSelectionBar(false)
      setSelectedCharCount(0)
      setChatMessages(prev => prev.filter(m => m.id !== messageId))
      return
    }

    // Convert markdown to HTML before inserting
    // This handles all markdown elements: headers (#, ##, ###), bold (**text**), 
    // italic (*text*), lists (ordered/unordered), code blocks, inline code, links, line breaks
    try {
      console.log('=== STARTING PROPOSAL INSERT ===')
      console.log('Proposal received:', proposal ? 'YES' : 'NO')
      console.log('Proposal length:', proposal?.length || 0)
      console.log('Proposal preview:', proposal?.substring(0, 200) || 'N/A')
      
      const htmlContent = markdownToHtml(proposal)
      
      console.log('=== HTML CONVERSION ===')
      console.log('HTML length:', htmlContent?.length || 0)
      console.log('HTML preview:', htmlContent?.substring(0, 400) || 'N/A')

      // Convert HTML to ProseMirror JSON using Tiptap's generateJSON
      // This ensures the HTML is properly parsed into Tiptap's document format
      const extensions = editor.extensionManager.extensions
      console.log('Extensions count:', extensions?.length || 0)
      
      const jsonContent = generateJSON(htmlContent, extensions)
      
      console.log('=== PROSEMIRROR JSON ===')
      console.log('JSON preview:', JSON.stringify(jsonContent).substring(0, 500))
      console.log('JSON type:', jsonContent?.type)
      console.log('JSON content length:', jsonContent?.content?.length || 0)

      // Insert the JSON content into the editor
      editor.chain()
        .focus()
        .setTextSelection(selectionRange)
        .unsetHighlight() // Remove the green highlight
        .insertContent(jsonContent)
        .run()
        
      console.log('=== INSERTION COMPLETE ===')
    } catch (error) {
      console.error('=== ERROR INSERTING PROPOSAL ===', error)
      console.error('Error details:', {
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
        proposal: proposal?.substring(0, 100)
      })
      // Fallback: try inserting as plain text
      editor.chain()
        .focus()
        .setTextSelection(selectionRange)
        .unsetHighlight()
        .insertContent(proposal)
        .run()
    }

    // Clear selection state
    setIsSelectionLocked(false)
    setLockedSelection(null)
    setShowSelectionBar(false)
    setSelectedCharCount(0)

    // Remove the proposal message from chat
    setChatMessages(prev => prev.filter(m => m.id !== messageId))
  }

  // Reject proposal
  const handleRejectProposal = (messageId: string) => {
    // Remove highlight from editor if there's a locked selection
    if (editor && lockedSelection) {
      editor.chain()
        .focus()
        .setTextSelection(lockedSelection)
        .unsetHighlight()
        .setTextSelection(lockedSelection.from)
        .run()
    }
    
    // Clear selection state when rejecting
    setIsSelectionLocked(false)
    setLockedSelection(null)
    setShowSelectionBar(false)
    setSelectedCharCount(0)
    
    // Remove the proposal message from chat
    setChatMessages(prev => prev.filter(m => m.id !== messageId))
  }

  // Handle Reformuler - Reformulate selected text and show as proposal in chat
  const handleReformuler = async () => {
    // Check chat message limit BEFORE proceeding
    const messageLimitCheck = checkChatMessageLimit()
    if (!messageLimitCheck.canSend) {
      setShowChatMessageLimitTooltip(true)
      return // Exit early - don't proceed with reformulation
    }

    if (!editor || !isSelectionLocked || !lockedSelection) {
      alert('Veuillez d\'abord sélectionner et valider du texte.')
      return
    }

    const { from, to } = lockedSelection
    const selectedText = editor.state.doc.textBetween(from, to, '')

    if (!selectedText || selectedText.trim().length === 0) {
      alert('Veuillez sélectionner du texte à reformuler.')
      return
    }

    // Add loading message to chat
    const loadingMessageId = Date.now().toString()
    const loadingMessage: ChatMessage = {
      id: loadingMessageId,
      text: 'Reformulation en cours',
      sender: 'assistant',
      timestamp: new Date(),
      isProposal: false,
      isLoading: true
    }
    setChatMessages(prev => [...prev, loadingMessage])

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY
      
      if (!supabaseUrl || !supabaseAnonKey) {
        throw new Error('Supabase configuration missing')
      }

      const response = await fetch(`${supabaseUrl}/functions/v1/reformulate-text`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${supabaseAnonKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ selectedText }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || 'Failed to reformulate text')
      }

      const data = await response.json()
      if (!data.success) {
        throw new Error(data.error || 'Reformulation failed')
      }
      const reformulatedText = data.reformulatedText

      // Remove loading message and add proposal message
      setChatMessages(prev => {
        const filtered = prev.filter(m => m.id !== loadingMessageId)
        const proposalMessage: ChatMessage = {
          id: Date.now().toString(),
          text: reformulatedText,
          sender: 'assistant',
          timestamp: new Date(),
          isProposal: true,
          proposalData: {
            proposal: reformulatedText,
            selectionRange: { from, to }
          }
        }
        return [...filtered, proposalMessage]
      })

      // Update chat message usage after successful reformulation (counts towards 10 messages/day limit)
      try {
        const { data: { session } } = await supabase.auth.getSession()
        if (session?.user) {
          // Update or insert chat message usage
          const now = new Date()
          const { data: existingUsage } = await supabase
            .from('chat_message_usage')
            .select('message_count, first_message_at')
            .eq('user_id', session.user.id)
            .single()
          
          if (existingUsage) {
            const firstMessageAt = new Date(existingUsage.first_message_at)
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
            const firstMessageDay = new Date(firstMessageAt.getFullYear(), firstMessageAt.getMonth(), firstMessageAt.getDate())
            
            if (today.getTime() === firstMessageDay.getTime()) {
              // Same day - increment
              await supabase
                .from('chat_message_usage')
                .update({
                  message_count: existingUsage.message_count + 1,
                  last_message_at: now.toISOString(),
                  updated_at: now.toISOString()
                })
                .eq('user_id', session.user.id)
            } else {
              // New day - reset
              await supabase
                .from('chat_message_usage')
                .update({
                  message_count: 1,
                  first_message_at: now.toISOString(),
                  last_message_at: now.toISOString(),
                  updated_at: now.toISOString()
                })
                .eq('user_id', session.user.id)
            }
          } else {
            // No record - create one
            await supabase
              .from('chat_message_usage')
              .insert({
                user_id: session.user.id,
                message_count: 1,
                first_message_at: now.toISOString(),
                last_message_at: now.toISOString()
              })
          }
          
          // Reload usage to update state
          await loadChatMessageUsage()
        }
      } catch (error) {
        console.error('Error updating chat message usage after reformulation:', error)
        // Still reload to get latest state
        await loadChatMessageUsage()
      }

    } catch (error) {
      console.error('Error reformulating text:', error)
      // Remove loading message
      setChatMessages(prev => prev.filter(m => m.id !== loadingMessageId))
      alert('Erreur lors de la reformulation. Veuillez réessayer.')
    }
  }

  // Handle Analyser - Analyze selected text and show analysis in chat
  const handleAnalyser = async () => {
    // Check chat message limit BEFORE proceeding
    const messageLimitCheck = checkChatMessageLimit()
    if (!messageLimitCheck.canSend) {
      setShowChatMessageLimitTooltip(true)
      return // Exit early - don't proceed with analysis
    }

    if (!editor || !isSelectionLocked || !lockedSelection) {
      alert('Veuillez d\'abord sélectionner et valider du texte.')
      return
    }

    const { from, to } = lockedSelection
    const selectedText = editor.state.doc.textBetween(from, to, '')

    if (!selectedText || selectedText.trim().length === 0) {
      alert('Veuillez sélectionner du texte à analyser.')
      return
    }

    // Add loading message to chat
    const loadingMessageId = Date.now().toString()
    const loadingMessage: ChatMessage = {
      id: loadingMessageId,
      text: 'Analyse en cours',
      sender: 'assistant',
      timestamp: new Date(),
      isProposal: false,
      isLoading: true
    }
    setChatMessages(prev => [...prev, loadingMessage])

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY
      
      if (!supabaseUrl || !supabaseAnonKey) {
        throw new Error('Supabase configuration missing')
      }

      const response = await fetch(`${supabaseUrl}/functions/v1/analyze-text`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${supabaseAnonKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ selectedText }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || 'Failed to analyze text')
      }

      const data = await response.json()
      if (!data.success) {
        throw new Error(data.error || 'Analysis failed')
      }
      const analysis = data.analysis

      // Remove loading message and add analysis as a proposal
      setChatMessages(prev => {
        const filtered = prev.filter(m => m.id !== loadingMessageId)
        const analysisMessage: ChatMessage = {
          id: Date.now().toString(),
          text: analysis,
          sender: 'assistant',
          timestamp: new Date(),
          isProposal: true,
          proposalData: {
            proposal: analysis,
            selectionRange: { from, to }
          }
        }
        return [...filtered, analysisMessage]
      })

      // Update chat message usage after successful analysis (counts towards 10 messages/day limit)
      try {
        const { data: { session } } = await supabase.auth.getSession()
        if (session?.user) {
          // Update or insert chat message usage
          const now = new Date()
          const { data: existingUsage } = await supabase
            .from('chat_message_usage')
            .select('message_count, first_message_at')
            .eq('user_id', session.user.id)
            .single()
          
          if (existingUsage) {
            const firstMessageAt = new Date(existingUsage.first_message_at)
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
            const firstMessageDay = new Date(firstMessageAt.getFullYear(), firstMessageAt.getMonth(), firstMessageAt.getDate())
            
            if (today.getTime() === firstMessageDay.getTime()) {
              // Same day - increment
              await supabase
                .from('chat_message_usage')
                .update({
                  message_count: existingUsage.message_count + 1,
                  last_message_at: now.toISOString(),
                  updated_at: now.toISOString()
                })
                .eq('user_id', session.user.id)
            } else {
              // New day - reset
              await supabase
                .from('chat_message_usage')
                .update({
                  message_count: 1,
                  first_message_at: now.toISOString(),
                  last_message_at: now.toISOString(),
                  updated_at: now.toISOString()
                })
                .eq('user_id', session.user.id)
            }
          } else {
            // No record - create one
            await supabase
              .from('chat_message_usage')
              .insert({
                user_id: session.user.id,
                message_count: 1,
                first_message_at: now.toISOString(),
                last_message_at: now.toISOString()
              })
          }
          
          // Reload usage to update state
          await loadChatMessageUsage()
        }
      } catch (error) {
        console.error('Error updating chat message usage after analysis:', error)
        // Still reload to get latest state
        await loadChatMessageUsage()
      }

    } catch (error) {
      console.error('Error analyzing text:', error)
      // Remove loading message
      setChatMessages(prev => prev.filter(m => m.id !== loadingMessageId))
      alert('Erreur lors de l\'analyse. Veuillez réessayer.')
    }
  }

  // Handle Résumer - Summarize selected text and show as proposal in chat
  const handleResumer = async () => {
    // Check chat message limit BEFORE proceeding
    const messageLimitCheck = checkChatMessageLimit()
    if (!messageLimitCheck.canSend) {
      setShowChatMessageLimitTooltip(true)
      return // Exit early - don't proceed with summarization
    }

    if (!editor || !isSelectionLocked || !lockedSelection) {
      alert('Veuillez d\'abord sélectionner et valider du texte.')
      return
    }

    const { from, to } = lockedSelection
    const selectedText = editor.state.doc.textBetween(from, to, '')

    if (!selectedText || selectedText.trim().length === 0) {
      alert('Veuillez sélectionner du texte à résumer.')
      return
    }

    // Add loading message to chat
    const loadingMessageId = Date.now().toString()
    const loadingMessage: ChatMessage = {
      id: loadingMessageId,
      text: 'Résumé en cours',
      sender: 'assistant',
      timestamp: new Date(),
      isProposal: false,
      isLoading: true
    }
    setChatMessages(prev => [...prev, loadingMessage])

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY
      
      if (!supabaseUrl || !supabaseAnonKey) {
        throw new Error('Supabase configuration missing')
      }

      const response = await fetch(`${supabaseUrl}/functions/v1/summarize-text`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${supabaseAnonKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ selectedText }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || 'Failed to summarize text')
      }

      const data = await response.json()
      if (!data.success) {
        throw new Error(data.error || 'Summarization failed')
      }
      const summary = data.summary

      // Remove loading message and add summary as proposal
      setChatMessages(prev => {
        const filtered = prev.filter(m => m.id !== loadingMessageId)
        const summaryMessage: ChatMessage = {
          id: Date.now().toString(),
          text: summary,
          sender: 'assistant',
          timestamp: new Date(),
          isProposal: true,
          proposalData: {
            proposal: summary,
            selectionRange: { from, to }
          }
        }
        return [...filtered, summaryMessage]
      })

      // Update chat message usage after successful summarization (counts towards 10 messages/day limit)
      try {
        const { data: { session } } = await supabase.auth.getSession()
        if (session?.user) {
          // Update or insert chat message usage
          const now = new Date()
          const { data: existingUsage } = await supabase
            .from('chat_message_usage')
            .select('message_count, first_message_at')
            .eq('user_id', session.user.id)
            .single()
          
          if (existingUsage) {
            const firstMessageAt = new Date(existingUsage.first_message_at)
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
            const firstMessageDay = new Date(firstMessageAt.getFullYear(), firstMessageAt.getMonth(), firstMessageAt.getDate())
            
            if (today.getTime() === firstMessageDay.getTime()) {
              // Same day - increment
              await supabase
                .from('chat_message_usage')
                .update({
                  message_count: existingUsage.message_count + 1,
                  last_message_at: now.toISOString(),
                  updated_at: now.toISOString()
                })
                .eq('user_id', session.user.id)
            } else {
              // New day - reset
              await supabase
                .from('chat_message_usage')
                .update({
                  message_count: 1,
                  first_message_at: now.toISOString(),
                  last_message_at: now.toISOString(),
                  updated_at: now.toISOString()
                })
                .eq('user_id', session.user.id)
            }
          } else {
            // No record - create one
            await supabase
              .from('chat_message_usage')
              .insert({
                user_id: session.user.id,
                message_count: 1,
                first_message_at: now.toISOString(),
                last_message_at: now.toISOString()
              })
          }
          
          // Reload usage to update state
          await loadChatMessageUsage()
        }
      } catch (error) {
        console.error('Error updating chat message usage after summarization:', error)
        // Still reload to get latest state
        await loadChatMessageUsage()
      }

    } catch (error) {
      console.error('Error summarizing text:', error)
      // Remove loading message
      setChatMessages(prev => prev.filter(m => m.id !== loadingMessageId))
      alert('Erreur lors du résumé. Veuillez réessayer.')
    }
  }

  // Modal component
  const Modal = () => {
    const [inputValue, setInputValue] = useState(modalState.defaultValue || '')
    const verificationText = 'SUPPRIMER MON COMPTE'
    
    useEffect(() => {
      if (modalState.isOpen && modalState.type === 'rename') {
        setInputValue(modalState.defaultValue || '')
      } else if (modalState.isOpen && modalState.type === 'delete-account') {
        setInputValue('')
      }
    }, [modalState.isOpen, modalState.defaultValue, modalState.type])
    
    if (!modalState.isOpen) return null
    
    const handleConfirm = () => {
      if (modalState.type === 'rename') {
        modalState.onConfirm?.(inputValue)
      } else if (modalState.type === 'delete-account') {
        modalState.onConfirm?.(inputValue)
      } else {
        modalState.onConfirm?.()
      }
    }
    
    const handleCancel = () => {
      modalState.onCancel?.()
    }
    
    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && (modalState.type === 'rename' || (modalState.type === 'delete-account' && inputValue === verificationText))) {
        handleConfirm()
      } else if (e.key === 'Escape') {
        handleCancel()
      }
    }
    
    const isDeleteAccountVerified = modalState.type === 'delete-account' && inputValue === verificationText
    
    return (
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]"
        onClick={handleCancel}
      >
        <div 
          className={`rounded-xl shadow-2xl ${modalState.type === 'delete-account' ? 'max-w-xl' : modalState.confirmButtonText === 'Se déconnecter' ? 'max-w-lg' : 'max-w-md'} w-full mx-4 max-h-[90vh] overflow-y-auto transition-colors duration-200 ${
            theme === 'dark' ? 'bg-[#1A1A1A] border border-[#2C2C2C]' : 'bg-white border border-gray-200'
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={`${modalState.type === 'delete-account' ? 'p-5' : modalState.confirmButtonText === 'Se déconnecter' ? 'p-6' : 'p-6'}`}>
            <h3 className={`${modalState.type === 'delete-account' ? 'text-base' : 'text-xl'} font-semibold ${modalState.type === 'delete-account' ? 'mb-3' : modalState.confirmButtonText === 'Se déconnecter' ? 'mb-2 text-center' : 'mb-4'} transition-colors duration-200 ${
              theme === 'dark' ? 'text-[#FFFFFF]' : 'text-gray-900'
            }`}>
              {modalState.title}
            </h3>
            
            {modalState.type === 'rename' ? (
              <div className="mb-4">
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent transition-colors duration-200 ${
                    theme === 'dark'
                      ? 'bg-[#1F1F1F] border-[#2C2C2C] text-[#FFFFFF] placeholder-[#808080] focus:ring-[#34B27B]'
                      : 'border-gray-300 focus:ring-[#25A26D]'
                  }`}
                  autoFocus
                  placeholder="Nom de la colonne"
                />
              </div>
            ) : modalState.type === 'delete-account' ? (
              <div className="mb-5 space-y-3">
                {/* Gray background zone with warning text */}
                <div className={`rounded-lg p-3.5 space-y-2.5 transition-colors duration-200 ${
                  theme === 'dark' ? 'bg-[#151515]' : 'bg-gray-100'
                }`}>
                  <p className={`text-xs leading-relaxed ${theme === 'dark' ? 'text-[#FFFFFF]' : 'text-gray-900'}`}>
                    <strong>L'accès au service sera révoqué</strong>
                  </p>
                  <p className={`text-xs leading-relaxed ${theme === 'dark' ? 'text-[#B3B3B3]' : 'text-gray-700'}`}>
                    Vous ne pourrez plus accéder aux services de Avocat AI, y compris le chat et la plateforme.
                  </p>
                  <p className={`text-xs leading-relaxed ${theme === 'dark' ? 'text-[#FFFFFF]' : 'text-gray-900'}`}>
                    <strong>Vos données ne pourront pas être récupérées</strong>
                  </p>
                  <p className={`text-xs leading-relaxed ${theme === 'dark' ? 'text-[#B3B3B3]' : 'text-gray-700'}`}>
                    Tout l'historique des conversations sera définitivement supprimé. Tout solde inutilisé sur votre compte sera perdu lors de la suppression.
                  </p>
                  <p className={`text-xs leading-relaxed ${theme === 'dark' ? 'text-[#FFFFFF]' : 'text-gray-900'}`}>
                    <strong>Évitez les suppressions répétées</strong>
                  </p>
                  <p className={`text-xs leading-relaxed ${theme === 'dark' ? 'text-[#B3B3B3]' : 'text-gray-700'}`}>
                    La suppression et la recréation fréquentes de comptes peuvent être signalées comme une activité anormale, ce qui peut entraîner des restrictions ou une interdiction permanente.
                  </p>
                </div>

                {/* Verification input */}
                <div className="space-y-2">
                  <p className={`text-xs ${theme === 'dark' ? 'text-[#B3B3B3]' : 'text-gray-700'}`}>
                    Pour confirmer, veuillez saisir <strong>SUPPRIMER MON COMPTE</strong> ci-dessous :
                  </p>
                  <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent transition-colors duration-200 ${
                      theme === 'dark'
                        ? 'bg-[#1F1F1F] border-[#2C2C2C] text-[#FFFFFF] placeholder-[#808080] focus:ring-[#34B27B]'
                        : 'border-gray-300 focus:ring-[#25A26D]'
                    }`}
                    autoFocus
                    placeholder="SUPPRIMER MON COMPTE"
                  />
                </div>
              </div>
            ) : (
              <div className="mb-6">
                {modalState.type === 'success' && (
                  <div className="flex items-center justify-center mb-4">
                    <div className={`w-16 h-16 rounded-full flex items-center justify-center transition-colors duration-200 ${
                      theme === 'dark' ? 'bg-[#34B27B]/20' : 'bg-green-100'
                    }`}>
                      <svg className={`w-8 h-8 transition-colors duration-200 ${theme === 'dark' ? 'text-[#34B27B]' : 'text-green-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  </div>
                )}
                {modalState.confirmButtonText === 'Se déconnecter' ? (
                  <div className="space-y-4">
                    {/* Warning Icon */}
                    <div className="flex items-center justify-center mb-4">
                      <div className={`w-14 h-14 rounded-full flex items-center justify-center transition-colors duration-200 ${
                        theme === 'dark' ? 'bg-[#34B27B]/20' : 'bg-green-100'
                      }`}>
                        <svg className={`w-7 h-7 transition-colors duration-200 ${theme === 'dark' ? 'text-[#34B27B]' : 'text-green-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                      </div>
                    </div>
                    {/* Warning Message Box */}
                    <div className={`rounded-xl p-4 space-y-3 transition-colors duration-200 ${
                      theme === 'dark' ? 'bg-[#34B27B]/10 border border-[#34B27B]/30' : 'bg-green-50 border border-green-200'
                    }`}>
                      <p className={`text-sm leading-relaxed text-center font-medium transition-colors duration-200 ${
                        theme === 'dark' ? 'text-[#34B27B]' : 'text-green-900'
                      }`}>
                        {modalState.message?.split('\n\n').map((line, index) => (
                          <span key={index}>
                            {line}
                            {index < modalState.message!.split('\n\n').length - 1 && <><br /><br /></>}
                          </span>
                        ))}
                      </p>
                    </div>
                  </div>
                ) : (
                  <p className={`text-center transition-colors duration-200 ${theme === 'dark' ? 'text-[#B3B3B3]' : 'text-gray-700'}`}>
                    {modalState.message}
                  </p>
                )}
              </div>
            )}
            
            <div className={`flex ${modalState.confirmButtonText === 'Se déconnecter' ? 'justify-center' : 'justify-end'} gap-3 mt-6`}>
              {modalState.type === 'success' ? (
                <button
                  onClick={handleConfirm}
                  className={`px-4 py-2 text-white rounded-lg transition-colors ${
                    theme === 'dark' ? 'bg-[#34B27B] hover:bg-[#2A9D6B]' : 'bg-[#25A26D] hover:bg-[#1e9d5f]'
                  }`}
                >
                  OK
                </button>
              ) : (
                <>
                  <button
                    onClick={handleCancel}
                    className={`${modalState.type === 'delete-account' ? 'px-3 py-1.5 text-sm' : 'px-5 py-2.5'} rounded-lg transition-all duration-200 font-medium ${
                      theme === 'dark'
                        ? 'text-[#FFFFFF] bg-[#1F1F1F] hover:bg-[#252525] border border-[#2C2C2C]'
                        : 'text-gray-700 bg-gray-100 hover:bg-gray-200 border border-gray-300'
                    }`}
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleConfirm}
                    disabled={(modalState.type === 'delete-account' && !isDeleteAccountVerified) || isSigningOut}
                    className={`${modalState.type === 'delete-account' ? 'px-3 py-1.5 text-sm' : 'px-5 py-2.5'} rounded-lg transition-all duration-200 flex items-center justify-center gap-2 font-medium ${
                      (modalState.type === 'delete-account' && !isDeleteAccountVerified) || isSigningOut
                        ? theme === 'dark'
                          ? 'bg-[#1F1F1F] text-[#666666] cursor-not-allowed'
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : (modalState.type === 'rename' || modalState.confirmButtonText === 'Réinitialiser' || modalState.confirmButtonText === 'Se déconnecter')
                          ? theme === 'dark'
                            ? 'bg-[#34B27B] hover:bg-[#2A9D6B] text-white shadow-lg hover:shadow-xl'
                            : 'bg-[#25A26D] hover:bg-[#1F8F5A] text-white shadow-lg hover:shadow-xl'
                          : 'bg-red-600 hover:bg-red-700 text-white'
                    }`}
                  >
                    {isSigningOut && modalState.confirmButtonText === 'Se déconnecter' ? (
                      <>
                        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Déconnexion...</span>
                      </>
                    ) : (
                      modalState.type === 'delete-account' 
                        ? 'Confirmer la suppression de mon compte'
                        : modalState.confirmButtonText || (modalState.type === 'rename' ? 'Confirmer' : 'Supprimer')
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <Modal />
      
      {/* Global Success Toast Notification - Fixed at top of screen */}
      {vaultSuccess && vaultSuccess.type === 'save' && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-[10001] animate-fadeIn">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 shadow-lg max-w-md">
            <div className="flex items-start gap-3">
              <svg 
                className="w-5 h-5 flex-shrink-0 mt-0.5 text-green-600" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold mb-1 text-green-900">
                  Sauvegarde réussie
                </h3>
                <p className="text-sm text-green-700">
                  {vaultSuccess.message}
                </p>
              </div>
              <button
                onClick={() => setVaultSuccess(null)}
                className="text-green-600 hover:text-green-800 transition-colors flex-shrink-0 p-1 rounded hover:bg-green-200"
                aria-label="Fermer"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Global Error Toast Notification - Fixed at top of screen (Google-style simple design) */}
      {saveLoadError && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-[10002] animate-fadeIn">
          <div className="text-center max-w-md px-4">
            <p className="text-sm text-gray-700 mb-2">{saveLoadError.message}</p>
            <div className="flex items-center justify-center gap-3">
              {saveLoadError.retryable && saveLoadError.onRetry && (
                <button
                  onClick={() => {
                    saveLoadError.onRetry?.()
                    setSaveLoadError(null)
                  }}
                  className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                >
                  Réessayer
                </button>
              )}
              <button
                onClick={() => setSaveLoadError(null)}
                className="text-sm text-gray-500 hover:text-gray-700"
                aria-label="Fermer"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Workflows Tooltip Bubble */}
      {showWorkflowsTooltip && (() => {
        const buttonRef = workflowsButtonRef.current || workflowsButtonFullRef.current
        if (!buttonRef) return null
        
        const rect = buttonRef.getBoundingClientRect()
        // Position tooltip to the right of the button, vertically centered
        const top = rect.top + (rect.height / 2)
        const left = rect.right + 10
        
        return (
          <div
            className="fixed z-[10003] animate-fadeIn"
            style={{
              top: `${top}px`,
              left: `${left}px`,
              transform: 'translateY(-50%)',
            }}
          >
            {/* Bubble with tail */}
            <div className="relative bg-[#3ECF8E] text-white px-4 py-2.5 rounded-lg shadow-lg whitespace-nowrap">
              <span className="text-sm font-medium">Fonctionnalité premium — disponible dans la version Pro</span>
              {/* Tail pointing left */}
              <div 
                className="absolute left-0 top-1/2 transform -translate-x-full -translate-y-1/2"
                style={{
                  width: 0,
                  height: 0,
                  borderTop: '8px solid transparent',
                  borderBottom: '8px solid transparent',
                  borderRight: '8px solid #3ECF8E',
                }}
              />
            </div>
          </div>
        )
      })()}
      
      {/* Notes at Scale Tooltip Bubble */}
      {showNotesAtScaleTooltip && (() => {
        const buttonRef = notesAtScaleButtonRef.current
        if (!buttonRef) return null
        
        const rect = buttonRef.getBoundingClientRect()
        // Position tooltip above the button, horizontally centered
        const top = rect.top - 10
        const left = rect.left + (rect.width / 2)
        
        return (
          <div
            className="fixed z-[10003] animate-fadeIn"
            style={{
              top: `${top}px`,
              left: `${left}px`,
              transform: 'translate(-50%, -100%)',
            }}
          >
            {/* Bubble with tail */}
            <div className="relative bg-[#3ECF8E] text-white px-4 py-2.5 rounded-lg shadow-lg whitespace-nowrap mb-2">
              <span className="text-sm font-medium">Fonctionnalité premium — disponible dans la version Pro</span>
              {/* Tail pointing down */}
              <div 
                className="absolute left-1/2 bottom-0 transform -translate-x-1/2 translate-y-full"
                style={{
                  width: 0,
                  height: 0,
                  borderLeft: '8px solid transparent',
                  borderRight: '8px solid transparent',
                  borderTop: '8px solid #3ECF8E',
                }}
              />
            </div>
          </div>
        )
      })()}
      
      {/* Analyse Tooltip Bubble */}
      {showAnalyseTooltip && (() => {
        const buttonRef = actionButtonRef.current
        if (!buttonRef) return null
        
        const rect = buttonRef.getBoundingClientRect()
        // Position tooltip above the button, horizontally centered
        const top = rect.top - 10
        const left = rect.left + (rect.width / 2)
        
        return (
          <div
            className="fixed z-[10003] animate-fadeIn"
            style={{
              top: `${top}px`,
              left: `${left}px`,
              transform: 'translate(-50%, -100%)',
            }}
          >
            {/* Bubble with tail */}
            <div className="relative bg-[#3ECF8E] text-white px-4 py-2.5 rounded-lg shadow-lg whitespace-nowrap mb-2">
              <span className="text-sm font-medium">Fonctionnalité premium — disponible dans la version Pro</span>
              {/* Tail pointing down */}
              <div 
                className="absolute left-1/2 bottom-0 transform -translate-x-1/2 translate-y-full"
                style={{
                  width: 0,
                  height: 0,
                  borderLeft: '8px solid transparent',
                  borderRight: '8px solid transparent',
                  borderTop: '8px solid #3ECF8E',
                }}
              />
            </div>
          </div>
        )
      })()}
      
      {/* Create Table Tooltip Bubble */}
      {showCreateTableTooltip && (() => {
        const buttonRef = actionButtonRef.current
        if (!buttonRef) return null
        
        const rect = buttonRef.getBoundingClientRect()
        // Position tooltip above the button, horizontally centered
        const top = rect.top - 10
        const left = rect.left + (rect.width / 2)
        
        return (
          <div
            className="fixed z-[10003] animate-fadeIn"
            style={{
              top: `${top}px`,
              left: `${left}px`,
              transform: 'translate(-50%, -100%)',
            }}
          >
            {/* Bubble with tail */}
            <div className="relative bg-[#3ECF8E] text-white px-4 py-2.5 rounded-lg shadow-lg whitespace-nowrap mb-2">
              <span className="text-sm font-medium">Fonctionnalité premium — disponible dans la version Pro</span>
              {/* Tail pointing down */}
              <div 
                className="absolute left-1/2 bottom-0 transform -translate-x-1/2 translate-y-full"
                style={{
                  width: 0,
                  height: 0,
                  borderLeft: '8px solid transparent',
                  borderRight: '8px solid transparent',
                  borderTop: '8px solid #3ECF8E',
                }}
              />
            </div>
          </div>
        )
      })()}
      
      {/* Upload Tooltip Bubble */}
      {showUploadTooltip && (() => {
        const buttonRef = actionButtonRef.current
        if (!buttonRef) return null
        
        const rect = buttonRef.getBoundingClientRect()
        // Position tooltip above the button, horizontally centered
        const top = rect.top - 10
        const left = rect.left + (rect.width / 2)
        
        return (
          <div
            className="fixed z-[10003] animate-fadeIn"
            style={{
              top: `${top}px`,
              left: `${left}px`,
              transform: 'translate(-50%, -100%)',
            }}
          >
            {/* Bubble with tail */}
            <div className="relative bg-[#3ECF8E] text-white px-4 py-2.5 rounded-lg shadow-lg whitespace-nowrap mb-2">
              <span className="text-sm font-medium">Fonctionnalité premium — disponible dans la version Pro</span>
              {/* Tail pointing down */}
              <div 
                className="absolute left-1/2 bottom-0 transform -translate-x-1/2 translate-y-full"
                style={{
                  width: 0,
                  height: 0,
                  borderLeft: '8px solid transparent',
                  borderRight: '8px solid transparent',
                  borderTop: '8px solid #3ECF8E',
                }}
              />
            </div>
          </div>
        )
      })()}
      
      {/* Sources Tooltip Bubble */}
      {showSourcesTooltip && (() => {
        const buttonRef = actionButtonRef.current
        if (!buttonRef) return null
        
        const rect = buttonRef.getBoundingClientRect()
        // Position tooltip above the button, horizontally centered
        const top = rect.top - 10
        const left = rect.left + (rect.width / 2)
        
        return (
          <div
            className="fixed z-[10003] animate-fadeIn"
            style={{
              top: `${top}px`,
              left: `${left}px`,
              transform: 'translate(-50%, -100%)',
            }}
          >
            {/* Bubble with tail */}
            <div className="relative bg-[#3ECF8E] text-white px-4 py-2.5 rounded-lg shadow-lg whitespace-nowrap mb-2">
              <span className="text-sm font-medium">Fonctionnalité premium — disponible dans la version Pro</span>
              {/* Tail pointing down */}
              <div 
                className="absolute left-1/2 bottom-0 transform -translate-x-1/2 translate-y-full"
                style={{
                  width: 0,
                  height: 0,
                  borderLeft: '8px solid transparent',
                  borderRight: '8px solid transparent',
                  borderTop: '8px solid #3ECF8E',
                }}
              />
            </div>
          </div>
        )
      })()}
      
      {/* Translation Tooltip Bubble */}
      {showTranslationTooltip && (() => {
        const buttonRef = actionButtonRef.current
        if (!buttonRef) return null
        
        const rect = buttonRef.getBoundingClientRect()
        // Position tooltip above the button, horizontally centered
        const top = rect.top - 10
        const left = rect.left + (rect.width / 2)
        
        return (
          <div
            className="fixed z-[10003] animate-fadeIn"
            style={{
              top: `${top}px`,
              left: `${left}px`,
              transform: 'translate(-50%, -100%)',
            }}
          >
            {/* Bubble with tail */}
            <div className="relative bg-[#3ECF8E] text-white px-4 py-2.5 rounded-lg shadow-lg whitespace-nowrap mb-2">
              <span className="text-sm font-medium">Fonctionnalité premium — disponible dans la version Pro</span>
              {/* Tail pointing down */}
              <div 
                className="absolute left-1/2 bottom-0 transform -translate-x-1/2 translate-y-full"
                style={{
                  width: 0,
                  height: 0,
                  borderLeft: '8px solid transparent',
                  borderRight: '8px solid transparent',
                  borderTop: '8px solid #3ECF8E',
                }}
              />
            </div>
          </div>
        )
      })()}
      
      {/* Serper API Limit Tooltip */}
      {showSerperLimitTooltip && (() => {
        const buttonRef = webSearchButtonRef.current
        if (!buttonRef) return null
        
        const rect = buttonRef.getBoundingClientRect()
        // Position tooltip above the button, horizontally centered
        const top = rect.top - 10
        const left = rect.left + (rect.width / 2)
        
        const errorMessage = 'Fonctionnalité premium — disponible dans la version Pro'
        
        return (
          <div
            className="fixed z-[10003] animate-fadeIn"
            style={{
              top: `${top}px`,
              left: `${left}px`,
              transform: 'translate(-50%, -100%)',
            }}
          >
            {/* Bubble with tail */}
            <div className={`relative px-4 py-2.5 rounded-lg shadow-lg whitespace-nowrap mb-2 ${
              theme === 'dark' 
                ? 'bg-[#34B27B] text-white' 
                : 'bg-[#25A26D] text-white'
            }`}>
              <span className="text-sm font-medium">{errorMessage}</span>
              {/* Tail pointing down */}
              <div 
                className="absolute left-1/2 bottom-0 transform -translate-x-1/2 translate-y-full"
                style={{
                  width: 0,
                  height: 0,
                  borderLeft: '8px solid transparent',
                  borderRight: '8px solid transparent',
                  borderTop: `8px solid ${theme === 'dark' ? '#34B27B' : '#25A26D'}`,
                }}
              />
            </div>
          </div>
        )
      })()}
      
      {/* Web Search Limit Tooltip */}
      {showWebSearchLimitTooltip && (() => {
        const buttonRef = webSearchButtonRef.current
        if (!buttonRef) return null
        
        const rect = buttonRef.getBoundingClientRect()
        // Position tooltip above the button, horizontally centered
        const top = rect.top - 10
        const left = rect.left + (rect.width / 2)
        
        const errorMessage = 'Fonctionnalité premium — Veuillez réessayer demain.'
        
        return (
          <div
            className="fixed z-[10003] animate-fadeIn"
            style={{
              top: `${top}px`,
              left: `${left}px`,
              transform: 'translate(-50%, -100%)',
            }}
          >
            {/* Bubble with tail */}
            <div className={`relative px-4 py-2.5 rounded-lg shadow-lg whitespace-nowrap mb-2 ${
              theme === 'dark' 
                ? 'bg-[#34B27B] text-white' 
                : 'bg-[#25A26D] text-white'
            }`}>
              <span className="text-sm font-medium">{errorMessage}</span>
              {/* Tail pointing down */}
              <div 
                className="absolute left-1/2 bottom-0 transform -translate-x-1/2 translate-y-full"
                style={{
                  width: 0,
                  height: 0,
                  borderLeft: '8px solid transparent',
                  borderRight: '8px solid transparent',
                  borderTop: `8px solid ${theme === 'dark' ? '#34B27B' : '#25A26D'}`,
                }}
              />
            </div>
          </div>
        )
      })()}
      
      {/* Chat Message Limit Tooltip */}
      {showChatMessageLimitTooltip && (() => {
        const buttonRef = sendButtonRef.current
        if (!buttonRef) return null
        
        const rect = buttonRef.getBoundingClientRect()
        // Position tooltip above the button, horizontally centered
        const top = rect.top - 10
        const left = rect.left + (rect.width / 2)
        
        const errorMessage = chatMessageUsage?.hoursRemaining 
          ? `Vous avez atteint la limite de 10 messages par jour. Veuillez réessayer dans ${chatMessageUsage.hoursRemaining} heure${chatMessageUsage.hoursRemaining > 1 ? 's' : ''}.`
          : 'Vous avez atteint la limite de 10 messages par jour. Veuillez réessayer demain.'
        
        return (
          <div
            className="fixed z-[10003] animate-fadeIn"
            style={{
              top: `${top}px`,
              left: `${left}px`,
              transform: 'translate(-50%, -100%)',
            }}
          >
            {/* Bubble with tail */}
            <div className={`relative px-4 py-2.5 rounded-lg shadow-lg whitespace-nowrap mb-2 ${
              theme === 'dark' 
                ? 'bg-[#34B27B] text-white' 
                : 'bg-[#25A26D] text-white'
            }`}>
              <span className="text-sm font-medium">{errorMessage}</span>
              {/* Tail pointing down */}
              <div 
                className="absolute left-1/2 bottom-0 transform -translate-x-1/2 translate-y-full"
                style={{
                  width: 0,
                  height: 0,
                  borderLeft: '8px solid transparent',
                  borderRight: '8px solid transparent',
                  borderTop: `8px solid ${theme === 'dark' ? '#34B27B' : '#25A26D'}`,
                }}
              />
            </div>
          </div>
        )
      })()}
      
    <div className={`flex min-h-screen transition-colors duration-200 ${theme === 'dark' ? 'bg-[#0D0D0D]' : 'bg-white'}`}>
      {/* Vertical Icon Bar - Only visible when sidebar is closed */}
      {!isSidebarOpen && (
      <aside className={`w-16 border-r flex flex-col items-center py-4 flex-shrink-0 select-none transition-colors duration-200 ${theme === 'dark' ? 'border-[#2C2C2C] bg-[#151515]' : 'border-gray-200'}`} style={theme === 'dark' ? { backgroundColor: '#151515' } : { backgroundColor: '#F8F9FA' }}>
        {/* Logo - Acts as menu toggle */}
        <button
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          className={`w-10 h-10 flex items-center justify-center rounded-lg transition-colors duration-200 mb-6 ${theme === 'dark' ? 'hover:bg-[#1F1F1F]' : 'hover:bg-gray-100'}`}
          title={isSidebarOpen ? "Fermer le menu" : "Ouvrir le menu"}
        >
          <img 
            src="/logo_supabase_green.png" 
            alt="Legal AI" 
            className="h-7 w-7"
          />
        </button>

        {/* Icon buttons */}
        <div className="flex flex-col items-center gap-2">
          {/* New Chat */}
          <button
            onClick={() => {
              setSelectedSidebarItem('new-chat')
              setChatMessages([])
              setCurrentChatId(null) // Clear current chat ID when starting new chat
              setCurrentVaultItemId(null) // Clear vault item ID
              setShowPromptSuggestions(true) // Show suggestions again when starting new chat
              // Clear sessionStorage when starting new chat
              sessionStorage.removeItem(SESSION_STORAGE_KEY)
              hasLoadedFromStorage.current = false // Reset flag to allow loading on next mount if needed
              // Focus the chat input after clearing messages
              requestAnimationFrame(() => {
                setTimeout(() => {
                  textareaRef.current?.focus()
                }, 100)
              })
            }}
            className={`w-10 h-10 flex items-center justify-center rounded-lg transition-colors duration-200 ${
              selectedSidebarItem === 'new-chat' 
                ? theme === 'dark' 
                  ? 'bg-[#34B27B]/20 text-[#34B27B]' 
                  : 'bg-emerald-100 text-emerald-600'
                : theme === 'dark'
                  ? 'hover:bg-[#1F1F1F] text-[#B3B3B3]'
                  : 'hover:bg-gray-100 text-gray-600'
            }`}
            title="Nouveau chat"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>

          {/* Search */}
          {/* Workflows */}
          <button
            ref={workflowsButtonRef}
            onClick={() => {
              setShowWorkflowsTooltip(true)
            }}
            className={`w-10 h-10 flex items-center justify-center rounded-lg transition-colors duration-200 relative ${
              theme === 'dark' ? 'hover:bg-[#1F1F1F] text-[#B3B3B3]' : 'hover:bg-gray-100 text-gray-600'
            }`}
            title="Workflows"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1H5a1 1 0 01-1-1v-3zM14 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1h-4a1 1 0 01-1-1v-3z" />
            </svg>
          </button>

          {/* Éditeur Avocat */}
          <button
            onClick={() => {
              if (selectedSidebarItem === 'editeur-avocat' && isEditorOpen && activePanel === 'editor') {
                setIsEditorOpen(false)
                setSelectedSidebarItem('new-chat')
              } else {
                setSelectedSidebarItem('editeur-avocat')
                setIsEditorOpen(true)
                setIsEditorContentVisible(true)
                setActivePanel('editor') // Editor becomes the active/expanded panel
                setLayoutMode('editor')
              }
            }}
            className={`w-10 h-10 flex items-center justify-center rounded-lg transition-colors duration-200 ${
              selectedSidebarItem === 'editeur-avocat'
                ? theme === 'dark'
                  ? 'bg-[#34B27B]/20 text-[#34B27B]'
                  : 'bg-emerald-100 text-emerald-600'
                : theme === 'dark'
                  ? 'hover:bg-[#1F1F1F] text-[#B3B3B3]'
                  : 'hover:bg-gray-100 text-gray-600'
            }`}
            title="Éditeur Avocat"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" opacity="0.4" />
            </svg>
          </button>

          {/* Tableur Avocat - Removed: Now integrated into Editeur Avocat as "Tableau" menu */}
          {false && (
          <button
            onClick={() => {
              if (selectedSidebarItem === 'tableur-avocat' && isTableurOpen && activePanel === 'tableur') {
                setIsTableurOpen(false)
                setSelectedSidebarItem('new-chat')
              } else {
                setSelectedSidebarItem('tableur-avocat')
                setIsTableurOpen(true)
                setActivePanel('tableur') // Tableur becomes the active/expanded panel
              }
            }}
            className={`w-10 h-10 flex items-center justify-center rounded-lg transition-colors duration-200 ${
              selectedSidebarItem === 'tableur-avocat' ? 'bg-emerald-100 text-emerald-600' : 'hover:bg-gray-100 text-gray-600'
            }`}
            title="Tableur Avocat"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          </button>
          )}

          {/* Vault */}
          <button
            onClick={() => {
              if (isVaultOpen) {
                setIsVaultOpen(false)
                setSelectedSidebarItem('new-chat')
              } else {
                setSelectedSidebarItem('vault')
                setIsVaultOpen(true)
              }
            }}
            className={`w-10 h-10 flex items-center justify-center rounded-lg transition-colors duration-200 ${
              selectedSidebarItem === 'vault'
                ? theme === 'dark'
                  ? 'bg-[#34B27B]/20 text-[#34B27B]'
                  : 'bg-emerald-100 text-emerald-600'
                : theme === 'dark'
                  ? 'hover:bg-[#1F1F1F] text-[#B3B3B3]'
                  : 'hover:bg-gray-100 text-gray-600'
            }`}
            title="Vault"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
            </svg>
          </button>
        </div>

        {/* User Profile Picture - Fixed at bottom */}
        <div className="mt-auto mb-4 flex justify-center">
          <button
            onClick={() => setIsSidebarOpen(true)}
            className={`w-10 h-10 rounded-full overflow-hidden border-2 transition-colors duration-200 flex items-center justify-center shadow-sm hover:shadow-md ${
              theme === 'dark' 
                ? 'border-[#2C2C2C] hover:border-[#34B27B] bg-[#1A1A1A]' 
                : 'border-gray-300 hover:border-emerald-500 bg-white'
            }`}
            title={currentUser?.name || currentUser?.email?.split('@')[0] || 'Utilisateur'}
          >
            {currentUser?.avatar_url && !avatarError ? (
              <img 
                src={currentUser.avatar_url} 
                alt={currentUser.name || 'User'} 
                className="w-full h-full object-cover"
                onError={() => setAvatarError(true)}
                onLoad={() => setAvatarError(false)}
                loading="lazy"
                referrerPolicy="no-referrer"
              />
            ) : (
              <div className="w-full h-full rounded-full bg-green-600 hover:bg-emerald-600 flex items-center justify-center text-white font-semibold text-sm transition-colors duration-200">
                {currentUser?.name ? currentUser.name.charAt(0).toUpperCase() : currentUser?.email ? currentUser.email.charAt(0).toUpperCase() : 'U'}
              </div>
            )}
          </button>
        </div>
      </aside>
      )}

      {/* Full Sidebar - Visible by default */}
      {isSidebarOpen && (
        <aside className={`w-64 h-screen border-r flex flex-col flex-shrink-0 select-none transition-colors duration-200 ${theme === 'dark' ? 'border-[#2C2C2C] bg-[#151515]' : 'border-gray-200'}`} style={theme === 'dark' ? { backgroundColor: '#151515' } : { backgroundColor: '#F8F9FA' }}>
          {/* Sidebar Header - Fixed at top */}
          <div className={`h-14 flex-shrink-0 flex items-center justify-between px-4 border-b transition-colors duration-200 ${theme === 'dark' ? 'border-[#2C2C2C]' : 'border-gray-200'}`}>
            <div className="flex items-center gap-2.5">
              <img 
                src="/logo_supabase_green.png" 
                alt="Legal AI" 
                className="h-6"
              />
              <span className="text-lg font-semibold tracking-tight" style={{ color: '#3ECF8E' }}>
                Avocat AI
              </span>
            </div>
            <button
              onClick={() => setIsSidebarOpen(false)}
              className={`w-8 h-8 rounded-md flex items-center justify-center transition-colors duration-200 ${
                theme === 'dark'
                  ? 'hover:bg-[#34B27B]/15 hover:text-[#34B27B] text-[#B3B3B3]'
                  : 'hover:bg-emerald-500/15 hover:text-emerald-600 text-gray-600'
              }`}
              title="Vue compacte"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <rect x="3" y="3" width="18" height="18" rx="2" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
                <path d="M9 3v18" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
              </svg>
            </button>
          </div>

          {/* Sidebar Content - Scrollable top section */}
          <div className="flex-1 min-h-0 px-2 py-3 sidebar-scrollable">
            {/* Action Menu - ChatGPT Style with Green Selection */}
            <div className="flex flex-col mb-4">
              <button 
                onClick={() => {
                  setSelectedSidebarItem('new-chat')
                  setChatMessages([])
                  setCurrentChatId(null) // Clear current chat ID when starting new chat
                  setShowPromptSuggestions(true) // Show suggestions again when starting new chat
                  // Focus the chat input after clearing messages
                  requestAnimationFrame(() => {
                    setTimeout(() => {
                      textareaRef.current?.focus()
                    }, 100)
                  })
                }}
                className={`group flex items-center gap-3 px-3 h-9 rounded-lg transition-all duration-150 ${
                  selectedSidebarItem === 'new-chat'
                    ? theme === 'dark'
                      ? 'bg-[#34B27B]/20 border-l-2 border-[#34B27B] text-[#FFFFFF] font-medium'
                      : 'bg-emerald-500/20 border-l-2 border-emerald-600 text-gray-900 font-medium'
                    : theme === 'dark'
                      ? 'text-[#B3B3B3] hover:bg-[#1F1F1F]'
                      : 'text-gray-700 hover:bg-emerald-500/10'
                }`}
              >
                <svg className={`w-[18px] h-[18px] transition-colors duration-150 ${
                  selectedSidebarItem === 'new-chat' 
                    ? theme === 'dark' ? 'text-[#34B27B]' : 'text-emerald-600'
                    : theme === 'dark' ? 'text-[#808080]' : 'text-gray-500'
                }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                <span className="text-sm">nouvelle session</span>
              </button>

              <button 
                ref={workflowsButtonFullRef}
                onClick={() => {
                  setShowWorkflowsTooltip(true)
                }}
                className={`group flex items-center gap-3 px-3 h-9 rounded-lg transition-all duration-150 relative ${
                  theme === 'dark' 
                    ? 'text-[#B3B3B3] hover:bg-[#1F1F1F]' 
                    : 'text-gray-700 hover:bg-emerald-500/10'
                }`}
              >
                <svg className={`w-[18px] h-[18px] transition-colors duration-150 ${theme === 'dark' ? 'text-[#808080]' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1H5a1 1 0 01-1-1v-3zM14 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1h-4a1 1 0 01-1-1v-3z" />
                </svg>
                <span className="text-sm">Workflows</span>
              </button>

              <button 
                onClick={() => {
                  if (selectedSidebarItem === 'editeur-avocat' && isEditorOpen && activePanel === 'editor') {
                    // If already open and active, close it
                    setIsEditorOpen(false)
                    setSelectedSidebarItem('new-chat')
                  } else {
                    // If not open or not active, open/activate it and close sidebar
                    setSelectedSidebarItem('editeur-avocat')
                    setIsEditorOpen(true)
                    setIsEditorContentVisible(true)
                    setActivePanel('editor') // Editor becomes the active/expanded panel
                    setLayoutMode('editor')
                    setIsSidebarOpen(false)
                  }
                }}
                className={`group flex items-center gap-3 px-3 h-9 rounded-lg transition-all duration-150 ${
                  selectedSidebarItem === 'editeur-avocat'
                    ? theme === 'dark'
                      ? 'bg-[#34B27B]/20 border-l-2 border-[#34B27B] text-[#FFFFFF] font-medium'
                      : 'bg-emerald-500/20 border-l-2 border-emerald-600 text-gray-900 font-medium'
                    : theme === 'dark'
                      ? 'text-[#B3B3B3] hover:bg-[#1F1F1F]'
                      : 'text-gray-700 hover:bg-emerald-500/10'
                }`}
              >
                <svg className={`w-[18px] h-[18px] transition-colors duration-150 ${
                  selectedSidebarItem === 'editeur-avocat'
                    ? theme === 'dark' ? 'text-[#34B27B]' : 'text-emerald-600'
                    : theme === 'dark' ? 'text-[#808080]' : 'text-gray-500'
                }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" opacity="0.5" />
                </svg>
                <span className="text-sm">Éditeur Avocat</span>
              </button>

              <button 
                onClick={() => {
                  if (isVaultOpen) {
                    // If already open, close it
                    setIsVaultOpen(false)
                    setSelectedSidebarItem('new-chat')
                  } else {
                    // If not open, open it and close sidebar
                    setSelectedSidebarItem('vault')
                    setIsVaultOpen(true)
                    setIsSidebarOpen(false)
                  }
                }}
                className={`group flex items-center gap-3 px-3 h-9 rounded-lg transition-all duration-150 ${
                  selectedSidebarItem === 'vault'
                    ? theme === 'dark'
                      ? 'bg-[#34B27B]/20 border-l-2 border-[#34B27B] text-[#FFFFFF] font-medium'
                      : 'bg-emerald-500/20 border-l-2 border-emerald-600 text-gray-900 font-medium'
                    : theme === 'dark'
                      ? 'text-[#B3B3B3] hover:bg-[#1F1F1F]'
                      : 'text-gray-700 hover:bg-emerald-500/10'
                }`}
              >
                <svg className={`w-[18px] h-[18px] transition-colors duration-150 ${
                  selectedSidebarItem === 'vault'
                    ? theme === 'dark' ? 'text-[#34B27B]' : 'text-emerald-600'
                    : theme === 'dark' ? 'text-[#808080]' : 'text-gray-500'
                }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                </svg>
                <span className="text-sm">Vault</span>
              </button>

              {/* Tableur Avocat - Removed: Now integrated into Editeur Avocat as "Tableau" menu */}
              {false && (
              <button 
                onClick={() => {
                  if (selectedSidebarItem === 'tableur-avocat' && isTableurOpen && activePanel === 'tableur') {
                    // If already open and active, close it
                    setIsTableurOpen(false)
                    setSelectedSidebarItem('new-chat')
                  } else {
                    // If not open or not active, open/activate it and close sidebar
                    setSelectedSidebarItem('tableur-avocat')
                    setIsTableurOpen(true)
                    setActivePanel('tableur') // Tableur becomes the active/expanded panel
                    setIsSidebarOpen(false)
                  }
                }}
                className={`group flex items-center gap-3 px-3 h-9 rounded-lg transition-all duration-150 ${
                  selectedSidebarItem === 'tableur-avocat'
                    ? 'bg-[#E6EFEA] border-l-2 border-[#25A26D] text-gray-900 font-medium'
                    : 'text-gray-700 hover:bg-[#E6EFEA]/50'
                }`}
              >
                <svg className={`w-[18px] h-[18px] transition-colors duration-150 ${selectedSidebarItem === 'tableur-avocat' ? 'text-[#25A26D]' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                <span className="text-sm">Tableur Avocat</span>
              </button>
              )}

            </div>

            {/* Chat History */}
            <div>
              <div className="space-y-0.5">
                {/* Empty - no chat conversations */}
              </div>
            </div>
          </div>

          {/* User Profile - Fixed at bottom */}
          <div className={`flex-shrink-0 border-t p-3 relative transition-colors duration-200 ${theme === 'dark' ? 'border-[#2C2C2C]' : 'border-gray-200'}`} ref={userMenuRef}>
            <button 
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              className={`group w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:pl-4 hover:shadow-sm transition-all duration-200 cursor-pointer ${
                theme === 'dark' 
                  ? 'hover:bg-[#34B27B]/15' 
                  : 'hover:bg-emerald-500/15'
              }`}
            >
              {currentUser?.avatar_url && !avatarError ? (
                <img 
                  src={currentUser.avatar_url} 
                  alt={currentUser.name || 'User'} 
                  className="w-8 h-8 rounded-full object-cover border-2 border-white shadow-sm flex-shrink-0"
                  onError={() => setAvatarError(true)}
                  onLoad={() => setAvatarError(false)}
                  loading="lazy"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-green-600 group-hover:bg-emerald-600 flex items-center justify-center text-white font-semibold text-sm transition-colors duration-200">
                  {currentUser?.name ? currentUser.name.charAt(0).toUpperCase() : currentUser?.email ? currentUser.email.charAt(0).toUpperCase() : 'U'}
                </div>
              )}
              <span className={`flex-1 text-left text-sm font-semibold transition-colors duration-200 ${
                theme === 'dark'
                  ? 'text-[#FFFFFF] group-hover:text-[#34B27B]'
                  : 'text-gray-800 group-hover:text-emerald-600'
              }`}>
                {currentUser?.name || currentUser?.email?.split('@')[0] || 'Utilisateur'}
              </span>
              <svg className={`w-4 h-4 transition-colors duration-200 ${
                theme === 'dark'
                  ? 'text-[#808080] group-hover:text-[#34B27B]'
                  : 'text-gray-500 group-hover:text-emerald-600'
              }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
              </svg>
            </button>

            {/* User Menu Dropdown */}
            {isUserMenuOpen && (
              <div className={`absolute bottom-full left-3 right-3 mb-2 rounded-lg shadow-xl border py-2 z-50 select-none transition-colors duration-200 ${
                theme === 'dark'
                  ? 'bg-[#1A1A1A] border-[#2C2C2C]'
                  : 'bg-white border-gray-200'
              }`}>
                {/* User Info */}
                <div className={`px-3 py-2.5 border-b transition-colors duration-200 ${
                  theme === 'dark' ? 'border-[#2C2C2C]' : 'border-gray-100'
                }`}>
                  <div className="flex items-center gap-2 mb-2">
                    {currentUser?.avatar_url && !avatarError ? (
                      <img 
                        src={currentUser.avatar_url} 
                        alt={currentUser.name || 'User'} 
                        className="w-8 h-8 rounded-full object-cover border border-gray-200 flex-shrink-0"
                        onError={() => setAvatarError(true)}
                        onLoad={() => setAvatarError(false)}
                        loading="lazy"
                        referrerPolicy="no-referrer"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-green-600 flex items-center justify-center text-white font-semibold text-sm">
                        {currentUser?.name ? currentUser.name.charAt(0).toUpperCase() : currentUser?.email ? currentUser.email.charAt(0).toUpperCase() : 'U'}
                      </div>
                    )}
                    <p className={`text-sm font-semibold ${theme === 'dark' ? 'text-[#FFFFFF]' : 'text-gray-900'}`}>
                      {currentUser?.name || currentUser?.email?.split('@')[0] || 'Utilisateur'}
                    </p>
                  </div>
                  {currentUser?.email && (
                    <p className={`text-xs mt-0.5 ${theme === 'dark' ? 'text-[#B3B3B3]' : 'text-gray-600'}`}>{currentUser.email}</p>
                  )}
                </div>

                {/* Menu Items */}
                <div className="py-1">
                  <button
                    onClick={() => {
                      setIsUserMenuOpen(false)
                      setShowSettings(true)
                    }}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 transition-colors duration-150 ${
                      theme === 'dark' ? 'hover:bg-[#1F1F1F]' : 'hover:bg-gray-50'
                    }`}
                  >
                    <svg className={`w-4 h-4 flex-shrink-0 ${theme === 'dark' ? 'text-[#B3B3B3]' : 'text-gray-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span className={`text-sm font-medium ${theme === 'dark' ? 'text-[#FFFFFF]' : 'text-gray-800'}`}>Paramètres</span>
                  </button>

                  <button
                    onClick={() => {
                      setIsUserMenuOpen(false)
                      setShowHelp(true)
                    }}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 transition-colors duration-150 ${
                      theme === 'dark' ? 'hover:bg-[#1F1F1F]' : 'hover:bg-gray-50'
                    }`}
                  >
                    <svg className={`w-4 h-4 flex-shrink-0 ${theme === 'dark' ? 'text-[#B3B3B3]' : 'text-gray-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className={`text-sm font-medium ${theme === 'dark' ? 'text-[#FFFFFF]' : 'text-gray-800'}`}>Aide</span>
                  </button>

                  <button
                    onClick={() => {
                      setIsUserMenuOpen(false)
                      // Show confirmation modal before signing out
                      openConfirmModal('L\'application est en mode test.\n\nEn vous déconnectant, votre compte sera supprimé et remplacé par un nouveau compte.\n\nVoulez-vous continuer ?', async () => {
                        setIsSigningOut(true)
                        // Sign out from Supabase
                        const { error } = await supabase.auth.signOut()
                        if (error) {
                          console.error('Error signing out:', error)
                          setIsSigningOut(false)
                        }
                        // The auth state change listener in App.tsx will handle redirecting to login
                      }, 'Se déconnecter')
                    }}
                    disabled={isSigningOut}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 transition-colors duration-150 border-t mt-1 ${
                      theme === 'dark' ? 'border-[#2C2C2C]' : 'border-gray-100'
                    } ${
                      isSigningOut 
                        ? 'opacity-50 cursor-not-allowed' 
                        : theme === 'dark' ? 'hover:bg-[#1F1F1F]' : 'hover:bg-gray-50'
                    }`}
                  >
                    {isSigningOut ? (
                      <>
                        <svg className={`w-4 h-4 flex-shrink-0 animate-spin ${theme === 'dark' ? 'text-[#B3B3B3]' : 'text-gray-600'}`} fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span className={`text-sm font-medium ${theme === 'dark' ? 'text-[#FFFFFF]' : 'text-gray-800'}`}>Déconnexion...</span>
                      </>
                    ) : (
                      <>
                        <svg className={`w-4 h-4 flex-shrink-0 ${theme === 'dark' ? 'text-[#B3B3B3]' : 'text-gray-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        <span className={`text-sm font-medium ${theme === 'dark' ? 'text-[#FFFFFF]' : 'text-gray-800'}`}>Se déconnecter</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </aside>
      )}

      {/* Main Content Area - Chat (order-2 to appear after editor panel which is order-1) */}
      <div className={`flex-1 flex flex-col h-screen transition-all duration-300 order-2 min-w-0 ${theme === 'dark' ? 'bg-[#0D0D0D]' : 'bg-white'}`}>
        {/* Chat content container - scrollable area */}
        <div className={`flex-1 min-h-0 flex flex-col px-4 relative main-chat-scroll ${isEditorOpen || false ? 'items-stretch justify-start pt-6' : chatMessages.length > 0 ? 'items-stretch' : 'items-center'}`}>
        
        {/* Top Right Actions */}
        <div className="absolute top-4 right-4 flex flex-col items-start gap-2">
          {/* Astuces button */}
          <button
            onClick={() => setIsGuideOpen(true)}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg transition-colors duration-200 select-none ${
              theme === 'dark'
                ? 'text-[#B3B3B3] hover:text-[#FFFFFF] hover:bg-[#1F1F1F]'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
            </svg>
            <span>Astuces</span>
          </button>

          {/* Theme Toggle */}
          <div className="flex items-center gap-2 text-sm select-none pl-6">
            <button
              onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}
              className="relative w-12 h-5 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#3ECF8E] focus:ring-offset-2"
              style={{ backgroundColor: theme === 'dark' ? '#3ECF8E' : '#d1d5db' }}
              title={theme === 'light' ? 'Passer en mode sombre' : 'Passer en mode clair'}
            >
              <span
                className="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-md transform transition-transform duration-200 flex items-center justify-center"
                style={{ transform: theme === 'dark' ? 'translateX(28px)' : 'translateX(0)' }}
              >
                {theme === 'light' ? (
                  <svg className="w-2.5 h-2.5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                ) : (
                  <svg className="w-2.5 h-2.5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                  </svg>
                )}
              </span>
            </button>
          </div>
        </div>

        {/* Guide Panel */}
        {isGuideOpen && (
          <>
            {/* Backdrop */}
            <div 
              className="fixed inset-0 bg-black bg-opacity-30 z-50"
              onClick={() => setIsGuideOpen(false)}
            />
            
            {/* Panel */}
            <div className={`fixed inset-y-0 right-0 w-full max-w-md shadow-2xl z-50 flex flex-col select-none transition-colors duration-200 ${
              theme === 'dark' ? 'bg-[#1A1A1A]' : 'bg-white'
            }`}>
              {/* Header */}
              <div className={`flex items-center justify-between px-5 py-3.5 border-b transition-colors duration-200 ${
                theme === 'dark' 
                  ? 'border-[#2C2C2C] bg-[#151515]' 
                  : 'border-gray-200 bg-gray-50'
              }`}>
                <div>
                  <h2 className={`text-sm font-semibold uppercase tracking-wide ${theme === 'dark' ? 'text-[#FFFFFF]' : 'text-gray-900'}`}>Guide d'utilisation</h2>
                  <p className={`text-xs mt-0.5 ${theme === 'dark' ? 'text-[#808080]' : 'text-gray-500'}`}>Avocat AI</p>
                </div>
                <button
                  onClick={() => setIsGuideOpen(false)}
                  className={`w-7 h-7 rounded flex items-center justify-center transition-colors duration-200 ${
                    theme === 'dark'
                      ? 'hover:bg-[#1F1F1F] text-[#808080] hover:text-[#FFFFFF]'
                      : 'hover:bg-gray-200 text-gray-400 hover:text-gray-600'
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Content */}
              <div className={`flex-1 overflow-y-auto px-5 py-5 transition-colors duration-200 ${
                theme === 'dark' ? 'bg-[#1A1A1A]' : 'bg-white'
              }`}>
                <div className="space-y-5">
                  {/* Introduction */}
                  <div className={`pb-4 mb-4 border-b transition-colors duration-200 ${
                    theme === 'dark' ? 'border-[#2C2C2C]' : 'border-gray-200'
                  }`}>
                    <p className={`text-sm leading-relaxed ${theme === 'dark' ? 'text-[#B3B3B3]' : 'text-gray-600'}`}>
                      Cette application vous permet de discuter avec un assistant intelligent pour obtenir des informations juridiques simplement et rapidement.
                      Vous pouvez poser vos questions sur les lois, les articles juridiques ou des situations légales, et recevoir des réponses claires en quelques secondes.
                      L'application peut aussi vous aider à résumer, analyser et organiser vos informations, puis à prendre des notes que vous pouvez modifier et enregistrer en toute sécurité dans votre Vault.
                      Tout est conçu pour vous faire gagner du temps et vous aider à mieux comprendre le droit, même sans connaissances juridiques ou techniques.
                    </p>
                  </div>

                  {/* Section 1 */}
                  <div className={`border-l-2 pl-3 ${theme === 'dark' ? 'border-[#34B27B]' : 'border-green-600'}`}>
                    <h3 className={`text-xs font-semibold uppercase tracking-wide mb-2 ${theme === 'dark' ? 'text-[#FFFFFF]' : 'text-gray-900'}`}>Poser une question juridique</h3>
                    <p className={`text-sm leading-snug mb-1.5 ${theme === 'dark' ? 'text-[#B3B3B3]' : 'text-gray-600'}`}>
                      Saisissez votre question dans le champ principal.
                    </p>
                    <p className={`text-sm leading-snug ${theme === 'dark' ? 'text-[#B3B3B3]' : 'text-gray-600'}`}>
                      Vous pouvez cliquer sur « Améliorer avec IA » afin d'obtenir une réponse plus précise et mieux structurée.
                    </p>
                  </div>

                  {/* Section 2 */}
                  <div className={`border-l-2 pl-3 ${theme === 'dark' ? 'border-[#34B27B]' : 'border-green-600'}`}>
                    <h3 className={`text-xs font-semibold uppercase tracking-wide mb-2 ${theme === 'dark' ? 'text-[#FFFFFF]' : 'text-gray-900'}`}>Utiliser le bouton « + »</h3>
                    <p className={`text-sm leading-snug ${theme === 'dark' ? 'text-[#B3B3B3]' : 'text-gray-600'}`}>
                      Le bouton ( + ) permet de sélectionner une action ou un workflow avant d'envoyer votre demande.
                    </p>
                  </div>

                  {/* Section 3 */}
                  <div className={`border-l-2 pl-3 ${theme === 'dark' ? 'border-[#34B27B]' : 'border-green-600'}`}>
                    <h3 className={`text-xs font-semibold uppercase tracking-wide mb-2 ${theme === 'dark' ? 'text-[#FFFFFF]' : 'text-gray-900'}`}>Rédaction de documents juridiques</h3>
                    <ul className={`space-y-1 text-sm mb-2 ${theme === 'dark' ? 'text-[#B3B3B3]' : 'text-gray-600'}`}>
                      <li className="flex items-start">
                        <span className={`mr-2 mt-0.5 ${theme === 'dark' ? 'text-[#34B27B]' : 'text-green-600'}`}>•</span>
                        <span>Sélectionnez « Analyse approfondie » pour des réponses détaillées</span>
                      </li>
                      <li className="flex items-start">
                        <span className={`mr-2 mt-0.5 ${theme === 'dark' ? 'text-[#34B27B]' : 'text-green-600'}`}>•</span>
                        <span>Indiquez le type de document souhaité</span>
                      </li>
                      <li className="flex items-start">
                        <span className={`mr-2 mt-0.5 ${theme === 'dark' ? 'text-[#34B27B]' : 'text-green-600'}`}>•</span>
                        <span>Avocat Ai génère une première version</span>
                      </li>
                    </ul>
                    <p className={`text-xs font-medium mb-1 ${theme === 'dark' ? 'text-[#808080]' : 'text-gray-500'}`}>Vous pouvez ensuite :</p>
                    <ul className={`space-y-1 text-sm ${theme === 'dark' ? 'text-[#B3B3B3]' : 'text-gray-600'}`}>
                      <li className="flex items-start">
                        <span className={`mr-2 mt-0.5 ${theme === 'dark' ? 'text-[#34B27B]' : 'text-green-600'}`}>•</span>
                        <span>Modifier le contenu dans l'éditeur</span>
                      </li>
                      <li className="flex items-start">
                        <span className={`mr-2 mt-0.5 ${theme === 'dark' ? 'text-[#34B27B]' : 'text-green-600'}`}>•</span>
                        <span>Améliorer ou reformuler le texte</span>
                      </li>
                      <li className="flex items-start">
                        <span className={`mr-2 mt-0.5 ${theme === 'dark' ? 'text-[#34B27B]' : 'text-green-600'}`}>•</span>
                        <span>Supprimer ou réécrire certaines parties</span>
                      </li>
                      <li className="flex items-start">
                        <span className={`mr-2 mt-0.5 ${theme === 'dark' ? 'text-[#34B27B]' : 'text-green-600'}`}>•</span>
                        <span>Enregistrer le document dans Vault</span>
                      </li>
                      <li className="flex items-start">
                        <span className={`mr-2 mt-0.5 ${theme === 'dark' ? 'text-[#34B27B]' : 'text-green-600'}`}>•</span>
                        <span>Exporter le document en Word ou PDF</span>
                      </li>
                    </ul>
                  </div>

                  {/* Section 4 */}
                  <div className={`border-l-2 pl-3 ${theme === 'dark' ? 'border-[#34B27B]' : 'border-green-600'}`}>
                    <h3 className={`text-xs font-semibold uppercase tracking-wide mb-2 ${theme === 'dark' ? 'text-[#FFFFFF]' : 'text-gray-900'}`}>Analyse et résumé de documents</h3>
                    <ul className={`space-y-1 text-sm ${theme === 'dark' ? 'text-[#B3B3B3]' : 'text-gray-600'}`}>
                      <li className="flex items-start">
                        <span className={`mr-2 mt-0.5 ${theme === 'dark' ? 'text-[#34B27B]' : 'text-green-600'}`}>•</span>
                        <span>Importez votre document</span>
                      </li>
                      <li className="flex items-start">
                        <span className={`mr-2 mt-0.5 ${theme === 'dark' ? 'text-[#34B27B]' : 'text-green-600'}`}>•</span>
                        <span>Nettoyez les informations sensibles si nécessaire</span>
                      </li>
                      <li className="flex items-start">
                        <span className={`mr-2 mt-0.5 ${theme === 'dark' ? 'text-[#34B27B]' : 'text-green-600'}`}>•</span>
                        <span>Lancez l'analyse</span>
                      </li>
                      <li className="flex items-start">
                        <span className={`mr-2 mt-0.5 ${theme === 'dark' ? 'text-[#34B27B]' : 'text-green-600'}`}>•</span>
                        <span>Recevez un résumé clair et structuré</span>
                      </li>
                    </ul>
                  </div>

                  {/* Section 5 */}
                  <div className={`border-l-2 pl-3 ${theme === 'dark' ? 'border-[#34B27B]' : 'border-green-600'}`}>
                    <h3 className={`text-xs font-semibold uppercase tracking-wide mb-2 ${theme === 'dark' ? 'text-[#FFFFFF]' : 'text-gray-900'}`}>Recherche juridique</h3>
                    <p className={`text-sm leading-snug ${theme === 'dark' ? 'text-[#B3B3B3]' : 'text-gray-600'}`}>
                      Décrivez la règle ou le sujet recherché.
                      Avocat Ai vous aide à identifier les articles et à comprendre leur contenu.
                    </p>
                  </div>

                  {/* Section 6 */}
                  <div className={`border-l-2 pl-3 ${theme === 'dark' ? 'border-[#34B27B]' : 'border-green-600'}`}>
                    <h3 className={`text-xs font-semibold uppercase tracking-wide mb-2 ${theme === 'dark' ? 'text-[#FFFFFF]' : 'text-gray-900'}`}>Traduction juridique</h3>
                    <p className={`text-sm leading-snug ${theme === 'dark' ? 'text-[#B3B3B3]' : 'text-gray-600'}`}>
                      Saisissez vos termes ou documents.
                      Avocat Ai fournit une traduction adaptée au vocabulaire juridique.
                    </p>
                  </div>

                  {/* Section 7 */}
                  <div className={`border-l-2 pl-3 ${theme === 'dark' ? 'border-[#34B27B]' : 'border-green-600'}`}>
                    <h3 className={`text-xs font-semibold uppercase tracking-wide mb-2 ${theme === 'dark' ? 'text-[#FFFFFF]' : 'text-gray-900'}`}>OCR et analyse d'images</h3>
                    <ul className={`space-y-1 text-sm ${theme === 'dark' ? 'text-[#B3B3B3]' : 'text-gray-600'}`}>
                      <li className="flex items-start">
                        <span className={`mr-2 mt-0.5 ${theme === 'dark' ? 'text-[#34B27B]' : 'text-green-600'}`}>•</span>
                        <span>Cliquez sur le bouton ( + )</span>
                      </li>
                      <li className="flex items-start">
                        <span className={`mr-2 mt-0.5 ${theme === 'dark' ? 'text-[#34B27B]' : 'text-green-600'}`}>•</span>
                        <span>Importez une image ou un document scanné</span>
                      </li>
                      <li className="flex items-start">
                        <span className={`mr-2 mt-0.5 ${theme === 'dark' ? 'text-[#34B27B]' : 'text-green-600'}`}>•</span>
                        <span>Avocat Ai extrait le texte et l'analyse</span>
                      </li>
                    </ul>
                  </div>

                  {/* Section 8 - Important */}
                  <div className={`border-t pt-4 mt-2 transition-colors duration-200 ${theme === 'dark' ? 'border-[#2C2C2C]' : 'border-gray-200'}`}>
                    <h3 className={`text-xs font-semibold uppercase tracking-wide mb-2 ${theme === 'dark' ? 'text-[#FFFFFF]' : 'text-gray-900'}`}>Informations importantes</h3>
                    <ul className={`space-y-1 text-sm ${theme === 'dark' ? 'text-[#B3B3B3]' : 'text-gray-600'}`}>
                      <li className="flex items-start">
                        <span className={`mr-2 mt-0.5 ${theme === 'dark' ? 'text-[#666666]' : 'text-gray-400'}`}>•</span>
                        <span>Avocat AI est un outil d'assistance</span>
                      </li>
                      <li className="flex items-start">
                        <span className={`mr-2 mt-0.5 ${theme === 'dark' ? 'text-[#666666]' : 'text-gray-400'}`}>•</span>
                        <span>Les réponses sont fournies à titre informatif et éducatif</span>
                      </li>
                      <li className="flex items-start">
                        <span className={`mr-2 mt-0.5 ${theme === 'dark' ? 'text-[#666666]' : 'text-gray-400'}`}>•</span>
                        <span>Il est recommandé de vérifier toute information avant utilisation</span>
                      </li>
                      <li className="flex items-start">
                        <span className={`mr-2 mt-0.5 ${theme === 'dark' ? 'text-[#666666]' : 'text-gray-400'}`}>•</span>
                        <span>Évitez de transmettre des données sensibles ou personnelles</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
        
        {/* Top section - Welcome or Chat Messages */}
        {isLoadingSession ? (
          // Smooth minimal loading indicator
          <div className="flex-1 flex flex-col items-center justify-center">
            <div className="flex flex-col items-center gap-4">
              {/* Minimal spinner */}
              <div className="relative w-12 h-12">
                <svg className="w-12 h-12 animate-spin text-[#25A26D]" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
              {/* Subtle loading text */}
              <p className={`text-sm select-none animate-pulse ${theme === 'dark' ? 'text-[#808080]' : 'text-gray-500'}`}>
                Chargement de la session...
              </p>
            </div>
          </div>
        ) : chatMessages.length === 0 ? (
          // Welcome Screen
          <div className="flex-1 flex flex-col items-center justify-center">
            {/* Logo */}
            <img 
              src="/logo_supabase_green.png" 
              alt="Legal AI" 
              className="h-20 w-auto mx-auto mb-4 select-none"
            />
            
            {/* Title */}
            <h1 className={`text-3xl md:text-4xl lg:text-5xl font-bold text-center mb-3 select-none ${theme === 'dark' ? 'text-[#FFFFFF]' : 'text-gray-900'}`}>
              Salut ! Je suis Avocat AI
            </h1>
            
            {/* Subtitle */}
            <p className={`text-base md:text-lg text-center select-none ${theme === 'dark' ? 'text-[#B3B3B3]' : 'text-gray-600'}`}>
              Posez votre question juridique.
            </p>
          </div>
        ) : (
          // Chat Messages Area - ChatGPT style document layout
          <div className="flex-1 pb-6">
            <div className={`mx-auto px-4 py-6 ${isEditorOpen || false ? 'max-w-full' : 'max-w-3xl'}`}>
              {chatMessages.map((message) => (
                <div
                  key={message.id}
                  className={`mb-6 ${message.sender === 'user' ? 'flex justify-end' : ''}`}
                >
                  {message.sender === 'user' ? (
                    // User message - ChatGPT-style rounded bubble in Supabase green
                    <div className="bg-[#34B27B] text-white px-4 py-2.5 rounded-2xl text-[15px] max-w-[70%]">
                      <p className="whitespace-pre-wrap leading-relaxed">{message.text}</p>
                    </div>
                  ) : message.isLoading ? (
                    // Loading message - professional grey with gradient and animation
                    <div className="flex items-start gap-3">
                      {/* Logo Avatar */}
                      <div className="flex-shrink-0">
                        <img 
                          src="/logo_supabase_green.png" 
                          alt="AI Assistant" 
                          className="w-8 h-8"
                        />
                      </div>
                      <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border max-w-fit transition-colors duration-200 ${
                        theme === 'dark'
                          ? 'bg-[#1A1A1A] border-[#2C2C2C]'
                          : 'bg-gradient-to-r from-gray-50 to-slate-50 border-gray-100'
                      }`}>
                        <div className="relative">
                          <svg className={`w-5 h-5 animate-spin ${theme === 'dark' ? 'text-[#808080]' : 'text-gray-400'}`} fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className={`text-[15px] font-medium ${theme === 'dark' ? 'text-[#B3B3B3]' : 'text-gray-600'}`}>{message.text}</span>
                          <span className="ai-thinking-dots">
                            <span className={theme === 'dark' ? 'text-[#808080]' : 'text-gray-400'} style={{ animationDelay: '0s' }}>.</span>
                            <span className={theme === 'dark' ? 'text-[#808080]' : 'text-gray-400'} style={{ animationDelay: '0.2s' }}>.</span>
                            <span className={theme === 'dark' ? 'text-[#808080]' : 'text-gray-400'} style={{ animationDelay: '0.4s' }}>.</span>
                          </span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    // AI message - Markdown rendered, conversational style
                    <div className="flex items-start gap-3">
                      {/* Logo Avatar */}
                      <div className="flex-shrink-0">
                        <img 
                          src="/logo_supabase_green.png" 
                          alt="AI Assistant" 
                          className="w-8 h-8"
                        />
                      </div>
                      <div className={`max-w-full flex-1 w-full transition-colors duration-200 ${
                        theme === 'dark' 
                          ? 'text-[#FFFFFF]' 
                          : 'text-gray-800'
                      }`}>
                      {message.isProposal && (
                        <div className={`mb-3 text-xs font-medium flex items-center gap-1.5 ${
                          theme === 'dark' ? 'text-[#34B27B]' : 'text-[#3ECF8E]'
                        }`}>
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                          </svg>
                          <span>Proposition de modification</span>
                        </div>
                      )}
                      {message.isWebSearch && (
                        <div className={`mb-3 text-xs font-medium flex items-center gap-1.5 ${
                          theme === 'dark' ? 'text-[#60A5FA]' : 'text-blue-600'
                        }`}>
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <circle cx="12" cy="12" r="10" strokeWidth={2} />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
                          </svg>
                          <span>Recherche sur le Web</span>
                        </div>
                      )}
                      {message.aiResult?.kind === 'table' && message.aiResult.table ? (
                        // Show table result in chat
                        <div className="mt-3 w-full" style={{ maxWidth: '100%', overflow: 'hidden' }}>
                          <div className={`px-4 py-3 rounded-lg mb-3 border transition-colors duration-200 ${
                            theme === 'dark'
                              ? 'bg-[#1A1A1A] border-[#2C2C2C]'
                              : 'bg-green-50 border-green-200'
                          }`}>
                            <div className={`flex items-center justify-between text-sm mb-2 ${
                              theme === 'dark' ? 'text-[#34B27B]' : 'text-green-800'
                            }`}>
                              <div className="flex items-center gap-2">
                                <svg className={`w-5 h-5 flex-shrink-0 ${theme === 'dark' ? 'text-[#34B27B]' : 'text-green-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span>✅ Tableau généré</span>
                              </div>
                              <button
                                onClick={() => downloadTableAsCSV(message.aiResult.table)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 flex items-center gap-1.5 ${
                                  theme === 'dark'
                                    ? 'bg-[#34B27B] hover:bg-[#2A9D6B] text-white'
                                    : 'bg-[#25A26D] hover:bg-[#1e8a5a] text-white'
                                }`}
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                </svg>
                                <span>Télécharger en CSV</span>
                              </button>
                            </div>
                            <div className="w-full" style={{ width: '100%', maxWidth: '100%', overflow: 'auto' }}>
                              <SpreadsheetTable
                                data={message.aiResult.table}
                                onChange={(newData) => {
                                  // Update the message's table data when user edits
                                  setChatMessages(prev => prev.map(msg => 
                                    msg.id === message.id 
                                      ? { ...msg, aiResult: { kind: 'table', table: newData } }
                                      : msg
                                  ))
                                }}
                                isAIActivated={isTableurAIActivated}
                              />
                            </div>
                          </div>
                        </div>
                      ) : (
                        // Regular text message - render markdown with background container for better visibility
                        <div className={`chat-markdown rounded-xl px-4 py-3 transition-colors duration-200 ${
                          theme === 'dark'
                            ? 'bg-[#1A1A1A] border border-[#2C2C2C] text-[#FFFFFF]'
                            : ''
                        }`}>
                          {(() => {
                            // Check if this message should stream (only new assistant messages, not errors, proposals, or loaded from storage)
                            const shouldStream = streamingMessageIds.has(message.id) && 
                                              !message.isProposal && 
                                              !message.isWebSearch &&
                                              !message.text.startsWith('⚠️');
                            
                            // Extract confidence percentage and articles from message text
                            // Match pattern: **الثقة** followed by percentage
                            const confidenceMatch = message.text.match(/\*\*الثقة\*\*\s*\n?\s*(\d+)%/);
                            // Match pattern: **الفصول القانونية:** followed by articles text (until next section or end)
                            const articlesMatch = message.text.match(/\*\*الفصول القانونية:\*\*\s*([^\n]+(?:\n(?!\*\*)[^\n]+)*)/);
                            const confidencePercent = confidenceMatch ? parseInt(confidenceMatch[1]) : null;
                            const articlesText = articlesMatch ? articlesMatch[1].trim() : null;
                            
                            // Split text to separate confidence section for rendering
                            let textBeforeConfidence = message.text;
                            let textAfterConfidence = '';
                            
                            if (confidenceMatch && confidenceMatch.index !== undefined) {
                              // Get text before confidence section
                              textBeforeConfidence = message.text.substring(0, confidenceMatch.index);
                              
                              // Find where confidence section ends (after articles or after percentage)
                              const confidenceStart = confidenceMatch.index;
                              const afterConfidence = message.text.substring(confidenceStart);
                              
                              // Try to find articles section
                              const articlesIndex = afterConfidence.indexOf('**الفصول القانونية:**');
                              if (articlesIndex !== -1) {
                                // Find end of articles section
                                const afterArticlesStart = confidenceStart + articlesIndex + '**الفصول القانونية:**'.length;
                                const afterArticles = message.text.substring(afterArticlesStart);
                                // Find next section (double newline or new bold section) or end of text
                                const nextSectionMatch = afterArticles.match(/\n\n|\n\*\*/);
                                if (nextSectionMatch && nextSectionMatch.index !== undefined) {
                                  textAfterConfidence = afterArticles.substring(nextSectionMatch.index + nextSectionMatch[0].length);
                                }
                              } else {
                                // No articles, find end after percentage
                                const percentEnd = confidenceStart + confidenceMatch[0].length;
                                const afterPercent = message.text.substring(percentEnd);
                                const nextSectionMatch = afterPercent.match(/\n\n|\n\*\*/);
                                if (nextSectionMatch && nextSectionMatch.index !== undefined) {
                                  textAfterConfidence = afterPercent.substring(nextSectionMatch.index + nextSectionMatch[0].length);
                                }
                              }
                            }
                            
                            // Preprocessing function to normalize _____ separators
                            const preprocessMarkdown = (text: string): string => {
                              // Remove all separator lines (standalone lines with only underscores)
                              return text
                                .split('\n')
                                .filter(line => {
                                  const trimmed = line.trim()
                                  // Remove lines that are only underscores (5 or more)
                                  return !/^_{5,}\s*$/.test(trimmed)
                                })
                                .join('\n')
                            }
                            
                            // Render function for markdown content
                            const renderMarkdown = (textToRender: string) => {
                              const processedText = preprocessMarkdown(textToRender)
                              return (
                                <ReactMarkdown
                                    components={{
                                      p: ({ children }) => (
                                        <p className={`text-[15px] leading-[1.8] mb-4 last:mb-0 ${
                                          theme === 'dark' ? 'text-[#FFFFFF]' : 'text-gray-700'
                                        }`}>
                                          {children}
                                        </p>
                                      ),
                                h1: ({ children }) => (
                                  <h1 className={`text-xl font-semibold mt-6 mb-3 first:mt-0 ${
                                    theme === 'dark' ? 'text-[#FFFFFF]' : 'text-gray-900'
                                  }`}>
                                    {children}
                                  </h1>
                                ),
                                h2: ({ children }) => (
                                  <h2 className={`text-lg font-semibold mt-5 mb-2.5 first:mt-0 ${
                                    theme === 'dark' ? 'text-[#FFFFFF]' : 'text-gray-900'
                                  }`}>
                                    {children}
                                  </h2>
                                ),
                                h3: ({ children }) => (
                                  <h3 className={`text-base font-semibold mt-4 mb-2 first:mt-0 ${
                                    theme === 'dark' ? 'text-[#FFFFFF]' : 'text-gray-800'
                                  }`}>
                                    {children}
                                  </h3>
                                ),
                                ul: ({ children }) => (
                                  <ul className="my-3 ml-1 space-y-2">
                                    {children}
                                  </ul>
                                ),
                                ol: ({ children }) => (
                                  <ol className="my-3 ml-1 space-y-2 list-decimal list-inside">
                                    {children}
                                  </ol>
                                ),
                                li: ({ children }) => (
                                  <li className={`text-[15px] leading-[1.7] flex items-start gap-2 ${
                                    theme === 'dark' ? 'text-[#FFFFFF]' : 'text-gray-700'
                                  }`}>
                                    <span className={`mt-1.5 flex-shrink-0 ${
                                      theme === 'dark' ? 'text-[#808080]' : 'text-gray-400'
                                    }`}>•</span>
                                    <span className="flex-1">{children}</span>
                                  </li>
                                ),
                                strong: ({ children }) => (
                                  <strong className={`font-semibold ${
                                    theme === 'dark' ? 'text-[#FFFFFF]' : 'text-gray-900'
                                  }`}>{children}</strong>
                                ),
                                em: ({ children }) => (
                                  <em className={`italic ${
                                    theme === 'dark' ? 'text-[#FFFFFF]' : 'text-gray-600'
                                  }`}>{children}</em>
                                ),
                                code: ({ children }) => (
                                  <code className={`px-1.5 py-0.5 text-sm rounded font-mono ${
                                    theme === 'dark'
                                      ? 'bg-[#1F1F1F] text-[#FFFFFF] border border-[#2C2C2C]'
                                      : 'bg-gray-100 text-gray-800'
                                  }`}>
                                    {children}
                                  </code>
                                ),
                                pre: ({ children }) => (
                                  <pre className={`my-4 p-4 rounded-lg overflow-x-auto text-sm ${
                                    theme === 'dark'
                                      ? 'bg-[#1F1F1F] text-[#FFFFFF] border border-[#2C2C2C]'
                                      : 'bg-gray-900 text-gray-100'
                                  }`}>
                                    {children}
                                  </pre>
                                ),
                                blockquote: ({ children }) => (
                                  <blockquote className={`my-4 pl-4 border-l-3 italic ${
                                    theme === 'dark'
                                      ? 'border-[#2C2C2C] text-[#B3B3B3]'
                                      : 'border-gray-300 text-gray-600'
                                  }`}>
                                    {children}
                                  </blockquote>
                                ),
                                hr: () => (
                                  <div className="my-6" style={{ 
                                    height: '0.5px', 
                                    backgroundColor: theme === 'dark' ? '#2C2C2C' : '#e5e7eb', 
                                    width: '100%' 
                                  }} />
                                ),
                                a: ({ href, children }) => (
                                  <a 
                                    href={href} 
                                    className={`font-medium transition-colors ${
                                      theme === 'dark' 
                                        ? 'text-[#34B27B] hover:text-[#3ECF8E]' 
                                        : 'text-[#3ECF8E] hover:text-[#2ba06e]'
                                    }`}
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                  >
                                    {children}
                                  </a>
                                ),
                                table: ({ children }) => (
                                  <div className="overflow-x-auto my-4">
                                    <table className={`w-full border-collapse text-sm ${
                                      theme === 'dark' ? 'border-[#2C2C2C]' : 'border-gray-300'
                                    }`}>
                                      {children}
                                    </table>
                                  </div>
                                ),
                                thead: ({ children }) => (
                                  <thead className={theme === 'dark' ? 'bg-[#1F1F1F]' : 'bg-gray-50'}>
                                    {children}
                                  </thead>
                                ),
                                tbody: ({ children }) => (
                                  <tbody>
                                    {children}
                                  </tbody>
                                ),
                                tr: ({ children }) => (
                                  <tr className={`${
                                    theme === 'dark' 
                                      ? 'border-b border-[#2C2C2C] hover:bg-[#252525]' 
                                      : 'border-b border-gray-200 hover:bg-gray-50'
                                  }`}>
                                    {children}
                                  </tr>
                                ),
                                th: ({ children }) => (
                                  <th className={`px-4 py-2 text-left font-semibold ${
                                    theme === 'dark' 
                                      ? 'text-[#FFFFFF] border-[#2C2C2C]' 
                                      : 'text-gray-900 border-gray-300'
                                  }`}>
                                    {children}
                                  </th>
                                ),
                                td: ({ children }) => (
                                  <td className={`px-4 py-2 ${
                                    theme === 'dark' 
                                      ? 'text-[#FFFFFF] border-[#2C2C2C]' 
                                      : 'text-gray-700 border-gray-300'
                                  }`}>
                                    {children}
                                  </td>
                                ),
                                    }}
                                  >
                                    {processedText}
                                  </ReactMarkdown>
                              )
                            };
                            
                            // Main render logic with typewriter effect
                            if (shouldStream) {
                              return (
                                <TypewriterText
                                  fullText={message.text}
                                  chunkSize={2}
                                  delay={50}
                                  onUpdate={() => {
                                    // Auto-scroll during streaming
                                    requestAnimationFrame(() => {
                                      chatEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
                                    })
                                  }}
                                  onComplete={() => {
                                    // Remove from streaming set when complete
                                    setStreamingMessageIds(prev => {
                                      const next = new Set(prev)
                                      next.delete(message.id)
                                      return next
                                    })
                                    // Final auto-scroll to bottom
                                    setTimeout(() => {
                                      chatEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
                                    }, 50)
                                  }}
                                >
                                  {(displayedText, isStreaming) => {
                                    // Re-extract confidence for displayed text
                                    const displayedConfidenceMatch = displayedText.match(/\*\*الثقة\*\*\s*\n?\s*(\d+)%/);
                                    const displayedArticlesMatch = displayedText.match(/\*\*الفصول القانونية:\*\*\s*([^\n]+(?:\n(?!\*\*)[^\n]+)*)/);
                                    const displayedConfidencePercent = displayedConfidenceMatch ? parseInt(displayedConfidenceMatch[1]) : null;
                                    const displayedArticlesText = displayedArticlesMatch ? displayedArticlesMatch[1].trim() : null;
                                    
                                    let displayedTextBeforeConfidence = displayedText;
                                    let displayedTextAfterConfidence = '';
                                    
                                    if (displayedConfidenceMatch && displayedConfidenceMatch.index !== undefined) {
                                      displayedTextBeforeConfidence = displayedText.substring(0, displayedConfidenceMatch.index);
                                      const displayedConfidenceStart = displayedConfidenceMatch.index;
                                      const displayedAfterConfidence = displayedText.substring(displayedConfidenceStart);
                                      const displayedArticlesIndex = displayedAfterConfidence.indexOf('**الفصول القانونية:**');
                                      if (displayedArticlesIndex !== -1) {
                                        const displayedAfterArticlesStart = displayedConfidenceStart + displayedArticlesIndex + '**الفصول القانونية:**'.length;
                                        const displayedAfterArticles = displayedText.substring(displayedAfterArticlesStart);
                                        const displayedNextSectionMatch = displayedAfterArticles.match(/\n\n|\n\*\*/);
                                        if (displayedNextSectionMatch && displayedNextSectionMatch.index !== undefined) {
                                          displayedTextAfterConfidence = displayedAfterArticles.substring(displayedNextSectionMatch.index + displayedNextSectionMatch[0].length);
                                        }
                                      } else {
                                        const displayedPercentEnd = displayedConfidenceStart + displayedConfidenceMatch[0].length;
                                        const displayedAfterPercent = displayedText.substring(displayedPercentEnd);
                                        const displayedNextSectionMatch = displayedAfterPercent.match(/\n\n|\n\*\*/);
                                        if (displayedNextSectionMatch && displayedNextSectionMatch.index !== undefined) {
                                          displayedTextAfterConfidence = displayedAfterPercent.substring(displayedNextSectionMatch.index + displayedNextSectionMatch[0].length);
                                        }
                                      }
                                    }
                                    
                                    return (
                                      <>
                                        {renderMarkdown(displayedTextBeforeConfidence)}
                                        
                                        {/* Confidence Section with Progress Bar - only show if fully streamed */}
                                        {displayedConfidencePercent !== null && !isStreaming && (
                                          <div className="my-6">
                                            <div className="mb-3">
                                              <strong className={`font-semibold ${
                                                theme === 'dark' ? 'text-[#FFFFFF]' : 'text-gray-900'
                                              }`}>الثقة</strong>
                                            </div>
                                            <div className="flex items-center gap-3 mb-1">
                                              <div className={`flex-1 rounded-full h-1 overflow-hidden ${
                                                theme === 'dark' ? 'bg-[#1F1F1F]' : 'bg-gray-100'
                                              }`} style={{ maxWidth: '600px' }}>
                                                <div
                                                  className="h-1 rounded-full transition-all duration-500 ease-out"
                                                  style={{ 
                                                    width: `${displayedConfidencePercent}%`,
                                                    backgroundColor: '#34B27B'
                                                  }}
                                                />
                                              </div>
                                              <div className="text-[15px] font-semibold" style={{ color: '#34B27B' }}>
                                                {displayedConfidencePercent}%
                                              </div>
                                            </div>
                                            
                                            {displayedArticlesText && (
                                              <div className="inline-flex items-center px-2 py-1 rounded-md" style={{ backgroundColor: '#34B27B', maxWidth: '600px' }}>
                                                <span className="text-[8px] font-medium text-white">
                                                  <strong>الفصول القانونية:</strong> {displayedArticlesText}
                                                </span>
                                              </div>
                                            )}
                                          </div>
                                        )}
                                        
                                        {displayedTextAfterConfidence && renderMarkdown(displayedTextAfterConfidence)}
                                      </>
                                    )
                                  }}
                                </TypewriterText>
                              )
                            }
                            
                            // Normal rendering (no streaming) - existing code continues
                            return (
                              <>
                                {renderMarkdown(textBeforeConfidence)}
                                
                                {/* Confidence Section with Progress Bar */}
                                {confidencePercent !== null && (
                                  <div className="my-6">
                                    <div className="mb-3">
                                      <strong className={`font-semibold ${
                                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                                      }`}>الثقة</strong>
                                    </div>
                                    {/* Progress Bar with Percentage - Thin, Longer, Modern, Professional */}
                                    <div className="flex items-center gap-3 mb-1">
                                      <div className="flex-1 bg-gray-100 rounded-full h-1 overflow-hidden" style={{ maxWidth: '600px' }}>
                                        <div
                                          className="h-1 rounded-full transition-all duration-500 ease-out"
                                          style={{ 
                                            width: `${confidencePercent}%`,
                                            backgroundColor: '#34B27B'
                                          }}
                                        />
                                      </div>
                                      <div className="text-[15px] font-semibold" style={{ color: '#34B27B' }}>
                                        {confidencePercent}%
                                      </div>
                                    </div>
                                    
                                    {/* Articles List - Small Button-like Metadata */}
                                    {articlesText && (
                                      <div className="inline-flex items-center px-2 py-1 rounded-md" style={{ backgroundColor: '#34B27B', maxWidth: '600px' }}>
                                        <span className="text-[8px] font-medium text-white">
                                          <strong>الفصول القانونية:</strong> {articlesText}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                )}
                                
                                {textAfterConfidence && renderMarkdown(textAfterConfidence)}
                              </>
                            )
                          })()}
                        </div>
                      )}
                      {message.isProposal && (
                        <div className="mt-4 flex items-center gap-2">
                          <button
                            onClick={() => {
                              console.log('🟢 BUTTON CLICKED - message.id:', message.id)
                              console.log('🟢 Message:', message)
                              handleApplyProposal(message.id)
                            }}
                            className="px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors"
                          >
                            Appliquer
                          </button>
                          <button
                            onClick={() => handleRejectProposal(message.id)}
                            className="px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
                          >
                            Rejeter
                          </button>
                        </div>
                      )}
                      {/* Confidence Score and Legal Sources */}
                      {message.confidence !== undefined && message.confidence > 0 && (
                        <div className="mt-3">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-[10px] text-gray-500">Confiance</span>
                            <span className="text-[10px] font-semibold text-[#25A26D]">
                              {Math.round(message.confidence * 100)}%
                            </span>
                          </div>
                          <div className="w-full bg-gray-100 rounded-full h-1 overflow-hidden">
                            <div
                              className="h-1 rounded-full transition-all duration-500 ease-out bg-[#25A26D]"
                              style={{ width: `${message.confidence * 100}%` }}
                            />
                          </div>
                          {message.legalSources && message.legalSources.length > 0 && (
                            <div className="mt-2">
                              <div className="flex flex-wrap gap-1">
                                {message.legalSources.map((source, idx) => (
                                  <span
                                    key={idx}
                                    className="inline-flex items-center px-1.5 py-0.5 bg-green-50 text-[#25A26D] text-[10px] font-medium rounded"
                                  >
                                    {source}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      {/* Web search sources */}
                      {message.isWebSearch && message.sources && message.sources.length > 0 && (
                        <div className={`mt-5 pt-4 overflow-hidden max-w-full w-full transition-colors duration-200 ${
                          theme === 'dark' ? 'border-t border-[#2C2C2C]' : 'border-t border-gray-100'
                        }`}>
                          <div className="flex items-center gap-2 mb-3">
                            <svg className={`w-4 h-4 transition-colors duration-200 ${
                              theme === 'dark' ? 'text-[#B3B3B3]' : 'text-gray-500'
                            }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <circle cx="12" cy="12" r="10" strokeWidth={2} />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
                            </svg>
                            <span className={`text-sm font-medium transition-colors duration-200 ${
                              theme === 'dark' ? 'text-[#FFFFFF]' : 'text-gray-600'
                            }`}>Sources</span>
                          </div>
                          <div className="space-y-2 overflow-hidden max-w-full w-full">
                            {message.sources.map((source, index) => (
                              <a
                                key={index}
                                href={source.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`block p-3 rounded-lg border transition-all group overflow-hidden max-w-full ${
                                  theme === 'dark'
                                    ? 'bg-[#1A1A1A] hover:bg-[#252525] border-[#2C2C2C] hover:border-[#34B27B]'
                                    : 'bg-gray-50 hover:bg-gray-100 border-gray-100 hover:border-gray-200'
                                }`}
                              >
                                <div className="flex items-start gap-3">
                                  <span className={`flex-shrink-0 w-5 h-5 text-xs font-medium rounded flex items-center justify-center transition-colors duration-200 ${
                                    theme === 'dark'
                                      ? 'bg-[#34B27B]/20 text-[#34B27B]'
                                      : 'bg-green-100 text-green-700'
                                  }`}>
                                    {index + 1}
                                  </span>
                                  <div className="flex-1 min-w-0 overflow-hidden w-full">
                                    <div className={`text-sm font-medium transition-colors truncate ${
                                      theme === 'dark'
                                        ? 'text-[#FFFFFF] group-hover:text-[#34B27B]'
                                        : 'text-gray-800 group-hover:text-green-700'
                                    }`}>
                                      {source.title}
                                    </div>
                                    <div className={`text-xs mt-0.5 truncate transition-colors duration-200 ${
                                      theme === 'dark' ? 'text-[#34B27B]' : 'text-green-600'
                                    }`}>
                                      {source.link}
                                    </div>
                                    {source.snippet && (
                                      <div className={`text-xs mt-1 line-clamp-2 transition-colors duration-200 ${
                                        theme === 'dark' ? 'text-[#B3B3B3]' : 'text-gray-500'
                                      }`}>
                                        {source.snippet}
                                      </div>
                                    )}
                                  </div>
                                  <svg className={`w-4 h-4 flex-shrink-0 transition-colors ${
                                    theme === 'dark'
                                      ? 'text-[#808080] group-hover:text-[#34B27B]'
                                      : 'text-gray-400 group-hover:text-green-600'
                                  }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                  </svg>
                                </div>
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Action Buttons: Copy, Like, Dislike */}
                      <div className="flex items-center gap-2 mt-3 pt-2">
                        {/* Copy Button */}
                        <button
                          onClick={async () => {
                            try {
                              await navigator.clipboard.writeText(message.text)
                              setCopiedMessageId(message.id)
                              setTimeout(() => setCopiedMessageId(null), 2000)
                            } catch (err) {
                              console.error('Failed to copy:', err)
                            }
                          }}
                          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md transition-colors duration-200 ${
                            theme === 'dark'
                              ? 'hover:bg-[#252525] text-[#808080] hover:text-[#FFFFFF]'
                              : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
                          }`}
                          title="Copier"
                        >
                          {copiedMessageId === message.id ? (
                            <>
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              <span className="text-xs">Copié</span>
                            </>
                          ) : (
                            <>
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                              <span className="text-xs">Copier</span>
                            </>
                          )}
                        </button>

                        {/* Like Button */}
                        <button
                          onClick={() => {
                            setMessageFeedback(prev => ({
                              ...prev,
                              [message.id]: prev[message.id] === 'like' ? null : 'like'
                            }))
                          }}
                          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md transition-colors duration-200 ${
                            messageFeedback[message.id] === 'like'
                              ? theme === 'dark'
                                ? 'bg-[#34B27B]/20 text-[#34B27B]'
                                : 'bg-green-100 text-green-600'
                              : theme === 'dark'
                                ? 'hover:bg-[#252525] text-[#808080] hover:text-[#FFFFFF]'
                                : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
                          }`}
                          title="J'aime"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                          </svg>
                          <span className="text-xs">J'aime</span>
                        </button>

                        {/* Dislike Button */}
                        <button
                          onClick={() => {
                            setMessageFeedback(prev => ({
                              ...prev,
                              [message.id]: prev[message.id] === 'dislike' ? null : 'dislike'
                            }))
                          }}
                          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md transition-colors duration-200 ${
                            messageFeedback[message.id] === 'dislike'
                              ? theme === 'dark'
                                ? 'bg-red-500/20 text-red-400'
                                : 'bg-red-100 text-red-600'
                              : theme === 'dark'
                                ? 'hover:bg-[#252525] text-[#808080] hover:text-[#FFFFFF]'
                                : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
                          }`}
                          title="Je n'aime pas"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14H5.236a2 2 0 01-1.789-2.894l3.5-7A2 2 0 018.736 3h4.018a2 2 0 01.485.06l3.76.94m-7 10v5a2 2 0 002 2h.096c.5 0 .905-.405.905-.904 0-.715.211-1.413.608-2.008L17 13V4m-7 10h2m5-10h2a2 2 0 012 2v6a2 2 0 01-2 2h-2.5" />
                          </svg>
                          <span className="text-xs">Je n'aime pas</span>
                        </button>
                      </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              
              {/* Loading indicator - renders like a normal AI message on the left */}
              {isChatLoading && (
                <div className="mb-6">
                  <div className="text-gray-500">
                    <div className="flex items-center gap-1 text-[15px]">
                      {loadingActionMode === 'websearch' ? (
                        <span className="shimmer-text">Recherche en cours</span>
                      ) : (
                        <span className="shimmer-text">
                          Analyse en cours
                        </span>
                      )}
                      <span className="ai-thinking-dots">
                        <span style={{ animationDelay: '0s' }}>.</span>
                        <span style={{ animationDelay: '0.2s' }}>.</span>
                        <span style={{ animationDelay: '0.4s' }}>.</span>
                      </span>
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={chatEndRef} />
            </div>
          </div>
        )}

        {/* Prompt suggestion chips - show when no messages and suggestions enabled */}
        {showPromptSuggestions && chatMessages.length === 0 && (
          <div className={`flex-shrink-0 px-4 pb-4 transition-colors duration-200 ${theme === 'dark' ? 'bg-[#0D0D0D]' : 'bg-white'}`}>
            <div className={`mx-auto ${isEditorOpen || false ? 'max-w-full' : 'max-w-[720px]'}`}>
              <div className="flex flex-wrap gap-2 justify-center">
                <button
                  onClick={() => handleSuggestionClick('هل يحق للزوجة طلب الطلاق بسبب عدم النفقة؟')}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 hover:scale-105 active:scale-95 ${
                    theme === 'dark'
                      ? 'bg-[#1F1F1F] hover:bg-[#252525] text-[#FFFFFF] border border-[#2C2C2C] hover:border-[#34B27B]/40'
                      : 'bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 hover:border-[#3ECF8E]/40'
                  }`}
                >
                  هل يحق للزوجة طلب الطلاق بسبب عدم النفقة؟
                </button>
                <button
                  onClick={() => handleSuggestionClick('متى يعتبر الشخص المفقود متوفى قانونيًا؟')}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 hover:scale-105 active:scale-95 ${
                    theme === 'dark'
                      ? 'bg-[#1F1F1F] hover:bg-[#252525] text-[#FFFFFF] border border-[#2C2C2C] hover:border-[#34B27B]/40'
                      : 'bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 hover:border-[#3ECF8E]/40'
                  }`}
                >
                  متى يعتبر الشخص المفقود متوفى قانونيًا؟
                </button>
                <button
                  onClick={() => handleSuggestionClick('ما الشروط الواجب توفرها لممارسة حق الشفعة؟')}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 hover:scale-105 active:scale-95 ${
                    theme === 'dark'
                      ? 'bg-[#1F1F1F] hover:bg-[#252525] text-[#FFFFFF] border border-[#2C2C2C] hover:border-[#34B27B]/40'
                      : 'bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 hover:border-[#3ECF8E]/40'
                  }`}
                >
                  ما الشروط الواجب توفرها لممارسة حق الشفعة؟
                </button>
                <button
                  onClick={() => handleSuggestionClick('ما هو عقد العمل وما شروطه الأساسية؟')}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 hover:scale-105 active:scale-95 ${
                    theme === 'dark'
                      ? 'bg-[#1F1F1F] hover:bg-[#252525] text-[#FFFFFF] border border-[#2C2C2C] hover:border-[#34B27B]/40'
                      : 'bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 hover:border-[#3ECF8E]/40'
                  }`}
                >
                  ما هو عقد العمل وما شروطه الأساسية؟
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Bottom section - Chat input at bottom of flex container */}
        <div className={`flex-shrink-0 pt-2 transition-colors duration-200 ${theme === 'dark' ? 'bg-[#0D0D0D]' : 'bg-white'}`}>
          <div className={`mx-auto pb-4 px-4 ${isEditorOpen || false ? 'max-w-full' : 'max-w-[720px]'}`}>
          <div className={`rounded-3xl shadow-md border p-3 transition-colors duration-200 ${
            theme === 'dark' 
              ? 'bg-[#1F1F1F] border-[#2C2C2C]' 
              : 'bg-white border-gray-200'
          }`}>
            {/* Input row */}
            <div className="flex items-end gap-2 mb-2">
              {/* Left - Add button with menu */}
              <div className="relative" ref={actionMenuRef}>
                <button
                  ref={actionButtonRef}
                  onClick={() => {
                    if (selectedActionMode) {
                      // If a mode is active, reset it
                      // If it's spreadsheet mode, also reset the AI mode
                      if (selectedActionMode === 'spreadsheet') {
                        setIsTableurAIActivated(false)
                        if (activeAIMode === 'tableur') {
                          setActiveAIMode(null)
                        }
                      }
                      setSelectedActionMode(null)
                      setIsActionMenuOpen(false)
                    } else {
                      // Otherwise, toggle the menu
                      setIsActionMenuOpen(!isActionMenuOpen)
                    }
                  }}
                  className={`w-9 h-9 flex-shrink-0 rounded-full flex items-center justify-center transition-all duration-200 ${
                    selectedActionMode
                      ? theme === 'dark'
                        ? 'bg-[#34B27B]/20 text-[#34B27B] border-2 border-[#34B27B] shadow-lg shadow-[#34B27B]/30'
                        : 'bg-green-50 text-green-600 border-2 border-green-500 shadow-lg shadow-green-500/30'
                      : theme === 'dark'
                        ? 'bg-[#1A1A1A] hover:bg-[#252525] text-[#B3B3B3] border border-[#2C2C2C]'
                        : 'bg-white hover:bg-gray-100 text-gray-600 border border-gray-300'
                  }`}
                  title={selectedActionMode || "Actions"}
                >
                  {!selectedActionMode && (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  )}
                  {selectedActionMode === 'upload' && (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                  )}
                  {selectedActionMode === 'analyze' && (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  )}
                  {selectedActionMode === 'contract' && (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  )}
                  {selectedActionMode === 'websearch' && (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="10" strokeWidth={2} />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
                    </svg>
                  )}
                  {selectedActionMode === 'research' && (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  )}
                  {selectedActionMode === 'translate' && (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                    </svg>
                  )}
                  {selectedActionMode === 'spreadsheet' && (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  )}
                </button>

                {/* Action Menu */}
                {isActionMenuOpen && (
                  <div className={`absolute bottom-full left-0 mb-2 w-64 rounded-lg shadow-xl border py-1.5 z-50 select-none transition-colors duration-200 ${
                    theme === 'dark'
                      ? 'bg-[#1A1A1A] border-[#2C2C2C]'
                      : 'bg-white border-gray-200'
                  }`}>
                    {/* Upload item - Always first */}
                    <button
                      ref={uploadButtonRef}
                      onClick={() => {
                        setShowUploadTooltip(true)
                        setIsActionMenuOpen(false)
                      }}
                      className="group w-full flex items-center gap-3 px-3 py-2.5 hover:bg-green-50 hover:pl-4 active:bg-green-100 active:border-l-2 active:border-green-600 transition-all duration-200 border-b border-gray-100 rounded-t-md cursor-pointer"
                    >
                      <svg className="w-4 h-4 text-green-600 group-hover:text-green-700 flex-shrink-0 transition-colors duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                      </svg>
                      <span className="text-sm text-gray-700 group-hover:text-green-700 font-medium transition-colors duration-200">Télécharger images / fichiers</span>
                    </button>

                    {/* Actions list */}
                    <div className="py-1">
                      <button
                        ref={createTableButtonRef}
                        onClick={handleCreateTableAction}
                        className="group w-full flex items-center gap-3 px-3 py-2.5 hover:bg-green-50 hover:pl-4 active:bg-green-100 active:border-l-2 active:border-green-600 transition-all duration-200 rounded-md cursor-pointer"
                      >
                        <svg className="w-4 h-4 text-gray-500 group-hover:text-green-600 flex-shrink-0 transition-colors duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                        <span className="text-sm text-gray-700 group-hover:text-green-600 transition-colors duration-200">Créer un tableau</span>
                      </button>

                      <button
                        ref={analyseButtonRef}
                        onClick={() => {
                          setShowAnalyseTooltip(true)
                          setIsActionMenuOpen(false)
                        }}
                        className="group w-full flex items-center gap-3 px-3 py-2.5 hover:bg-green-50 hover:pl-4 active:bg-green-100 active:border-l-2 active:border-green-600 transition-all duration-200 rounded-md cursor-pointer"
                      >
                        <svg className="w-4 h-4 text-gray-500 group-hover:text-green-600 flex-shrink-0 transition-colors duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        <span className="text-sm text-gray-700 group-hover:text-green-600 transition-colors duration-200">Analyse approfondie</span>
                      </button>

                      <button
                        ref={sourcesButtonRef}
                        onClick={() => {
                          setShowSourcesTooltip(true)
                          setIsActionMenuOpen(false)
                        }}
                        className="group w-full flex items-center gap-3 px-3 py-2.5 hover:bg-green-50 hover:pl-4 active:bg-green-100 active:border-l-2 active:border-green-600 transition-all duration-200 rounded-md cursor-pointer"
                      >
                        <svg className="w-4 h-4 text-gray-500 group-hover:text-green-600 flex-shrink-0 transition-colors duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <span className="text-sm text-gray-700 group-hover:text-green-600 transition-colors duration-200">Ajouter des sources</span>
                      </button>

                      <button
                        ref={translationButtonRef}
                        onClick={() => {
                          setShowTranslationTooltip(true)
                          setIsActionMenuOpen(false)
                        }}
                        className="group w-full flex items-center gap-3 px-3 py-2.5 hover:bg-green-50 hover:pl-4 active:bg-green-100 active:border-l-2 active:border-green-600 transition-all duration-200 rounded-md cursor-pointer"
                      >
                        <svg className="w-4 h-4 text-gray-500 group-hover:text-green-600 flex-shrink-0 transition-colors duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                        </svg>
                        <span className="text-sm text-gray-700 group-hover:text-green-600 transition-colors duration-200">Traduction juridique</span>
                      </button>

                    </div>
                  </div>
                )}
              </div>

              {/* Center - Textarea */}
              <textarea
                ref={textareaRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Écrivez votre question ici…"
                className={`flex-1 px-3 py-2 bg-transparent focus:outline-none resize-none overflow-y-auto select-text transition-colors duration-200 ${
                  theme === 'dark'
                    ? 'text-[#FFFFFF] placeholder-[#808080]'
                    : 'text-gray-900 placeholder-gray-400'
                }`}
                rows={1}
                style={{ minHeight: '36px', maxHeight: '160px' }}
              />

              {/* Right - TTS and Send buttons */}
              <div className="flex items-center gap-1 flex-shrink-0">
                {/* TTS/Voice button */}
                <button
                  onClick={async () => {
                    if (isRecording) {
                      // Stop recording
                      await stopRecording()
                    } else if (!isProcessing) {
                      // Start recording
                      await startRecording()
                    }
                  }}
                  disabled={isProcessing}
                  className={`w-9 h-9 rounded-lg flex items-center justify-center transition-all duration-200 ${
                    isRecording
                      ? theme === 'dark'
                        ? 'bg-[#34B27B] hover:bg-[#2BA66B] text-white animate-pulse'
                        : 'bg-black hover:bg-gray-900 text-white animate-pulse'
                      : isProcessing
                      ? theme === 'dark'
                        ? 'bg-[#2C2C2C] text-[#808080] cursor-not-allowed'
                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : theme === 'dark'
                        ? 'hover:bg-[#252525] text-[#808080] hover:text-[#FFFFFF]'
                        : 'hover:bg-gray-100 text-gray-600'
                  }`}
                  title={
                    isRecording
                      ? 'Arrêter l\'enregistrement'
                      : isProcessing
                      ? 'Transcription en cours...'
                      : 'Saisie vocale'
                  }
                >
                  {isProcessing ? (
                    <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                    </svg>
                  )}
                </button>

                {/* Send button */}
                <button
                  ref={sendButtonRef}
                  onClick={handleSend}
                  disabled={!inputValue.trim() || isChatLoading}
                  className={`w-9 h-9 rounded-lg flex items-center justify-center transition-all duration-200 disabled:cursor-not-allowed ${
                    theme === 'dark'
                      ? !inputValue.trim() || isChatLoading
                        ? 'bg-[#2C2C2C] text-[#808080]'
                        : 'bg-[#34B27B] hover:bg-[#2BA66B] text-white'
                      : !inputValue.trim() || isChatLoading
                        ? 'bg-gray-200 text-gray-400'
                        : 'bg-black hover:bg-gray-800 text-white'
                  }`}
                  title="Envoyer"
                >
                  {isChatLoading ? (
                    <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Voice recording indicator */}
            {isRecording && (
              <div className={`mt-2 px-3 py-2 rounded-lg text-sm flex items-center gap-2 transition-colors duration-200 ${
                theme === 'dark'
                  ? 'bg-[#1A1A1A] border-[#2C2C2C] border text-[#FFFFFF]'
                  : 'bg-gray-50 border border-gray-200 text-gray-700'
              }`}>
                <div className={`w-2 h-2 rounded-full animate-pulse ${theme === 'dark' ? 'bg-[#34B27B]' : 'bg-black'}`}></div>
                <span>Enregistrement en cours... Cliquez à nouveau pour arrêter</span>
              </div>
            )}

            {/* Action buttons */}
            <div className="flex items-center gap-2 select-none">
              <button 
                onClick={handleImproveInput}
                disabled={!isEditeurAIActivated || !inputValue.trim() || isImproving || isChatLoading}
                className={`py-1.5 px-3 rounded-lg border text-xs font-medium inline-flex items-center gap-1.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md ${
                  theme === 'dark'
                    ? 'bg-[#1F1F1F] hover:bg-[#252525] border-[#2C2C2C] hover:border-[#34B27B]/40 text-[#34B27B]'
                    : 'bg-white hover:bg-gray-50 border-gray-200 hover:border-[#3ECF8E]/40 text-[#25A26D]'
                }`}
              >
                {isImproving ? (
                  <>
                    <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Amélioration...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                    </svg>
                    <span>Améliorer avec IA</span>
                  </>
                )}
              </button>

              <button 
                onClick={handleEditWithAI}
                disabled={!chatMessages.some(msg => msg.sender === 'assistant')}
                className={`py-1.5 px-3 rounded-lg border text-xs font-medium inline-flex items-center gap-1.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md ${
                  theme === 'dark'
                    ? 'bg-[#1F1F1F] hover:bg-[#252525] border-[#2C2C2C] hover:border-[#34B27B]/40 text-[#34B27B]'
                    : 'bg-white hover:bg-gray-50 border-gray-200 hover:border-[#3ECF8E]/40 text-[#25A26D]'
                }`}
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v2M3 5h2M6 17v2m-2-2h2m5-14l1.5 4.5L17 8l-3.5 1.5L12 14l-1.5-4.5L7 8l3.5-1.5L12 3z" opacity="0.6" />
                </svg>
                <span>Notes avec IA</span>
              </button>

              <button 
                ref={webSearchButtonRef}
                onClick={() => {
                  const limitCheck = checkWebSearchLimit()
                  if (!limitCheck.canSearch) {
                    setShowWebSearchLimitTooltip(true)
                    return
                  }
                  setSelectedActionMode('websearch')
                }}
                onMouseEnter={() => {
                  // Show tooltip on hover when button is disabled
                  if (webSearchUsage !== null && !webSearchUsage.canSearch) {
                    setShowWebSearchLimitTooltip(true)
                  }
                }}
                onMouseLeave={() => {
                  // Hide tooltip when mouse leaves (with small delay for smooth UX)
                  setTimeout(() => {
                    setShowWebSearchLimitTooltip(false)
                  }, 300)
                }}
                disabled={isChatLoading || !webSearchUsage || (webSearchUsage !== null && !webSearchUsage.canSearch)}
                title={webSearchUsage !== null && !webSearchUsage.canSearch
                  ? 'Fonctionnalité premium — Veuillez réessayer demain.'
                  : undefined}
                className={`py-1.5 px-3 rounded-lg border text-xs font-medium inline-flex items-center gap-1.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md ${
                  selectedActionMode === 'websearch'
                    ? theme === 'dark'
                      ? 'bg-[#34B27B] hover:bg-[#2A9D6B] border-[#34B27B] text-white'
                      : 'bg-[#25A26D] hover:bg-[#1e8a5a] border-[#25A26D] text-white'
                    : theme === 'dark'
                      ? 'bg-[#1F1F1F] hover:bg-[#252525] border-[#2C2C2C] hover:border-[#34B27B]/40 text-[#34B27B]'
                      : 'bg-white hover:bg-gray-50 border-gray-200 hover:border-[#3ECF8E]/40 text-[#25A26D]'
                }`}
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" strokeWidth={2} />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
                </svg>
                <span>Recherche sur le Web</span>
              </button>

              <button 
                onClick={handleSaveConversation}
                disabled={chatMessages.length === 0 || isSavingConversation}
                className={`py-1.5 px-3 rounded-lg border text-xs font-medium inline-flex items-center gap-1.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md ${
                  theme === 'dark'
                    ? 'bg-[#1F1F1F] hover:bg-[#252525] border-[#2C2C2C] hover:border-[#34B27B]/40 text-[#34B27B]'
                    : 'bg-white hover:bg-gray-50 border-gray-200 hover:border-[#3ECF8E]/40 text-[#25A26D]'
                }`}
              >
                {isSavingConversation ? (
                  <>
                    <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Sauvegarde...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                    </svg>
                    <span>Sauvegarder</span>
                  </>
                )}
                    </button>
            </div>
          </div>

          {/* Disclaimer */}
          <p className="mt-3 text-center text-xs text-gray-500 px-4 select-none">
            Ce service ne constitue pas un conseil juridique. Les informations fournies sont à titre informatif et éducatif uniquement. Veuillez vérifier toute information importante.
          </p>
        </div>
        </div>
      </div>
      </div>

    {/* Settings Modal Overlay */}
    {showSettings && (
      <Settings 
        onClose={() => setShowSettings(false)} 
        language={language}
        onLanguageChange={setLanguage}
        user={currentUser}
        onDeleteAllHistory={handleDeleteAllHistory}
        openConfirmModal={openConfirmModal}
        openDeleteAccountModal={openDeleteAccountModal}
        theme={theme}
        onThemeChange={setTheme}
      />
    )}

    {/* Help Modal Overlay */}
    {showHelp && <Help onClose={() => setShowHelp(false)} theme={theme} onOpenGuide={() => setIsGuideOpen(true)} />}

    {/* Editor/Vault Panel Container - Part of flex flow */}
    {(isEditorOpen || isVaultOpen) && (
      <div className={`w-[40%] flex-shrink-0 h-screen border-r overflow-hidden flex flex-col order-1 transition-colors duration-200 ${
        theme === 'dark'
          ? 'border-[#2C2C2C] bg-[#0D0D0D]'
          : 'border-gray-200 bg-white'
      }`}>
        
        {/* Editor Panel */}
        {isEditorOpen && isEditorContentVisible && (
          <div 
            className={`flex flex-col transition-all duration-300 editor-panel-scrollable ${
              theme === 'dark' ? 'bg-[#0D0D0D]' : 'bg-white'
            } ${
              (isTableurOpen && activePanel !== 'editor')
                ? `flex-shrink-0 border-b order-2 overflow-hidden ${
                    theme === 'dark' ? 'border-[#2C2C2C]' : 'border-gray-200'
                  }` 
                : 'flex-1 min-h-0 order-1'
            }`}
            style={(isTableurOpen && activePanel !== 'editor') ? { height: '40px', maxHeight: '40px', minHeight: '40px' } : {}}
          >
            {/* Menu Bar - Always visible */}
            <div 
              onClick={() => {
                if (isTableurOpen && activePanel !== 'editor') {
                  setActivePanel('editor')
                }
              }}
              className={`border-b px-4 py-1.5 flex items-center gap-1 relative transition-colors duration-200 ${
                theme === 'dark'
                  ? 'bg-[#151515] border-[#2C2C2C]'
                  : 'bg-gray-50 border-gray-200'
              } ${
                (isTableurOpen && activePanel !== 'editor')
                  ? theme === 'dark'
                    ? 'cursor-pointer hover:bg-[#1F1F1F]'
                    : 'cursor-pointer hover:bg-gray-100'
                  : ''
              }`}
            >
                {!isEditorNavCollapsed && (
                  <>
                    <button 
                      onClick={() => setActiveMenuButton('Fichier')}
                      className={`px-3 py-1 text-sm rounded transition-colors ${
                        activeMenuButton === 'Fichier' 
                          ? theme === 'dark'
                            ? 'bg-[#34B27B] text-white'
                            : 'bg-[#25A26D] text-white'
                          : theme === 'dark'
                            ? 'text-[#B3B3B3] hover:bg-[#1F1F1F]'
                            : 'text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Fichier
                    </button>
                    <button 
                      onClick={() => setActiveMenuButton('Édition')}
                      className={`px-3 py-1 text-sm rounded transition-colors ${
                        activeMenuButton === 'Édition' 
                          ? theme === 'dark'
                            ? 'bg-[#34B27B] text-white'
                            : 'bg-[#25A26D] text-white'
                          : theme === 'dark'
                            ? 'text-[#B3B3B3] hover:bg-[#1F1F1F]'
                            : 'text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Édition
                    </button>
                    <button 
                      onClick={() => setActiveMenuButton('Aide')}
                      className={`px-3 py-1 text-sm rounded transition-colors ${
                        activeMenuButton === 'Aide' 
                          ? theme === 'dark'
                            ? 'bg-[#34B27B] text-white'
                            : 'bg-[#25A26D] text-white'
                          : theme === 'dark'
                            ? 'text-[#B3B3B3] hover:bg-[#1F1F1F]'
                            : 'text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Aide
                    </button>
                    <button 
                      onClick={() => setActiveMenuButton('Enregistrer')}
                      className={`px-3 py-1 text-sm rounded transition-colors ${
                        activeMenuButton === 'Enregistrer' 
                          ? theme === 'dark'
                            ? 'bg-[#34B27B] text-white'
                            : 'bg-[#25A26D] text-white'
                          : theme === 'dark'
                            ? 'text-[#B3B3B3] hover:bg-[#1F1F1F]'
                            : 'text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Enregistrer
                    </button>
                  </>
                )}
                <div className="flex items-center gap-2 ml-auto">
                  <button 
                    onClick={(e) => {
                      e.stopPropagation()
                      setIsEditorNavCollapsed(!isEditorNavCollapsed)
                    }}
                    className={`px-3 py-1 text-sm rounded transition-colors ${
                      theme === 'dark'
                        ? 'text-[#B3B3B3] hover:bg-[#1F1F1F]'
                        : 'text-gray-700 hover:bg-gray-200'
                    }`}
                    title={isEditorNavCollapsed ? "Afficher la navigation" : "Masquer la navigation"}
                  >
                    {isEditorNavCollapsed ? (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                    )}
                  </button>
                  {/* Minimize/Restore Button - Only show if tableur panel is also open */}
                  {isTableurOpen && (
                    <button 
                      onClick={(e) => {
                        e.stopPropagation()
                        // Toggle: if editor is active, switch to tableur panel
                        if (activePanel === 'editor') {
                          setActivePanel('tableur')
                        } else {
                          setActivePanel('editor')
                        }
                      }}
                      className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors duration-150 ${
                        theme === 'dark' ? 'hover:bg-[#1F1F1F]' : 'hover:bg-gray-100'
                      }`}
                      title={activePanel === 'editor' ? "Réduire" : "Agrandir"}
                    >
                      <span className={`text-[10px] font-mono leading-none transition-colors duration-200 ${
                        theme === 'dark' ? 'text-[#808080]' : 'text-gray-600'
                      }`}>__</span>
                    </button>
                  )}
                  {/* Close Button */}
                  <button 
                    onClick={(e) => {
                      e.stopPropagation()
                      setIsEditorOpen(false)
                      setIsEditorContentVisible(true)
                      // If closing editor, make tableur panel active if open
                      if (isTableurOpen) setActivePanel('tableur')
                    }}
                    className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors duration-150 ${
                      theme === 'dark' ? 'hover:bg-[#1F1F1F]' : 'hover:bg-gray-100'
                    }`}
                    title="Fermer"
                  >
                    <svg className={`w-3.5 h-3.5 transition-colors duration-200 ${
                      theme === 'dark' ? 'text-[#34B27B]' : 'text-[#25A26D]'
                    }`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

            {/* Editor Content - Hidden when minimized (when tableur panel is open and active) */}
            {!(isTableurOpen && activePanel !== 'editor') && (
            <div className="flex flex-col flex-1 overflow-hidden" style={{ minHeight: 0 }}>
              {/* Toolbar - Formatting and Controls */}
              {!isEditorNavCollapsed && (
              <div className={`border-b px-4 py-2 flex items-center justify-between gap-3 flex-wrap transition-colors duration-200 ${
                theme === 'dark'
                  ? 'bg-[#151515] border-[#2C2C2C]'
                  : 'bg-white border-gray-200'
              }`}>
              {/* Left side: Dynamic Content based on active menu */}
              {activeMenuButton === 'Fichier' ? (
                // Fichier Menu - Split Layout (Word/Notion style)
                <div className="w-full flex items-center px-4 py-3 gap-4">
                  {/* Left side: Document Actions - Horizontal, 25% width */}
                  <div className="flex flex-row gap-2 w-1/4">
                    {/* Import from Vault - Green UI */}
                    <button 
                      onClick={() => {
                        setIsVaultOpen(true)
                        setSelectedSidebarItem('vault')
                        // Fetch vault items if not already loaded
                        if (vaultItems.length === 0) {
                          fetchVaultItems()
                        }
                      }}
                      className={`group flex flex-col items-center gap-1 px-2 py-2.5 border rounded transition-all duration-150 w-full ${
                        theme === 'dark'
                          ? 'border-[#34B27B] hover:border-[#3ECF8E] hover:bg-[#34B27B]/10 bg-[#34B27B]/5'
                          : 'border-[#25A26D] hover:border-[#1e8a5a] hover:bg-[#3ECF8E]/10 bg-[#3ECF8E]/5'
                      }`}
                    >
                      <svg className={`w-5 h-5 transition-colors duration-200 ${
                        theme === 'dark'
                          ? 'text-[#34B27B] group-hover:text-[#3ECF8E]'
                          : 'text-[#25A26D] group-hover:text-[#1e8a5a]'
                      }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                      </svg>
                      <span className={`text-xs font-medium transition-colors duration-200 ${
                        theme === 'dark' ? 'text-[#34B27B]' : 'text-[#25A26D]'
                      }`}>Importer depuis Vault</span>
                    </button>
                  </div>

                  {/* Right side: Icon Actions - 75% width */}
                  <div className="flex items-center gap-3 flex-1">
                    <div className={`w-px h-12 mx-2 transition-colors duration-200 ${
                      theme === 'dark' ? 'bg-[#2C2C2C]' : 'bg-gray-300'
                    }`}></div>

                    {/* Rename/Name File */}
                    <button 
                      onClick={() => {
                        openRenameModal(
                          activeDocument.title,
                          (newTitle) => {
                            setDocuments(prev =>
                              prev.map(doc =>
                                doc.id === activeDocumentId
                                  ? { ...doc, title: newTitle.trim() }
                                  : doc
                              )
                            )
                          },
                          'Renommer le document'
                        )
                      }}
                      className={`flex flex-col items-center gap-1 px-3 py-2.5 rounded transition-colors group ${
                        theme === 'dark'
                          ? 'hover:bg-[#1F1F1F]'
                          : 'hover:bg-gray-100'
                      }`}
                      title="Renommer le document"
                    >
                      <svg className={`w-5 h-5 transition-colors duration-200 ${
                        theme === 'dark'
                          ? 'text-[#B3B3B3] group-hover:text-[#FFFFFF]'
                          : 'text-gray-600 group-hover:text-gray-900'
                      }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      <span className={`text-xs transition-colors duration-200 ${
                        theme === 'dark'
                          ? 'text-[#B3B3B3] group-hover:text-[#FFFFFF]'
                          : 'text-gray-600 group-hover:text-gray-900'
                      }`}>Renommer</span>
                    </button>

                    {/* Delete Document */}
                    <button 
                      onClick={() => {
                        if (documents.length <= 1) {
                          // Can't delete the last document, just reset it
                          openConfirmModal(
                            'Êtes-vous sûr de vouloir réinitialiser ce document ?',
                            () => {
                              setDocuments(prev =>
                                prev.map(doc =>
                                  doc.id === activeDocumentId
                                    ? { ...doc, title: 'Nouveau document', content: defaultDocumentContent }
                                    : doc
                                )
                              )
                              if (editor) {
                                editor.commands.setContent(defaultDocumentContent)
                              }
                            },
                            'Réinitialiser'
                          )
                        } else {
                          openConfirmModal(
                            'Êtes-vous sûr de vouloir supprimer ce document ?',
                            () => {
                              // Switch to another document before deleting
                              const otherDoc = documents.find(doc => doc.id !== activeDocumentId)
                              if (otherDoc) {
                                setActiveDocumentId(otherDoc.id)
                              }
                              // Delete the document
                              setDocuments(prev => prev.filter(doc => doc.id !== activeDocumentId))
                            },
                            'Supprimer'
                          )
                        }
                      }}
                      className={`flex flex-col items-center gap-1 px-3 py-2.5 rounded transition-colors group ${
                        theme === 'dark'
                          ? 'hover:bg-red-900/20'
                          : 'hover:bg-red-50'
                      }`}
                      title="Supprimer le document"
                    >
                      <svg className={`w-5 h-5 transition-colors duration-200 ${
                        theme === 'dark'
                          ? 'text-[#B3B3B3] group-hover:text-red-400'
                          : 'text-gray-600 group-hover:text-red-600'
                      }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      <span className={`text-xs transition-colors duration-200 ${
                        theme === 'dark'
                          ? 'text-[#B3B3B3] group-hover:text-red-400'
                          : 'text-gray-600 group-hover:text-red-600'
                      }`}>Supprimer</span>
                    </button>
                  </div>
                </div>
              ) : activeMenuButton === 'Enregistrer' ? (
                // Enregistrer Menu - Three column layout
                <div className="w-full flex items-center px-4 py-3 gap-4">
                  {/* Section: Enregistrer */}
                  <div className="flex flex-col items-center gap-0.5">
                    <span className={`text-[10px] font-medium mb-0.5 transition-colors duration-200 ${
                      theme === 'dark' ? 'text-[#808080]' : 'text-gray-400'
                    }`}>Enregistrer</span>
                    <button 
                      onClick={async () => {
                        if (!editor || isSavingToVault) return
                        
                        setIsSavingToVault(true)
                        setVaultSuccess(null) // Clear any previous success message
                        try {
                          // Get editor content
                          const editorContent = editor.getHTML()
                          
                          // Get document title
                          const title = activeDocument.title || 'Document sans titre'
                          
                          // Calculate metadata
                          const textContent = editorContent.replace(/<[^>]*>/g, '') // Strip HTML tags
                          const metadata = {
                            characterCount: textContent.length,
                            wordCount: textContent.split(/\s+/).filter(word => word.length > 0).length
                          }
                          
                          // Save to vault
                          const vaultItemId = await saveToVault('document', editorContent, title, metadata)
                          
                          if (vaultItemId) {
                            // Success - show success message
                            console.log('Document saved to vault:', vaultItemId)
                            setVaultError(null) // Clear any previous errors
                            setSaveLoadError(null) // Clear any previous toast errors
                            setVaultSuccess({
                              type: 'save',
                              message: 'Document sauvegardé avec succès dans le Vault'
                            })
                            // Auto-dismiss success message after 3 seconds
                            setTimeout(() => {
                              setVaultSuccess(null)
                            }, 3000)
                          } else {
                            // Error already set in saveToVault, check if it's a timeout
                            console.warn('Failed to save document to vault')
                            setVaultSuccess(null) // Clear success message on error
                            
                            // Timeout errors are logged but not shown to user to avoid annoying notifications
                            // Removed timeout error notification display
                            // setTimeout(() => {
                            //   if (vaultError && vaultError.type === 'save' && vaultError.category === 'timeout') {
                            //     setSaveLoadError({
                            //       message: 'Chargement interrompu. Cela est généralement dû à une instabilité du réseau Internet. Veuillez vérifier votre connexion et réessayer.',
                            //       retryable: true,
                            //       onRetry: async () => {
                            //         if (!editor || isSavingToVault) return
                            //         setIsSavingToVault(true)
                            //         setSaveLoadError(null)
                            //         try {
                            //           const editorContent = editor.getHTML()
                            //           const title = activeDocument.title || 'Document sans titre'
                            //           const textContent = editorContent.replace(/<[^>]*>/g, '')
                            //           const metadata = {
                            //             characterCount: textContent.length,
                            //             wordCount: textContent.split(/\s+/).filter(word => word.length > 0).length
                            //           }
                            //           const vaultItemId = await saveToVault('document', editorContent, title, metadata)
                            //           if (vaultItemId) {
                            //             setVaultError(null)
                            //             setVaultSuccess({
                            //               type: 'save',
                            //               message: 'Document sauvegardé avec succès dans le Vault'
                            //             })
                            //             setTimeout(() => setVaultSuccess(null), 3000)
                            //           }
                            //         } catch (error) {
                            //           const errorInfo = categorizeError(error)
                            //           setVaultError({
                            //             type: 'save',
                            //             category: errorInfo.category,
                            //             message: `Erreur lors de la sauvegarde: ${errorInfo.message}`,
                            //             retryable: errorInfo.retryable
                            //           })
                            //         } finally {
                            //           setIsSavingToVault(false)
                            //         }
                            //       }
                            //     })
                            //   }
                            // }, 0)
                          }
                        } catch (error) {
                          console.error('Error saving document to vault:', error)
                          const errorInfo = categorizeError(error)
                          setVaultError({
                            type: 'save',
                            category: errorInfo.category,
                            message: `Erreur lors de la sauvegarde: ${errorInfo.message}`,
                            retryable: errorInfo.retryable
                          })
                        } finally {
                          setIsSavingToVault(false)
                        }
                      }}
                      disabled={!editor || isSavingToVault}
                      className={`group flex flex-col items-center gap-1 px-4 py-2.5 border rounded transition-all duration-150 ${
                        theme === 'dark'
                          ? 'border-[#34B27B] hover:border-[#3ECF8E] hover:bg-[#34B27B]/10 bg-[#34B27B]/5'
                          : 'border-[#25A26D] hover:border-[#1e8a5a] hover:bg-[#3ECF8E]/10 bg-[#3ECF8E]/5'
                      } ${
                        isSavingToVault 
                          ? 'opacity-50 cursor-not-allowed' 
                          : ''
                      }`}
                    >
                      {isSavingToVault ? (
                        <svg className={`w-5 h-5 animate-spin transition-colors duration-200 ${
                          theme === 'dark' ? 'text-[#34B27B]' : 'text-[#25A26D]'
                        }`} fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      ) : (
                        <svg className={`w-5 h-5 transition-colors duration-200 ${
                          theme === 'dark'
                            ? 'text-[#34B27B] group-hover:text-[#3ECF8E]'
                            : 'text-[#25A26D] group-hover:text-[#1e8a5a]'
                        }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                        </svg>
                      )}
                      <span className={`text-xs font-medium transition-colors duration-200 ${
                        theme === 'dark' ? 'text-[#34B27B]' : 'text-[#25A26D]'
                      }`}>{isSavingToVault ? 'Sauvegarde...' : 'Vault'}</span>
                    </button>
                  </div>

                  <div className={`w-px h-12 transition-colors duration-200 ${
                    theme === 'dark' ? 'bg-[#2C2C2C]' : 'bg-gray-300'
                  }`}></div>

                  {/* Section: Télécharger */}
                  <div className="flex flex-col items-center gap-0.5">
                    <span className={`text-[10px] font-medium mb-0.5 transition-colors duration-200 ${
                      theme === 'dark' ? 'text-[#808080]' : 'text-gray-400'
                    }`}>Télécharger</span>
                    <div className="flex gap-2">
                      <button 
                        onClick={() => {
                          if (!editor) return
                          
                          // Get plain text content from editor
                          const textContent = editor.getText()
                          
                          // Get document title or use default
                          const title = activeDocument.title || 'Document sans titre'
                          
                          // Create a blob with the text content
                          const blob = new Blob([textContent], { type: 'text/plain;charset=utf-8' })
                          
                          // Create a download link
                          const url = URL.createObjectURL(blob)
                          const link = document.createElement('a')
                          link.href = url
                          link.download = `${title}.txt`
                          
                          // Trigger download
                          document.body.appendChild(link)
                          link.click()
                          
                          // Cleanup
                          document.body.removeChild(link)
                          URL.revokeObjectURL(url)
                        }}
                        className={`group flex flex-col items-center gap-1 px-4 py-2.5 border rounded transition-all duration-150 ${
                          theme === 'dark'
                            ? 'border-[#2C2C2C] hover:border-[#3C3C3C] hover:bg-[#1F1F1F] bg-[#1A1A1A]'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 bg-white'
                        }`}
                      >
                        {/* Download Icon */}
                        <svg className={`w-5 h-5 transition-colors duration-200 ${
                          theme === 'dark'
                            ? 'text-[#B3B3B3] group-hover:text-[#FFFFFF]'
                            : 'text-gray-600 group-hover:text-gray-900'
                        }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        <span className={`text-xs font-medium transition-colors duration-200 ${
                          theme === 'dark' ? 'text-[#FFFFFF]' : 'text-gray-900'
                        }`}>TXT</span>
                      </button>
                    </div>
                  </div>

                </div>
              ) : activeMenuButton === 'Aide' ? (
                // Aide Menu - Simple help options
                <div className="w-full flex items-center px-4 py-3 gap-4">
                  <button 
                    onClick={() => setShowHelp(true)}
                    className={`group flex flex-col items-center gap-1 px-4 py-2.5 border rounded transition-all duration-150 ${
                      theme === 'dark'
                        ? 'border-[#2C2C2C] hover:border-[#3C3C3C] hover:bg-[#1F1F1F] bg-[#1A1A1A]'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 bg-white'
                    }`}
                  >
                    <svg className={`w-5 h-5 transition-colors duration-200 ${
                      theme === 'dark'
                        ? 'text-[#B3B3B3] group-hover:text-[#FFFFFF]'
                        : 'text-gray-600 group-hover:text-gray-900'
                    }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                    <span className={`text-xs font-medium transition-colors duration-200 ${
                      theme === 'dark' ? 'text-[#FFFFFF]' : 'text-gray-900'
                    }`}>Guide</span>
                  </button>

                  <button 
                    onClick={() => setShowHelp(true)}
                    className={`group flex flex-col items-center gap-1 px-4 py-2.5 border rounded transition-all duration-150 ${
                      theme === 'dark'
                        ? 'border-[#2C2C2C] hover:border-[#3C3C3C] hover:bg-[#1F1F1F] bg-[#1A1A1A]'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 bg-white'
                    }`}
                  >
                    <svg className={`w-5 h-5 transition-colors duration-200 ${
                      theme === 'dark'
                        ? 'text-[#B3B3B3] group-hover:text-[#FFFFFF]'
                        : 'text-gray-600 group-hover:text-gray-900'
                    }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className={`text-xs font-medium transition-colors duration-200 ${
                      theme === 'dark' ? 'text-[#FFFFFF]' : 'text-gray-900'
                    }`}>Support</span>
                  </button>

                  <button 
                    onClick={() => setShowHelp(true)}
                    className={`group flex flex-col items-center gap-1 px-4 py-2.5 border rounded transition-all duration-150 ${
                      theme === 'dark'
                        ? 'border-[#2C2C2C] hover:border-[#3C3C3C] hover:bg-[#1F1F1F] bg-[#1A1A1A]'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 bg-white'
                    }`}
                  >
                    <svg className={`w-5 h-5 transition-colors duration-200 ${
                      theme === 'dark'
                        ? 'text-[#B3B3B3] group-hover:text-[#FFFFFF]'
                        : 'text-gray-600 group-hover:text-gray-900'
                    }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className={`text-xs font-medium transition-colors duration-200 ${
                      theme === 'dark' ? 'text-[#FFFFFF]' : 'text-gray-900'
                    }`}>FAQ</span>
                  </button>
                </div>
              ) : (
              <div className="flex items-center gap-1 flex-wrap">
                {activeMenuButton === 'Édition' ? (
                  // Édition Menu Content
                  <>
                    {/* Font Family Selector */}
                    <select
                      value={fontFamily}
                      onChange={(e) => {
                        const newFont = e.target.value
                        setFontFamily(newFont)
                        editor?.chain().focus().setFontFamily(newFont).run()
                      }}
                      className={`px-3 py-1 text-sm border rounded focus:outline-none focus:ring-2 transition-all duration-200 ${
                        theme === 'dark'
                          ? 'border-[#2C2C2C] bg-[#1A1A1A] text-[#FFFFFF] hover:bg-[#1F1F1F] focus:ring-[#34B27B]'
                          : 'border-gray-300 hover:bg-gray-50 focus:ring-[#3ECF8E]'
                      }`}
                    >
                      <option value="Times New Roman">Times New Roman</option>
                      <option value="Arial">Arial</option>
                      <option value="Georgia">Georgia</option>
                    </select>

                    {/* Font Size Selector */}
                    <select
                      value={fontSize}
                      onChange={(e) => {
                        const newSize = parseInt(e.target.value)
                        setFontSize(newSize)
                        // Apply font size to selected text only
                        if (editor) {
                          editor.chain().focus().setMark('textStyle', { fontSize: `${newSize}px` }).run()
                        }
                      }}
                      className={`px-3 py-1 text-sm border rounded focus:outline-none focus:ring-2 ml-2 transition-all duration-200 ${
                        theme === 'dark'
                          ? 'border-[#2C2C2C] bg-[#1A1A1A] text-[#FFFFFF] hover:bg-[#1F1F1F] focus:ring-[#34B27B]'
                          : 'border-gray-300 hover:bg-gray-50 focus:ring-[#3ECF8E]'
                      }`}
                    >
                      <option value="10">10</option>
                      <option value="12">12</option>
                      <option value="14">14</option>
                      <option value="16">16</option>
                      <option value="18">18</option>
                      <option value="20">20</option>
                    </select>

                    <div className={`w-px h-6 mx-2 transition-colors duration-200 ${
                      theme === 'dark' ? 'bg-[#2C2C2C]' : 'bg-gray-300'
                    }`}></div>

                    {/* Text Formatting Section */}
                    <button onClick={() => editor?.chain().focus().toggleBold().run()} className={`p-1.5 rounded transition-colors duration-200 ${
                      theme === 'dark' ? 'hover:bg-[#1F1F1F]' : 'hover:bg-gray-100'
                    }`} title="Bold">
                      <svg className={`w-4 h-4 transition-colors duration-200 ${
                        theme === 'dark' ? 'text-[#B3B3B3]' : 'text-gray-700'
                      }`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 4h8a4 4 0 014 4 4 4 0 01-4 4H6z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 12h9a4 4 0 014 4 4 4 0 01-4 4H6z"/></svg>
                    </button>
                    <button onClick={() => editor?.chain().focus().toggleItalic().run()} className={`p-1.5 rounded transition-colors duration-200 ${
                      theme === 'dark' ? 'hover:bg-[#1F1F1F]' : 'hover:bg-gray-100'
                    }`} title="Italic">
                      <svg className={`w-4 h-4 transition-colors duration-200 ${
                        theme === 'dark' ? 'text-[#B3B3B3]' : 'text-gray-700'
                      }`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 4h4M8 20h8M14 4L10 20"/></svg>
                    </button>
                    <button onClick={() => editor?.chain().focus().toggleUnderline?.().run()} className={`p-1.5 rounded transition-colors duration-200 ${
                      theme === 'dark' ? 'hover:bg-[#1F1F1F]' : 'hover:bg-gray-100'
                    }`} title="Underline">
                      <svg className={`w-4 h-4 transition-colors duration-200 ${
                        theme === 'dark' ? 'text-[#B3B3B3]' : 'text-gray-700'
                      }`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 20h14M7 4v8a5 5 0 0010 0V4"/></svg>
                    </button>
                    <button onClick={() => editor?.chain().focus().toggleStrike().run()} className={`p-1.5 rounded transition-colors duration-200 ${
                      theme === 'dark' ? 'hover:bg-[#1F1F1F]' : 'hover:bg-gray-100'
                    }`} title="Strikethrough">
                      <svg className={`w-4 h-4 transition-colors duration-200 ${
                        theme === 'dark' ? 'text-[#B3B3B3]' : 'text-gray-700'
                      }`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12h18M9 5h6M7 19h10"/></svg>
                    </button>

                    <div className={`w-px h-6 mx-2 transition-colors duration-200 ${
                      theme === 'dark' ? 'bg-[#2C2C2C]' : 'bg-gray-300'
                    }`}></div>

                    {/* Alignment Section */}
                    <button onClick={() => editor?.chain().focus().setTextAlign('left').run()} className={`p-1.5 rounded transition-colors duration-200 ${
                      theme === 'dark' ? 'hover:bg-[#1F1F1F]' : 'hover:bg-gray-100'
                    }`} title="Align Left">
                      <svg className={`w-4 h-4 transition-colors duration-200 ${
                        theme === 'dark' ? 'text-[#B3B3B3]' : 'text-gray-700'
                      }`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h10M4 18h16"/></svg>
                    </button>
                    <button onClick={() => editor?.chain().focus().setTextAlign('center').run()} className={`p-1.5 rounded transition-colors duration-200 ${
                      theme === 'dark' ? 'hover:bg-[#1F1F1F]' : 'hover:bg-gray-100'
                    }`} title="Center">
                      <svg className={`w-4 h-4 transition-colors duration-200 ${
                        theme === 'dark' ? 'text-[#B3B3B3]' : 'text-gray-700'
                      }`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M7 12h10M4 18h16"/></svg>
                    </button>
                    <button onClick={() => editor?.chain().focus().setTextAlign('right').run()} className={`p-1.5 rounded transition-colors duration-200 ${
                      theme === 'dark' ? 'hover:bg-[#1F1F1F]' : 'hover:bg-gray-100'
                    }`} title="Align Right">
                      <svg className={`w-4 h-4 transition-colors duration-200 ${
                        theme === 'dark' ? 'text-[#B3B3B3]' : 'text-gray-700'
                      }`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M10 12h10M4 18h16"/></svg>
                    </button>

                    <div className={`w-px h-6 mx-2 transition-colors duration-200 ${
                      theme === 'dark' ? 'bg-[#2C2C2C]' : 'bg-gray-300'
                    }`}></div>

                    {/* Line Spacing Section */}
                    <span className={`text-xs font-medium mr-1 transition-colors duration-200 ${
                      theme === 'dark' ? 'text-[#FFFFFF]' : 'text-gray-600'
                    }`}>Ligne :</span>
                    <button 
                      onClick={() => setLineSpacing(1.0)}
                      className={`px-2 py-1 text-xs rounded border transition-all duration-200 ${
                        theme === 'dark'
                          ? lineSpacing === 1.0
                            ? 'bg-[#1F1F1F] border-[#34B27B] text-[#FFFFFF]'
                            : 'border-[#2C2C2C] hover:bg-[#1F1F1F] hover:border-[#34B27B]/50 text-[#FFFFFF]'
                          : lineSpacing === 1.0
                            ? 'bg-gray-200 border-gray-300'
                            : 'hover:bg-gray-100 border-gray-300'
                      }`}
                    >
                      1.0
                    </button>
                    <button 
                      onClick={() => setLineSpacing(1.5)}
                      className={`px-2 py-1 text-xs rounded border transition-all duration-200 ${
                        theme === 'dark'
                          ? lineSpacing === 1.5
                            ? 'bg-[#1F1F1F] border-[#34B27B] text-[#FFFFFF]'
                            : 'border-[#2C2C2C] hover:bg-[#1F1F1F] hover:border-[#34B27B]/50 text-[#FFFFFF]'
                          : lineSpacing === 1.5
                            ? 'bg-gray-200 border-gray-300'
                            : 'hover:bg-gray-100 border-gray-300'
                      }`}
                    >
                      1.5
                    </button>
                    <button 
                      onClick={() => setLineSpacing(2.0)}
                      className={`px-2 py-1 text-xs rounded border transition-all duration-200 ${
                        theme === 'dark'
                          ? lineSpacing === 2.0
                            ? 'bg-[#1F1F1F] border-[#34B27B] text-[#FFFFFF]'
                            : 'border-[#2C2C2C] hover:bg-[#1F1F1F] hover:border-[#34B27B]/50 text-[#FFFFFF]'
                          : lineSpacing === 2.0
                            ? 'bg-gray-200 border-gray-300'
                            : 'hover:bg-gray-100 border-gray-300'
                      }`}
                    >
                      2.0
                    </button>

                    {/* AI Action Buttons - Always visible */}
                    <div className={`w-px h-6 mx-1 transition-colors duration-200 ${
                      theme === 'dark' ? 'bg-[#2C2C2C]' : 'bg-gray-300'
                    }`}></div>

                    {/* Reformuler Button */}
                    <button 
                      onClick={handleReformuler}
                      disabled={!isSelectionLocked}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md transition-all duration-200 bg-white border ${
                        isSelectionLocked 
                          ? 'border-gray-300 hover:border-gray-400 hover:bg-gray-50 text-gray-700' 
                          : 'border-gray-200 text-gray-400 cursor-not-allowed'
                      }`}
                      title={isSelectionLocked ? "Reformuler le texte sélectionné" : "Sélectionnez et validez du texte d'abord"}
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      <span className="text-xs font-medium">Reformuler</span>
                    </button>

                    {/* Analyser Button */}
                    <button 
                      onClick={handleAnalyser}
                      disabled={!isSelectionLocked}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md transition-all duration-200 bg-white border ${
                        isSelectionLocked 
                          ? 'border-gray-300 hover:border-gray-400 hover:bg-gray-50 text-gray-700' 
                          : 'border-gray-200 text-gray-400 cursor-not-allowed'
                      }`}
                      title={isSelectionLocked ? "Analyser le texte sélectionné" : "Sélectionnez et validez du texte d'abord"}
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                      </svg>
                      <span className="text-xs font-medium">Analyser</span>
                    </button>

                    {/* Résumer Button */}
                    <button 
                      onClick={handleResumer}
                      disabled={!isSelectionLocked}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md transition-all duration-200 bg-white border ${
                        isSelectionLocked 
                          ? 'border-gray-300 hover:border-gray-400 hover:bg-gray-50 text-gray-700' 
                          : 'border-gray-200 text-gray-400 cursor-not-allowed'
                      }`}
                      title={isSelectionLocked ? "Résumer le texte sélectionné" : "Sélectionnez et validez du texte d'abord"}
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span className="text-xs font-medium">Résumer</span>
                    </button>
                  </>
                ) : (
                  // Default Toolbar Content (for other menu items)
                  <>
                    <button
                      onClick={() => editor?.chain().focus().toggleBold().run()}
                      className={`p-2 rounded hover:bg-gray-100 transition-colors ${editor?.isActive('bold') ? 'bg-gray-200' : ''}`}
                      title="Gras"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 4h8a4 4 0 014 4 4 4 0 01-4 4H6z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 12h9a4 4 0 014 4 4 4 0 01-4 4H6z" />
                      </svg>
                    </button>
                    
                    <button
                      onClick={() => editor?.chain().focus().toggleItalic().run()}
                      className={`p-2 rounded hover:bg-gray-100 transition-colors ${editor?.isActive('italic') ? 'bg-gray-200' : ''}`}
                      title="Italique"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 4h4M8 20h8M14 4L10 20" />
                      </svg>
                    </button>

                    <button
                      onClick={() => editor?.chain().focus().toggleStrike().run()}
                      className={`p-2 rounded hover:bg-gray-100 transition-colors ${editor?.isActive('strike') ? 'bg-gray-200' : ''}`}
                      title="Barré"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12h18M9 5h6M7 19h10" />
                      </svg>
                    </button>

                    <div className="w-px h-6 bg-gray-300 mx-1"></div>

                    <button
                      onClick={() => editor?.chain().focus().toggleHeading({ level: 1 }).run()}
                      className={`px-2 py-1 rounded hover:bg-gray-100 transition-colors text-sm font-semibold ${editor?.isActive('heading', { level: 1 }) ? 'bg-gray-200' : ''}`}
                      title="Titre 1"
                    >
                      H1
                    </button>

                    <button
                      onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
                      className={`px-2 py-1 rounded hover:bg-gray-100 transition-colors text-sm font-semibold ${editor?.isActive('heading', { level: 2 }) ? 'bg-gray-200' : ''}`}
                      title="Titre 2"
                    >
                      H2
                    </button>

                    <button
                      onClick={() => editor?.chain().focus().toggleHeading({ level: 3 }).run()}
                      className={`px-2 py-1 rounded hover:bg-gray-100 transition-colors text-sm font-semibold ${editor?.isActive('heading', { level: 3 }) ? 'bg-gray-200' : ''}`}
                      title="Titre 3"
                    >
                      H3
                    </button>

                    {/* AI Action Buttons - Only visible when AI is activated */}
                    {isEditeurAIActivated && (
                      <>
                        <div className={`w-px h-6 mx-2 transition-colors duration-200 ${
                          theme === 'dark' ? 'bg-[#2C2C2C]' : 'bg-gray-300'
                        }`}></div>
                        
                        <button
                          className={`px-3 py-1.5 rounded-md text-white text-xs font-medium transition-colors duration-200 ${
                            theme === 'dark'
                              ? 'bg-[#1F1F1F] hover:bg-[#252525]'
                              : 'bg-gray-900 hover:bg-gray-800'
                          }`}
                          title="Extraire les points clés"
                        >
                          Points clés
                        </button>

                        <button
                          className={`px-3 py-1.5 rounded-md text-white text-xs font-medium transition-colors duration-200 ${
                            theme === 'dark'
                              ? 'bg-[#1F1F1F] hover:bg-[#252525]'
                              : 'bg-gray-900 hover:bg-gray-800'
                          }`}
                          title="Nettoyer le texte"
                        >
                          Nettoyer
                        </button>

                        <button
                          onClick={handleReformuler}
                          disabled={!isSelectionLocked}
                          className={`px-3 py-1.5 rounded-md text-white text-xs font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                            theme === 'dark'
                              ? 'bg-[#1F1F1F] hover:bg-[#252525]'
                              : 'bg-gray-900 hover:bg-gray-800'
                          }`}
                          title="Reformuler le texte sélectionné"
                        >
                          Reformuler
                        </button>

                        <button
                          onClick={handleResumer}
                          disabled={!isSelectionLocked}
                          className={`px-3 py-1.5 rounded-md text-white text-xs font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                            theme === 'dark'
                              ? 'bg-[#1F1F1F] hover:bg-[#252525]'
                              : 'bg-gray-900 hover:bg-gray-800'
                          }`}
                          title="Résumer le texte sélectionné"
                        >
                          Résumer
                        </button>
                      </>
                    )}
                  </>
                )}
              {/* Right side: Token Counter, AI Activation Button, and Close Button */}
              {activeMenuButton !== 'Fichier' && activeMenuButton !== 'Enregistrer' && activeMenuButton !== 'Aide' && (
              <div className="flex items-center gap-4">
                {/* Token Counter - Minimal & Professional */}
                <div className="flex items-center gap-1.5 px-2 py-1">
                  <span className={`text-xs transition-colors duration-200 ${
                    theme === 'dark' ? 'text-[#808080]' : 'text-gray-500'
                  }`}>
                    Utilisation IA: <span className="font-mono">{selectedCharCount.toLocaleString()}</span> / <span className="font-mono">10K</span> tokens
                    {selectedCharCount >= MAX_SELECTION_CHARS && (
                      <span className="ml-1.5 text-red-500">⚠</span>
                    )}
                  </span>
                </div>

                {/* Activer Avocat AI Button */}
                <button 
                  onClick={handleEditeurActivateAI}
                  disabled={isChatLoading || isEditeurAIActivated}
                  className={`px-2.5 py-1 text-white text-xs rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                    theme === 'dark'
                      ? 'bg-[#34B27B] hover:bg-[#2A9D6B]'
                      : 'bg-[#25A26D] hover:bg-[#1e8a5a]'
                  }`}
                >
                  {isChatLoading ? 'Chargement...' : (isEditeurAIActivated ? 'Avocat AI activé' : 'Activer Avocat AI')}
                </button>

                {/* Toggle - Only shows when AI is activated */}
                {isEditeurAIActivated && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleEditeurAIToggle}
                      className={`relative inline-flex h-5 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                        theme === 'dark'
                          ? 'bg-[#34B27B] focus:ring-[#34B27B]'
                          : 'bg-[#25A26D] focus:ring-gray-400'
                      }`}
                    >
                      <span className="inline-block h-4 w-4 transform rounded-full bg-white transition-transform translate-x-6" />
                    </button>
                    <svg className={`w-4 h-4 transition-colors duration-200 ${
                      theme === 'dark' ? 'text-[#808080]' : 'text-gray-600'
                    }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                  </div>
                )}

              </div>
              )}
              </div>
              )}
            </div>
            )}

              {/* Document Tabs */}
              <div className={`tabs-scrollable flex-shrink-0 border-b px-2 py-1 flex items-center gap-1 overflow-x-auto overflow-y-hidden flex-nowrap transition-colors duration-200 ${
                theme === 'dark'
                  ? 'bg-[#151515] border-[#2C2C2C]'
                  : 'bg-gray-100 border-gray-200'
              }`}>
                {documents.map((doc) => (
                  <div
                    key={doc.id}
                    className={`group relative flex items-center gap-2 px-3 py-1.5 rounded-t text-sm transition-colors cursor-pointer select-none flex-1 min-w-[90px] max-w-[180px] ${
                      doc.id === activeDocumentId
                        ? theme === 'dark'
                          ? 'bg-[#0D0D0D] text-[#FFFFFF] font-medium'
                          : 'bg-white text-gray-900 font-medium'
                        : theme === 'dark'
                          ? 'bg-[#1F1F1F] text-[#B3B3B3] hover:bg-[#252525]'
                          : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                    }`}
                    onClick={() => switchToDocument(doc.id)}
                  >
                    <span className="flex-1 truncate">{doc.title}</span>
                    {documents.length > 1 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          closeDocument(doc.id)
                        }}
                        className={`opacity-0 group-hover:opacity-100 rounded p-0.5 transition-opacity flex-shrink-0 ${
                          theme === 'dark'
                            ? 'hover:bg-[#252525]'
                            : 'hover:bg-gray-300'
                        }`}
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
                <button
                  onClick={createNewDocument}
                  disabled={documents.length >= 5}
                  className={`p-1.5 rounded transition-colors flex-shrink-0 ${
                    documents.length >= 5 
                      ? 'opacity-40 cursor-not-allowed' 
                      : theme === 'dark'
                        ? 'hover:bg-[#1F1F1F]'
                        : 'hover:bg-gray-200'
                  }`}
                  title={documents.length >= 5 ? "Maximum 5 documents" : "Nouveau document"}
                >
                  <svg className={`w-4 h-4 transition-colors duration-200 ${
                    theme === 'dark' ? 'text-[#808080]' : 'text-gray-600'
                  }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </button>
              </div>

              {/* Floating Selection Bar - Minimal, positioned near selection */}
              {isEditeurAIActivated && showSelectionBar && (
                <div 
                  ref={selectionBarRef} 
                  className={`fixed z-[9999] flex items-center gap-1 px-1 py-0.5 border rounded shadow-md transition-colors duration-200 ${
                    theme === 'dark'
                      ? 'bg-[#1A1A1A] border-[#2C2C2C]'
                      : 'bg-white border-gray-300'
                  }`}
                  style={{ 
                    top: `${selectionBarPosition.top}px`, 
                    left: `${selectionBarPosition.left}px`,
                    transform: 'translateX(-50%)'
                  }}
                >
                  {!isSelectionLocked ? (
                    <>
                      <button
                        onClick={handleValidateSelection}
                        disabled={selectedCharCount >= MAX_SELECTION_CHARS}
                        className={`px-2 py-1 text-xs font-medium text-white rounded transition-colors disabled:cursor-not-allowed ${
                          theme === 'dark'
                            ? 'bg-[#1F1F1F] hover:bg-[#252525] disabled:bg-[#1F1F1F] disabled:opacity-50'
                            : 'bg-gray-900 hover:bg-black disabled:bg-gray-400'
                        }`}
                        title={selectedCharCount >= MAX_SELECTION_CHARS ? `Sélection trop longue (${selectedCharCount} caractères). Maximum : 9999 caractères.` : ''}
                      >
                        Valider
                      </button>
                      <button
                        onClick={handleCancelSelection}
                        className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                          theme === 'dark'
                            ? 'text-[#FFFFFF] bg-[#1F1F1F] hover:bg-[#252525]'
                            : 'text-gray-700 bg-gray-100 hover:bg-gray-200'
                        }`}
                      >
                        Annuler
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={handleCancelSelection}
                      className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                        theme === 'dark'
                          ? 'text-[#FFFFFF] bg-[#1F1F1F] hover:bg-[#252525]'
                          : 'text-gray-700 bg-gray-100 hover:bg-gray-200'
                      }`}
                    >
                      Annuler
                    </button>
                  )}
                </div>
              )}

              {/* Editor Content - Scrollable Area with absolute positioning */}
              {(
                <div className="relative flex-1" style={{ minHeight: 0 }}>
                  <div className={`absolute inset-0 editor-scrollable transition-colors duration-200 ${
                    theme === 'dark' ? 'bg-[#0D0D0D]' : 'bg-white'
                  }`} style={{ overflowY: 'scroll', overflowX: 'hidden' }}>
                    <EditorContent 
                      editor={editor} 
                      className={`tiptap-editor prose prose-sm max-w-none p-6 break-words ${isEditeurAIActivated ? 'ai-activated' : ''} ${isSelectionLocked ? 'selection-locked' : ''} ${
                        theme === 'dark' ? 'prose-invert' : ''
                      }`}
                      style={{ lineHeight: lineSpacing, fontFamily: fontFamily }}
                    />
                  </div>
                </div>
              )}

            </div>
            )}
          </div>
        )}

        {/* Vault Modal */}
        {isVaultOpen && (
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center"
            onClick={(e) => {
              // Close modal when clicking backdrop
              if (e.target === e.currentTarget) {
                setIsVaultOpen(false)
                setSelectedSidebarItem('new-chat')
              }
            }}
          >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity" />
            
            {/* Modal Content */}
            <div 
              className={`relative rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden transition-colors duration-200 ${
                theme === 'dark' ? 'bg-[#1A1A1A]' : 'bg-white'
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className={`flex items-center justify-between px-6 py-4 border-b transition-colors duration-200 ${
                theme === 'dark'
                  ? 'border-[#2C2C2C] bg-[#1A1A1A]'
                  : 'border-gray-200 bg-white'
              }`}>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors duration-200 ${
                    theme === 'dark' ? 'bg-[#34B27B]/20' : 'bg-[#25A26D]/10'
                  }`}>
                    <svg className={`w-6 h-6 transition-colors duration-200 ${
                      theme === 'dark' ? 'text-[#34B27B]' : 'text-[#25A26D]'
                    }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                    </svg>
                  </div>
                  <div>
                    <h2 className={`text-xl font-semibold transition-colors duration-200 ${
                      theme === 'dark' ? 'text-[#FFFFFF]' : 'text-gray-900'
                    }`}>Vault</h2>
                    <p className={`text-sm transition-colors duration-200 ${
                      theme === 'dark' ? 'text-[#B3B3B3]' : 'text-gray-500'
                    }`}>Importez, stockez et analysez vos documents</p>
                  </div>
                  {!isOnline && (
                    <div className={`flex items-center gap-1.5 px-2.5 py-1 border rounded-md text-xs transition-colors duration-200 ${
                      theme === 'dark'
                        ? 'bg-red-900/20 border-red-500/40 text-red-300'
                        : 'bg-red-50 border-red-200 text-red-700'
                    }`}>
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829a4.978 4.978 0 01-1.414-2.83m-1.414 5.658a9 9 0 01-2.167-9.238m7.824 2.167a1 1 0 111.414 1.414m-1.414-1.414L3 3m8.293 8.293l1.414 1.414" />
                      </svg>
                      <span className="font-medium">Hors ligne</span>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => {
                    setIsVaultOpen(false)
                    setSelectedSidebarItem('new-chat')
                  }}
                  className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors duration-200 ${
                    theme === 'dark'
                      ? 'hover:bg-[#1F1F1F] text-[#808080] hover:text-[#FFFFFF]'
                      : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
                  }`}
                  title="Fermer"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              {/* Scrollable content area */}
              <div className={`flex-1 overflow-y-auto transition-colors duration-200 ${
                theme === 'dark' ? 'bg-[#0D0D0D]' : 'bg-gray-50'
              }`}>

                {/* Success Toast Notification for Save */}
                {vaultSuccess && vaultSuccess.type === 'save' && (
                  <div className="px-6 pt-4">
                    <div className={`border rounded-lg p-4 transition-colors duration-200 ${
                      theme === 'dark'
                        ? 'bg-[#34B27B]/20 border-[#34B27B]/40'
                        : 'bg-green-50 border-green-200'
                    }`}>
                      <div className="flex items-start gap-3">
                        <svg 
                          className={`w-5 h-5 flex-shrink-0 mt-0.5 transition-colors duration-200 ${
                            theme === 'dark' ? 'text-[#34B27B]' : 'text-green-600'
                          }`} 
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div className="flex-1 min-w-0">
                          <h3 className={`text-sm font-semibold mb-1 transition-colors duration-200 ${
                            theme === 'dark' ? 'text-[#34B27B]' : 'text-green-900'
                          }`}>
                            Sauvegarde réussie
                          </h3>
                          <p className={`text-sm transition-colors duration-200 ${
                            theme === 'dark' ? 'text-[#B3B3B3]' : 'text-green-700'
                          }`}>
                            {vaultSuccess.message}
                          </p>
                        </div>
                        <button
                          onClick={() => setVaultSuccess(null)}
                          className={`transition-colors flex-shrink-0 p-1 rounded ${
                            theme === 'dark'
                              ? 'text-[#34B27B] hover:text-[#3ECF8E] hover:bg-[#34B27B]/20'
                              : 'text-green-600 hover:text-green-800 hover:bg-green-200'
                          }`}
                          aria-label="Fermer"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Error Toast Notifications for Save and Load */}
                {vaultError && (vaultError.type === 'save' || vaultError.type === 'load') && (
                  <div className="px-6 pt-4">
                    <div className={`border rounded-lg p-4 transition-colors duration-200 ${
                      vaultError.type === 'save' 
                        ? theme === 'dark'
                          ? 'bg-orange-900/20 border-orange-500/40'
                          : 'bg-orange-50 border-orange-200'
                        : theme === 'dark'
                          ? 'bg-red-900/20 border-red-500/40'
                          : 'bg-red-50 border-red-200'
                    }`}>
                      <div className="flex items-start gap-3">
                        <svg 
                          className={`w-5 h-5 flex-shrink-0 mt-0.5 transition-colors duration-200 ${
                            vaultError.type === 'save' 
                              ? theme === 'dark' ? 'text-orange-400' : 'text-orange-600'
                              : theme === 'dark' ? 'text-red-400' : 'text-red-600'
                          }`} 
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <div className="flex-1 min-w-0">
                          <h3 className={`text-sm font-semibold mb-1 transition-colors duration-200 ${
                            vaultError.type === 'save' 
                              ? theme === 'dark' ? 'text-orange-300' : 'text-orange-900'
                              : theme === 'dark' ? 'text-red-300' : 'text-red-900'
                          }`}>
                            {vaultError.type === 'save' ? 'Erreur de sauvegarde' : 'Erreur de chargement'}
                          </h3>
                          <p className={`text-sm mb-3 transition-colors duration-200 ${
                            vaultError.type === 'save' 
                              ? theme === 'dark' ? 'text-orange-200' : 'text-orange-700'
                              : theme === 'dark' ? 'text-red-200' : 'text-red-700'
                          }`}>
                            {vaultError.message}
                          </p>
                          {vaultError.retryable && (
                            <button
                              onClick={() => {
                                if (vaultError.type === 'save') {
                                  // For save errors, clear error and let user try saving again
                                  setVaultError(null)
                                } else if (vaultError.type === 'load') {
                                  // For load errors, we need to know which item to retry
                                  // This is handled by individual item retry buttons
                                  setVaultError(null)
                                }
                              }}
                              disabled={isSavingToVault || loadingVaultItemId !== null || !isOnline}
                              title={!isOnline ? 'Vous êtes hors ligne' : ''}
                              className={`px-3 py-1.5 text-xs font-medium rounded transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                                vaultError.type === 'save'
                                  ? 'bg-orange-600 hover:bg-orange-700 text-white'
                                  : 'bg-red-600 hover:bg-red-700 text-white'
                              }`}
                            >
                              {(isSavingToVault || loadingVaultItemId !== null) ? (
                                <>
                                  <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                  <span>Nouvelle tentative...</span>
                                </>
                              ) : (
                                <>
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                  </svg>
                                  <span>Réessayer</span>
                                </>
                              )}
                            </button>
                          )}
                        </div>
                        <button
                          onClick={() => setVaultError(null)}
                          className={`flex-shrink-0 p-1 rounded transition-colors ${
                            vaultError.type === 'save' 
                              ? theme === 'dark'
                                ? 'text-orange-400 hover:bg-orange-900/30'
                                : 'text-orange-600 hover:bg-orange-200'
                              : theme === 'dark'
                                ? 'text-red-400 hover:bg-red-900/30'
                                : 'text-red-600 hover:bg-red-200'
                          }`}
                          title="Fermer"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Action Cards Section */}
                <div className="px-6 pt-6 pb-4">
                  {/* Create project card */}
                  <div 
                    ref={notesAtScaleButtonRef}
                    className={`border rounded-xl p-5 hover:shadow-lg transition-all cursor-pointer ${
                      theme === 'dark'
                        ? 'bg-gradient-to-r from-[#34B27B]/10 to-[#34B27B]/10 border-[#34B27B]/20'
                        : 'bg-gradient-to-r from-[#25A26D]/10 to-[#3ECF8E]/10 border-[#25A26D]/20'
                    }`}
                    onClick={() => {
                      setShowNotesAtScaleTooltip(true)
                    }}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        theme === 'dark' ? 'bg-[#34B27B]' : 'bg-[#25A26D]'
                      }`}>
                        <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2L13.09 8.26L19 9L13.09 9.74L12 16L10.91 9.74L5 9L10.91 8.26L12 2Z" />
                          <path d="M19 15L19.5 17.5L22 18L19.5 18.5L19 21L18.5 18.5L16 18L18.5 17.5L19 15Z" />
                          <path d="M5 15L5.5 17.5L8 18L5.5 18.5L5 21L4.5 18.5L2 18L4.5 17.5L5 15Z" />
                          <circle cx="12" cy="12" r="1.5" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <h3 className={`text-base font-semibold mb-1 transition-colors duration-200 ${
                          theme === 'dark' ? 'text-[#FFFFFF]' : 'text-gray-900'
                        }`}>Résumer toutes les notes</h3>
                        <p className={`text-sm transition-colors duration-200 ${
                          theme === 'dark' ? 'text-[#B3B3B3]' : 'text-gray-600'
                        }`}>L'IA examine toutes vos questions et réponses pour en tirer une vue d'ensemble</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Navigation Tabs and Search Bar */}
                <div className={`px-6 pb-4 flex items-center justify-between border-b transition-colors duration-200 ${
                  theme === 'dark' ? 'border-[#2C2C2C]' : 'border-gray-200'
                }`}>
                  <div className="flex gap-2">
                    <button className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                      theme === 'dark'
                        ? 'text-[#FFFFFF] bg-[#1F1F1F] hover:bg-[#252525]'
                        : 'text-gray-900 bg-gray-100 hover:bg-gray-200'
                    }`}>
                      Tous les projets
                    </button>
                  </div>
                  <div className="relative w-64">
                    <svg className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 transition-colors duration-200 ${
                      theme === 'dark' ? 'text-[#808080]' : 'text-gray-400'
                    }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input
                      type="text"
                      placeholder="Rechercher..."
                      value={vaultSearchQuery}
                      onChange={(e) => setVaultSearchQuery(e.target.value)}
                      className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent text-sm transition-all ${
                        theme === 'dark'
                          ? 'bg-[#1F1F1F] border-[#2C2C2C] text-[#FFFFFF] placeholder-[#808080] focus:ring-[#34B27B]'
                          : 'bg-white border-gray-200 focus:ring-[#25A26D]'
                      }`}
                    />
                  </div>
                </div>

                {/* Project Cards Grid - Scrollable area */}
                <div className="px-6 py-6">
                {vaultError && vaultError.type === 'fetch' ? (
                  // Simple error state
                  <div className="flex flex-col items-center justify-center py-16">
                    <div className="text-center max-w-md">
                      <svg className="w-16 h-16 text-red-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <p className={`text-base mb-4 transition-colors duration-200 ${
                        theme === 'dark' ? 'text-[#B3B3B3]' : 'text-gray-700'
                      }`}>{vaultError.message}</p>
                      {vaultError.retryable && (
                        <button
                          onClick={() => fetchVaultItems()}
                          disabled={isLoadingVaultItems || !isOnline}
                          className={`px-4 py-2 text-sm font-medium hover:underline disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline transition-colors ${
                            theme === 'dark'
                              ? 'text-[#34B27B] hover:text-[#3ECF8E]'
                              : 'text-[#25A26D] hover:text-[#1e8a5a]'
                          }`}
                          title={!isOnline ? 'Vous êtes hors ligne' : ''}
                        >
                          {isLoadingVaultItems ? 'Chargement...' : 'Réessayer'}
                        </button>
                      )}
                    </div>
                    {vaultItems.length > 0 && (
                      <p className={`text-xs mt-6 transition-colors duration-200 ${
                        theme === 'dark' ? 'text-[#808080]' : 'text-gray-500'
                      }`}>Affichage des éléments en cache ({vaultItems.length})</p>
                    )}
                  </div>
                ) : isLoadingVaultItems && vaultItems.length === 0 ? (
                  // Loading state when fetching AND no cached data exists
                  <div className="flex items-center justify-center py-16">
                    <div className="flex flex-col items-center gap-4">
                      <svg className={`w-10 h-10 animate-spin transition-colors duration-200 ${
                        theme === 'dark' ? 'text-[#34B27B]' : 'text-[#25A26D]'
                      }`} fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span className={`text-sm transition-colors duration-200 ${
                        theme === 'dark' ? 'text-[#B3B3B3]' : 'text-gray-600'
                      }`}>Chargement des éléments...</span>
                    </div>
                  </div>
                ) : vaultItems.length === 0 ? (
                  // Empty state when no items
                  <div className="flex flex-col items-center justify-center py-16">
                    <svg className={`w-20 h-20 mb-4 transition-colors duration-200 ${
                      theme === 'dark' ? 'text-[#2C2C2C]' : 'text-gray-300'
                    }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                    </svg>
                    <p className={`text-base font-medium mb-1 transition-colors duration-200 ${
                      theme === 'dark' ? 'text-[#FFFFFF]' : 'text-gray-700'
                    }`}>Aucun élément sauvegardé</p>
                    <p className={`text-sm transition-colors duration-200 ${
                      theme === 'dark' ? 'text-[#808080]' : 'text-gray-500'
                    }`}>Vos éléments sauvegardés apparaîtront ici</p>
                  </div>
                ) : (() => {
                  // Filter vault items based on search query
                  const filteredItems = vaultItems.filter((item) => {
                    if (!vaultSearchQuery.trim()) return true
                    const query = vaultSearchQuery.toLowerCase().trim()
                    const titleMatch = item.title.toLowerCase().includes(query)
                    const typeMatch = item.type.toLowerCase().includes(query)
                    // Also check type labels in French
                    const typeLabel = item.type === 'chat' ? 'discussion' : 
                                    item.type === 'document' ? 'document' : 
                                    item.type === 'spreadsheet' ? 'tableau' : ''
                    const typeLabelMatch = typeLabel.includes(query)
                    return titleMatch || typeMatch || typeLabelMatch
                  })

                  // Show "no results" message if search is active but returns no items
                  if (vaultSearchQuery.trim() && filteredItems.length === 0) {
                    return (
                      <div className="flex flex-col items-center justify-center py-16">
                        <svg className={`w-20 h-20 mb-4 transition-colors duration-200 ${
                          theme === 'dark' ? 'text-[#2C2C2C]' : 'text-gray-300'
                        }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <p className={`text-base font-medium mb-1 transition-colors duration-200 ${
                          theme === 'dark' ? 'text-[#FFFFFF]' : 'text-gray-700'
                        }`}>Aucun résultat trouvé</p>
                        <p className={`text-sm transition-colors duration-200 ${
                          theme === 'dark' ? 'text-[#808080]' : 'text-gray-500'
                        }`}>Essayez avec d'autres mots-clés</p>
                      </div>
                    )
                  }

                  // Map over filtered vaultItems to display in grid - 4 columns for better modal layout
                  return (
                    <div className="w-full">
                      <div className="grid grid-cols-4 gap-4">
                      {filteredItems.map((item) => {
                        const isActive = currentVaultItemId === item.id || (item.isLegacy && currentChatId === item.id)
                        
                        // Get type label in French
                        const getTypeLabel = () => {
                          if (item.type === 'chat') return 'Discussion'
                          if (item.type === 'document') return 'Document'
                          if (item.type === 'spreadsheet') return 'Tableau'
                          return 'Document'
                        }
                        
                      // Format date
                        const formatDate = () => {
                      const date = new Date(item.created_at)
                          return date.toLocaleDateString('fr-FR', { 
                        day: 'numeric', 
                        month: 'short', 
                        year: 'numeric'
                      })
                        }
                      
                        // Get icon based on type - using image from public folder
                      const getTypeIcon = () => {
                          return (
                            <img 
                              src="/—Pngtree—gray folder free illustration_4546005.png" 
                              alt="Folder icon" 
                              className="w-full h-full object-contain"
                            />
                          )
                      }
                      
                      const isLoading = loadingVaultItemId === item.id
                      const itemError = itemErrors.get(item.id)
                      
                      return (
                          <div key={item.id} className="flex flex-col">
                            {/* Card with image at top right */}
                        <div
                              onClick={() => !isLoading && !itemError && loadVaultItem(item.id, item.type)}
                              className={`relative group border rounded-xl p-4 hover:shadow-lg transition-all h-20 ${
                            isLoading 
                              ? theme === 'dark'
                                ? 'border-[#34B27B]/30 bg-[#34B27B]/10 cursor-wait'
                                : 'border-[#25A26D]/30 bg-[#25A26D]/5 cursor-wait'
                              : itemError
                              ? theme === 'dark'
                                ? 'border-red-500/50 bg-red-900/20 cursor-default'
                                : 'border-red-300 bg-red-50 cursor-default'
                              : isActive 
                              ? theme === 'dark'
                                ? 'border-[#34B27B] ring-2 ring-[#34B27B]/20 bg-[#252525] cursor-pointer shadow-md'
                                : 'border-[#25A26D] ring-2 ring-[#25A26D]/20 bg-white cursor-pointer shadow-md'
                              : theme === 'dark'
                                ? 'border-[#2C2C2C] bg-[#252525] hover:border-[#34B27B]/50 cursor-pointer hover:shadow-md'
                                : 'border-gray-200 bg-white hover:border-[#25A26D]/50 cursor-pointer hover:shadow-md'
                          }`}
                        >
                          {/* Loading indicator */}
                          {isLoading && (
                            <div className={`absolute inset-0 flex items-center justify-center backdrop-blur-sm rounded-xl z-20 ${
                              theme === 'dark' ? 'bg-[#1A1A1A]/90' : 'bg-white/90'
                            }`}>
                              <div className="flex flex-col items-center gap-2">
                                <svg className={`w-5 h-5 animate-spin transition-colors duration-200 ${
                                  theme === 'dark' ? 'text-[#34B27B]' : 'text-[#25A26D]'
                                }`} fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span className={`text-xs font-medium transition-colors duration-200 ${
                                  theme === 'dark' ? 'text-[#34B27B]' : 'text-[#25A26D]'
                                }`}>Chargement...</span>
                              </div>
                            </div>
                          )}
                          
                          {/* Error state */}
                          {itemError && !isLoading && (
                            <div className={`absolute inset-0 flex flex-col items-center justify-center bg-opacity-95 rounded-lg z-20 p-2 ${
                              theme === 'dark' ? 'bg-red-900/30' : 'bg-red-50'
                            }`}>
                              <svg className={`w-4 h-4 mb-1 transition-colors duration-200 ${
                                theme === 'dark' ? 'text-red-400' : 'text-red-600'
                              }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                              </svg>
                              <p className={`text-[10px] text-center mb-1.5 px-1 line-clamp-2 transition-colors duration-200 ${
                                theme === 'dark' ? 'text-red-300' : 'text-red-700'
                              }`}>{itemError.message}</p>
                              {itemError.retryable && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    if (!isOnline) return
                                    setItemErrors(prev => {
                                      const newMap = new Map(prev)
                                      newMap.delete(item.id)
                                      return newMap
                                    })
                                    loadVaultItem(item.id, item.type)
                                  }}
                                  disabled={!isOnline}
                                  className="px-2 py-0.5 text-[10px] font-medium bg-red-600 hover:bg-red-700 text-white rounded transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                                  title={!isOnline ? 'Vous êtes hors ligne' : ''}
                                >
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                  </svg>
                                  Réessayer
                                </button>
                              )}
                            </div>
                          )}
                          {/* 3 dots menu - visible on hover */}
                          <div className="vault-menu-container absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity z-30">
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                setOpenVaultMenuId(openVaultMenuId === item.id ? null : item.id)
                              }}
                              className={`w-8 h-8 rounded flex items-center justify-center transition-colors duration-200 ${
                                theme === 'dark'
                                  ? 'hover:bg-[#1F1F1F] text-[#808080]'
                                  : 'hover:bg-gray-200 text-gray-600'
                              }`}
                              title="Options"
                            >
                              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
                              </svg>
                            </button>
                            
                            {/* Dropdown menu */}
                            {openVaultMenuId === item.id && (
                              <div className={`absolute top-10 left-0 border rounded-lg shadow-lg py-1 min-w-[120px] z-40 transition-colors duration-200 ${
                                theme === 'dark'
                                  ? 'bg-[#1A1A1A] border-[#2C2C2C]'
                                  : 'bg-white border-gray-200'
                              }`}>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    setOpenVaultMenuId(null)
                                    if (item.isLegacy) {
                                      openRenameModal(
                                        item.title,
                                        (newTitle) => handleRenameConversation(item.id, newTitle),
                                        'Renommer la conversation'
                                      )
                                    } else {
                                      openRenameModal(
                                        item.title,
                                        async (newTitle) => {
                                          const { data: { session } } = await supabase.auth.getSession()
                                          if (!session?.user) return
                                          
                                          const { error } = await supabase
                                            .from('vault_items')
                                            .update({ title: newTitle.trim() })
                                            .eq('id', item.id)
                                            .eq('user_id', session.user.id)
                                          
                                          if (error) {
                                            console.error('Error renaming vault item:', error)
                                            return
                                          }
                                          
                                          setVaultItems(prev =>
                                            prev.map(vItem =>
                                              vItem.id === item.id
                                                ? { ...vItem, title: newTitle.trim() }
                                                : vItem
                                            )
                                          )
                                        },
                                        'Renommer l\'élément'
                                      )
                                    }
                                  }}
                                  className={`w-full px-4 py-2 text-left text-sm flex items-center gap-2 transition-colors duration-200 ${
                                    theme === 'dark'
                                      ? 'text-[#FFFFFF] hover:bg-[#1F1F1F]'
                                      : 'text-gray-700 hover:bg-gray-100'
                                  }`}
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                  Renommer
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    setOpenVaultMenuId(null)
                                    if (item.isLegacy) {
                                      openConfirmModal(
                                        'Êtes-vous sûr de vouloir supprimer cette conversation ?',
                                        () => handleDeleteConversation(item.id),
                                        'Supprimer'
                                      )
                                    } else {
                                      openConfirmModal(
                                        `Êtes-vous sûr de vouloir supprimer cet élément ?`,
                                        async () => {
                                          const { data: { session } } = await supabase.auth.getSession()
                                          if (!session?.user) return
                                          
                                          const { error } = await supabase
                                            .from('vault_items')
                                            .delete()
                                            .eq('id', item.id)
                                            .eq('user_id', session.user.id)
                                          
                                          if (error) {
                                            console.error('Error deleting vault item:', error)
                                            alert('Erreur lors de la suppression')
                                            return
                                          }
                                          
                                          setVaultItems(prev => prev.filter(vItem => vItem.id !== item.id))
                                          
                                          if (currentVaultItemId === item.id) {
                                            setCurrentVaultItemId(null)
                                          }
                                          
                                          // Refresh vault items to ensure consistency
                                          await fetchVaultItems()
                                        },
                                        'Supprimer'
                                      )
                                    }
                                  }}
                                  className={`w-full px-4 py-2 text-left text-sm flex items-center gap-2 transition-colors duration-200 ${
                                    theme === 'dark'
                                      ? 'text-red-400 hover:bg-red-900/20'
                                      : 'text-red-600 hover:bg-red-50'
                                  }`}
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                  Supprimer
                                </button>
                              </div>
                            )}
                          </div>
                          
                              {/* Image at top right corner - inside the card */}
                              <div className="absolute -top-3 right-2 w-16 h-16 flex items-center justify-center z-10">
                                {getTypeIcon()}
                              </div>
                            </div>
                            
                            {/* Title below the card */}
                            <h3 className={`text-sm font-medium mt-3 truncate transition-colors duration-200 ${
                              theme === 'dark' ? 'text-[#FFFFFF]' : 'text-gray-900'
                            }`}>
                              {item.title}
                            </h3>
                            
                            {/* Subtitle with type and date */}
                            <p className={`text-xs mt-1 transition-colors duration-200 ${
                              theme === 'dark' ? 'text-[#808080]' : 'text-gray-500'
                            }`}>
                              {getTypeLabel()} • {formatDate()}
                            </p>
                        </div>
                      )
                    })}
                      </div>
                    </div>
                  )
                })()}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tableur Panel - Removed: Now integrated into Editeur Avocat as "Tableau" menu */}
        {false && (
          <div 
            className={`flex flex-col bg-white transition-all duration-300 overflow-hidden border-t border-gray-200 ${
              ((isEditorOpen || isVaultOpen) && activePanel !== 'tableur')
                ? 'flex-shrink-0 order-2' 
                : 'flex-1 min-h-0 order-1'
            }`}
            style={((isEditorOpen || isVaultOpen) && activePanel !== 'tableur') ? { height: '40px', maxHeight: '40px', minHeight: '40px' } : {}}
          >
            {/* Excel-style Menu Bar - Always visible */}
            <div 
              onClick={() => {
                if ((isEditorOpen || isVaultOpen) && activePanel !== 'tableur') {
                  setActivePanel('tableur')
                }
              }}
              className={`flex-shrink-0 bg-gray-50 border-b border-gray-200 px-4 py-1.5 flex items-center gap-1 ${
                ((isEditorOpen || isVaultOpen) && activePanel !== 'tableur')
                  ? 'cursor-pointer hover:bg-gray-100' 
                  : ''
              }`}
            >
                {!isTableurNavCollapsed && (
                  <button 
                    onClick={() => setActiveTableurMenuButton(activeTableurMenuButton === 'Édition' ? '' : 'Édition')}
                    className={`px-3 py-1 text-sm rounded transition-colors ${
                      activeTableurMenuButton === 'Édition' 
                        ? 'bg-[#25A26D] text-white' 
                        : 'text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Édition
                  </button>
                )}
                <div className="flex items-center gap-2 ml-auto">
                  <button 
                    onClick={(e) => {
                      e.stopPropagation()
                      setIsTableurNavCollapsed(!isTableurNavCollapsed)
                    }}
                    className="px-3 py-1 text-sm rounded transition-colors text-gray-700 hover:bg-gray-200"
                    title={isTableurNavCollapsed ? "Afficher la navigation" : "Masquer la navigation"}
                  >
                    {isTableurNavCollapsed ? (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                    )}
                  </button>
                  {/* Minimize/Restore Button - Only show if another panel is also open */}
                  {(isEditorOpen || isVaultOpen) && (
                    <button 
                      onClick={(e) => {
                        e.stopPropagation()
                        // Toggle: if tableur is active, switch to another open panel
                        if (activePanel === 'tableur') {
                          setActivePanel(isEditorOpen ? 'editor' : 'vault')
                        } else {
                          setActivePanel('tableur')
                        }
                      }}
                      className="w-6 h-6 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors duration-150"
                      title={activePanel === 'tableur' ? "Réduire" : "Agrandir"}
                    >
                      <span className="text-gray-600 text-[10px] font-mono leading-none">__</span>
                    </button>
                  )}
                  {/* Close Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      setIsTableurOpen(false)
                      // If closing tableur, make another open panel active
                      if (isEditorOpen) setActivePanel('editor')
                      else if (isVaultOpen) setActivePanel('vault')
                    }}
                    className="w-6 h-6 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors duration-150"
                    title="Fermer"
                  >
                    <svg className="w-3.5 h-3.5 text-[#25A26D]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

            {/* Tableur Content - Hidden when minimized (when another panel is open and active) */}
            {!((isEditorOpen || isVaultOpen) && activePanel !== 'tableur') && (
            <div className="flex-1 flex flex-col overflow-hidden bg-white">
                  
              {/* Toolbar - Menu Content */}
              {!isTableurNavCollapsed && (
              <div className="bg-white border-b border-gray-200 px-4 py-2 flex items-center justify-between gap-3 flex-wrap">
                {/* Dynamic Content based on active menu */}
                {activeTableurMenuButton === 'Édition' ? (
                  // Édition Menu - Combined Colonne and Ligne functionality
                  <div className="w-full flex flex-col px-4 py-3 gap-2">
                    {/* Top row: Renommer and Supprimer next to each other */}
                    <div className="flex items-center gap-2">
                      {/* Renommer - works for both column and row */}
                      <button 
                        onClick={() => {
                          if (selectedColumnIndex !== null && currentTableData) {
                            const currentName = currentTableData.columns[selectedColumnIndex] || `Colonne ${selectedColumnIndex + 1}`
                            openRenameModal(currentName, (newName) => {
                              updateTableData(data => ({
                                ...data,
                                columns: data.columns.map((col, idx) => 
                                  idx === selectedColumnIndex ? newName : col
                                )
                              }))
                            })
                          } else if (selectedRowIndex !== null) {
                            // Row renaming - currently rows don't have names, but we can add this functionality
                            console.warn('Le renommage de ligne n\'est pas encore disponible')
                          } else {
                            console.warn('Veuillez sélectionner une colonne ou une ligne à renommer')
                          }
                        }}
                        className="w-9 h-9 flex items-center justify-center border border-gray-200 rounded hover:border-gray-300 hover:bg-gray-50 transition-all duration-150 bg-white"
                        title="Renommer"
                      >
                        <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      {/* Supprimer - works for both column and row */}
                      <button 
                        onClick={() => {
                          if (selectedColumnIndex !== null && currentTableData) {
                            if (currentTableData.columns.length > 1) {
                              openConfirmModal('Êtes-vous sûr de vouloir supprimer cette colonne ?', () => {
                                updateTableData(data => {
                                  const newColumns = data.columns.filter((_, idx) => idx !== selectedColumnIndex)
                                  const newRows = data.rows.map(row => row.filter((_, idx) => idx !== selectedColumnIndex))
                                  const newPinnedColumns = (data.pinnedColumns || [])
                                    .filter(idx => idx !== selectedColumnIndex)
                                    .map(idx => idx > selectedColumnIndex ? idx - 1 : idx)
                                  const newHiddenColumns = (data.hiddenColumns || [])
                                    .filter(idx => idx !== selectedColumnIndex)
                                    .map(idx => idx > selectedColumnIndex ? idx - 1 : idx)
                                  
                                  return {
                                    ...data,
                                    columns: newColumns,
                                    rows: newRows,
                                    pinnedColumns: newPinnedColumns,
                                    hiddenColumns: newHiddenColumns
                                  }
                                })
                                setSelectedColumnIndex(null)
                              })
                            } else {
                              console.warn('Vous ne pouvez pas supprimer la dernière colonne')
                            }
                          } else if (selectedRowIndex !== null && currentTableData) {
                            if (currentTableData.rows.length > 1) {
                              const rowIndexToDelete = selectedRowIndex
                              const totalRows = currentTableData.rows.length
                              openConfirmModal('Êtes-vous sûr de vouloir supprimer cette ligne ?', () => {
                                if (spreadsheetTableRef.current) {
                                  spreadsheetTableRef.current.removeRow(rowIndexToDelete)
                                  if (rowIndexToDelete === totalRows - 1) {
                                    setSelectedRowIndex(totalRows - 2)
                                  } else if (rowIndexToDelete < totalRows - 1) {
                                    setSelectedRowIndex(rowIndexToDelete)
                                  } else {
                                    setSelectedRowIndex(null)
                                  }
                                }
                              })
                            }
                          } else {
                            console.warn('Veuillez sélectionner une colonne ou une ligne à supprimer')
                          }
                        }}
                        className="w-9 h-9 flex items-center justify-center border border-gray-200 rounded hover:border-gray-300 hover:bg-gray-50 transition-all duration-150 bg-white"
                        title="Supprimer"
                      >
                        <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                      {/* Insérer une colonne à gauche */}
                      <button 
                        onClick={() => {
                          if (!currentTableData || currentTableData.columns.length >= MAX_SPREADSHEET_COLUMNS) {
                            return
                          }
                          if (spreadsheetTableRef.current && selectedColumnIndex !== null) {
                            spreadsheetTableRef.current.insertColumnLeft(selectedColumnIndex)
                            setSelectedColumnIndex(selectedColumnIndex)
                          } else if (spreadsheetTableRef.current && currentTableData) {
                            spreadsheetTableRef.current.insertColumnLeft(0)
                            setSelectedColumnIndex(0)
                          }
                        }}
                        disabled={!currentTableData || (currentTableData?.columns.length >= MAX_SPREADSHEET_COLUMNS)}
                        className="w-9 h-9 flex items-center justify-center border border-gray-200 rounded hover:border-gray-300 hover:bg-gray-50 transition-all duration-150 bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                        title={currentTableData && currentTableData.columns.length >= MAX_SPREADSHEET_COLUMNS ? "Limite de 10 colonnes atteinte" : "Insérer une colonne à gauche"}
                      >
                        <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6H6m6 0h6M4 4h16v2H4V4z" />
                        </svg>
                      </button>
                      {/* Insérer une colonne à droite */}
                      <button 
                        onClick={() => {
                          if (!currentTableData || currentTableData.columns.length >= MAX_SPREADSHEET_COLUMNS) {
                            return
                          }
                          if (spreadsheetTableRef.current && selectedColumnIndex !== null) {
                            spreadsheetTableRef.current.insertColumnRight(selectedColumnIndex)
                            setSelectedColumnIndex(selectedColumnIndex + 1)
                          } else if (spreadsheetTableRef.current && currentTableData) {
                            const lastIndex = currentTableData.columns.length - 1
                            spreadsheetTableRef.current.insertColumnRight(lastIndex)
                            setSelectedColumnIndex(currentTableData.columns.length)
                          }
                        }}
                        disabled={!currentTableData || (currentTableData?.columns.length >= MAX_SPREADSHEET_COLUMNS)}
                        className="w-9 h-9 flex items-center justify-center border border-gray-200 rounded hover:border-gray-300 hover:bg-gray-50 transition-all duration-150 bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                        title={currentTableData && currentTableData.columns.length >= MAX_SPREADSHEET_COLUMNS ? "Limite de 10 colonnes atteinte" : "Insérer une colonne à droite"}
                      >
                        <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6M4 4h16v2H4V4z" />
                        </svg>
                      </button>
                      {/* Insérer une ligne au-dessus */}
                      <button 
                        onClick={() => {
                          if (!currentTableData || currentTableData.rows.length >= MAX_SPREADSHEET_ROWS) {
                            return
                          }
                          if (spreadsheetTableRef.current && selectedRowIndex !== null) {
                            spreadsheetTableRef.current.insertRowAbove(selectedRowIndex)
                            setSelectedRowIndex(selectedRowIndex + 1)
                          } else if (spreadsheetTableRef.current && currentTableData) {
                            spreadsheetTableRef.current.insertRowAbove(0)
                            setSelectedRowIndex(0)
                          }
                        }}
                        disabled={!currentTableData || (currentTableData?.rows.length >= MAX_SPREADSHEET_ROWS)}
                        className="w-9 h-9 flex items-center justify-center border border-gray-200 rounded hover:border-gray-300 hover:bg-gray-50 transition-all duration-150 bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                        title={currentTableData && currentTableData.rows.length >= MAX_SPREADSHEET_ROWS ? "Limite de 10 lignes atteinte" : "Insérer une ligne au-dessus"}
                      >
                        <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6H6m6 0h6M4 4h16v2H4V4z" />
                        </svg>
                      </button>
                      {/* Insérer une ligne en dessous */}
                      <button 
                        onClick={() => {
                          if (!currentTableData || currentTableData.rows.length >= MAX_SPREADSHEET_ROWS) {
                            return
                          }
                          if (spreadsheetTableRef.current && selectedRowIndex !== null) {
                            spreadsheetTableRef.current.insertRowBelow(selectedRowIndex)
                            setSelectedRowIndex(selectedRowIndex)
                          } else if (spreadsheetTableRef.current && currentTableData) {
                            const lastIndex = currentTableData.rows.length - 1
                            spreadsheetTableRef.current.insertRowBelow(lastIndex)
                            setSelectedRowIndex(currentTableData.rows.length)
                          }
                        }}
                        disabled={!currentTableData || (currentTableData?.rows.length >= MAX_SPREADSHEET_ROWS)}
                        className="w-9 h-9 flex items-center justify-center border border-gray-200 rounded hover:border-gray-300 hover:bg-gray-50 transition-all duration-150 bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                        title={currentTableData && currentTableData.rows.length >= MAX_SPREADSHEET_ROWS ? "Limite de 10 lignes atteinte" : "Insérer une ligne en dessous"}
                      >
                        <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6M4 4h16v2H4V4z" />
                        </svg>
                      </button>
                    </div>
                    
                    {/* Third row: Toggle for Selection Logic */}
                    <div className="flex items-center gap-2">
                      {/* Toggle - Always visible */}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={handleTableurAIToggle}
                          className={`relative inline-flex h-5 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 ${
                            isTableurAIActivated 
                              ? 'bg-[#25A26D]' 
                              : 'bg-gray-300'
                          }`}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            isTableurAIActivated 
                              ? 'translate-x-6' 
                              : 'translate-x-1'
                          }`} />
                        </button>
                        <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                      </div>
                      
                      {/* Token Counter - Minimal & Professional */}
                      <div className="flex items-center gap-1.5 px-2 py-1">
                        <span className="text-xs text-gray-500">
                          Utilisation IA: <span className="font-mono">{selectedCharCount.toLocaleString()}</span> / <span className="font-mono">10K</span> tokens
                          {selectedCharCount >= MAX_SELECTION_CHARS && (
                            <span className="ml-1.5 text-red-500">⚠</span>
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
              )}

              {/* Spreadsheet Tabs */}
              <div className="flex-shrink-0 bg-gray-100 border-b border-gray-200 px-2 py-1 flex items-center gap-1 overflow-x-auto overflow-y-hidden flex-nowrap">
                {spreadsheets.map((spreadsheet) => (
                  <div
                    key={spreadsheet.id}
                    className={`group relative flex items-center gap-2 px-3 py-1.5 rounded-t text-sm transition-colors cursor-pointer select-none flex-1 min-w-[90px] max-w-[180px] ${
                      spreadsheet.id === activeSpreadsheetId
                        ? 'bg-white text-gray-900 font-medium'
                        : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                    }`}
                    onClick={() => switchToSpreadsheet(spreadsheet.id)}
                  >
                    <span className="flex-1 truncate">{spreadsheet.title}</span>
                    {spreadsheets.length > 1 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          closeSpreadsheet(spreadsheet.id)
                        }}
                        className="opacity-0 group-hover:opacity-100 hover:bg-gray-300 rounded p-0.5 transition-opacity flex-shrink-0"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
                <button
                  onClick={createNewSpreadsheet}
                  disabled={spreadsheets.length >= 5}
                  className={`p-1.5 rounded transition-colors flex-shrink-0 ${
                    spreadsheets.length >= 5 
                      ? 'opacity-40 cursor-not-allowed' 
                      : 'hover:bg-gray-200'
                  }`}
                  title={spreadsheets.length >= 5 ? "Maximum 5 tableurs" : "Nouveau tableur"}
                >
                  <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </button>
              </div>

              {/* Spreadsheet - Full workspace */}
              <div className="flex-1 overflow-hidden pt-5 bg-white">
                {currentTableData ? (
                  <div className="w-full h-full p-6 overflow-auto relative">
                    {/* Validate/Cancel buttons for table selection (similar to editor) */}
                    {isTableurAIActivated && activeAIMode === 'tableur' && (selectedColumnIndex !== null || selectedRowIndex !== null) && (
                      <div 
                        ref={tableSelectionBarRef}
                        className="fixed z-[9999] bg-white border border-gray-300 rounded-lg shadow-lg px-2 py-1 flex items-center gap-2"
                        style={{
                          top: `${tableSelectionBarPosition.top}px`,
                          left: `${tableSelectionBarPosition.left}px`,
                          transform: 'translate(-50%, 0)'
                        }}
                      >
                        {!isTableSelectionLocked ? (
                          <>
                            <button
                              onClick={handleValidateTableSelection}
                              className="px-2 py-1 text-xs font-medium text-white bg-gray-900 hover:bg-black rounded transition-colors"
                            >
                              Valider
                            </button>
                            <button
                              onClick={handleCancelTableSelection}
                              className="px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
                            >
                              Annuler
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={handleCancelTableSelection}
                            className="px-2 py-1 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
                          >
                            Annuler
                          </button>
                        )}
                      </div>
                    )}
                    {currentTableData ? (
                      <SpreadsheetTable 
                        ref={spreadsheetTableRef}
                        data={currentTableData} 
                      onChange={(updatedData: TableData) => {
                        setCurrentTableData(updatedData)
                      }}
                      isAIActivated={isTableurAIActivated && activeAIMode === 'tableur'}
                      selectedColumnIndex={selectedColumnIndex}
                      onColumnSelect={(columnIndex: number) => {
                        setSelectedColumnIndex(columnIndex >= 0 ? columnIndex : null)
                        // Clear row selection when column is selected
                        if (columnIndex >= 0) {
                          setSelectedRowIndex(null)
                          // Clear locked selection if it was a row
                          if (isTableSelectionLocked && lockedTableSelection?.type === 'row') {
                            setIsTableSelectionLocked(false)
                            setLockedTableSelection(null)
                          }
                          // Update button position
                          setTimeout(() => {
                            if (spreadsheetTableRef.current) {
                              const pos = spreadsheetTableRef.current.getSelectedElementPosition()
                              if (pos) {
                                setTableSelectionBarPosition(pos)
                              }
                            }
                          }, 0)
                        }
                      }}
                      selectedRowIndex={selectedRowIndex}
                      onRowSelect={(rowIndex: number) => {
                        setSelectedRowIndex(rowIndex >= 0 ? rowIndex : null)
                        // Clear column selection when row is selected
                        if (rowIndex >= 0) {
                          setSelectedColumnIndex(null)
                          // Clear locked selection if it was a column
                          if (isTableSelectionLocked && lockedTableSelection?.type === 'column') {
                            setIsTableSelectionLocked(false)
                            setLockedTableSelection(null)
                          }
                          // Update button position
                          setTimeout(() => {
                            if (spreadsheetTableRef.current) {
                              const pos = spreadsheetTableRef.current.getSelectedElementPosition()
                              if (pos) {
                                setTableSelectionBarPosition(pos)
                              }
                            }
                          }, 0)
                        }
                      }}
                    />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <div className="text-center">
                          <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                          <p className="text-gray-500 text-sm">Aucun tableur ouvert</p>
                          <p className="text-gray-400 text-xs mt-1">Cliquez sur "Notes avec IA" pour ouvrir un tableau</p>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                    <p>Tableur - À implémenter</p>
                  </div>
                )}
              </div>
            </div>
            )}
          </div>
        )}

      </div>
    )}
    </div>
    </>
  )
}

export default LandingChatScreen
